globalThis.__zod_globalConfig=Object.assign(globalThis.__zod_globalConfig||{},{jitless:true});

// src/ai.ts
import { z as z3 } from "zod";

// src/common.ts
import { z } from "zod";
var identifierSchema = z.string().trim().min(1).max(64);
var requestIdSchema = identifierSchema;
var eventIdSchema = identifierSchema;
var stateVersionSchema = z.number().int().nonnegative();
var serverTimestampSchema = z.iso.datetime({ offset: true });
function containsC0ControlCharacter(value) {
  return Array.from(value).some((character) => {
    const codePoint = character.codePointAt(0);
    return codePoint !== void 0 && (codePoint <= 31 || codePoint === 127);
  });
}

// src/game.ts
import { z as z2 } from "zod";
var gameStatusSchema = z2.enum([
  "CREATED",
  "WAITING",
  "READY",
  "PLAYING",
  "COUNTING_REQUESTED",
  "SCORING",
  "FINISHED",
  "ABORTED"
]);
var roomStatusSchema = z2.enum(["OPEN", "READY", "IN_GAME", "CLOSED", "CANCELLED"]);
var expectedRoomStatuses = {
  CREATED: ["OPEN"],
  WAITING: ["OPEN"],
  READY: ["READY"],
  PLAYING: ["IN_GAME"],
  COUNTING_REQUESTED: ["IN_GAME"],
  SCORING: ["IN_GAME"],
  FINISHED: ["CLOSED"],
  ABORTED: ["CANCELLED", "CLOSED"]
};
var stoneColorSchema = z2.enum(["BLACK", "WHITE"]);
var roomSeatSchema = stoneColorSchema;
var roomCodeSchema = z2.string().regex(/^[A-Z2-9]{6,12}$/);
var boardSizeSchema = z2.union([z2.literal(9), z2.literal(13), z2.literal(19)]);
var rulesetSchema = z2.literal("CHINESE_AREA_V1");
var gameTypeSchema = z2.enum([
  "EVEN",
  "LET_FIRST",
  "HANDICAP_2",
  "HANDICAP_3",
  "HANDICAP_4"
]);
var creatorColorPreferenceSchema = z2.enum(["BLACK", "WHITE", "RANDOM"]);
var nigiriParitySchema = z2.enum(["ODD", "EVEN"]);
var nigiriResolutionSchema = z2.enum([
  "GUESSED_CORRECT",
  "GUESSED_WRONG",
  "SELECTOR_TIMEOUT",
  "GUESSER_TIMEOUT"
]);
var nigiriSnapshotSchema = z2.object({
  stage: z2.enum(["SELECTING", "GUESSING", "RESOLVED"]),
  selectorMembershipId: identifierSchema,
  guesserMembershipId: identifierSchema,
  deadlineAt: serverTimestampSchema.nullable(),
  resolution: nigiriResolutionSchema.nullable(),
  guesserCorrect: z2.boolean().nullable(),
  finalBlackMembershipId: identifierSchema.nullable(),
  resolvedAt: serverTimestampSchema.nullable()
}).strict().superRefine((nigiri, context) => {
  if (nigiri.selectorMembershipId === nigiri.guesserMembershipId) {
    context.addIssue({
      code: "custom",
      message: "Nigiri participants must be different",
      path: ["guesserMembershipId"]
    });
  }
  const resolved = nigiri.stage === "RESOLVED";
  if (resolved !== (nigiri.deadlineAt === null) || resolved !== (nigiri.resolution !== null) || resolved !== (nigiri.finalBlackMembershipId !== null) || resolved !== (nigiri.resolvedAt !== null)) {
    context.addIssue({
      code: "custom",
      message: "Nigiri lifecycle fields must match its stage",
      path: ["stage"]
    });
  }
  if (nigiri.finalBlackMembershipId !== null && nigiri.finalBlackMembershipId !== nigiri.selectorMembershipId && nigiri.finalBlackMembershipId !== nigiri.guesserMembershipId) {
    context.addIssue({
      code: "custom",
      message: "Final black player must be a nigiri participant",
      path: ["finalBlackMembershipId"]
    });
  }
  const expectedCorrect = nigiri.resolution === "GUESSED_CORRECT" ? true : nigiri.resolution === "GUESSED_WRONG" ? false : null;
  if (nigiri.guesserCorrect !== expectedCorrect) {
    context.addIssue({
      code: "custom",
      message: "Nigiri correctness must match its resolution",
      path: ["guesserCorrect"]
    });
  }
});
var moveKindSchema = z2.enum(["PLAY", "PASS"]);
var boardPointSchema = z2.object({
  x: z2.number().int().nonnegative(),
  y: z2.number().int().nonnegative()
}).strict();
var positionHashSchema = z2.string().regex(/^[0-9a-f]{64}$/);
var intersectionSchema = stoneColorSchema.nullable();
var boardSnapshotSchema = z2.object({
  ruleset: rulesetSchema,
  size: boardSizeSchema,
  currentPlayer: stoneColorSchema,
  stones: z2.array(intersectionSchema),
  positionHash: positionHashSchema
}).strict().superRefine((snapshot, context) => {
  const expectedIntersections = snapshot.size * snapshot.size;
  if (snapshot.stones.length !== expectedIntersections) {
    context.addIssue({
      code: "custom",
      message: `Expected ${expectedIntersections} intersections for a ${snapshot.size} line board`,
      path: ["stones"]
    });
  }
});
var roomPlayerSnapshotSchema = z2.object({
  membershipId: identifierSchema,
  displayName: z2.string().trim().min(1).max(64),
  avatarUrl: z2.url().max(512).nullable(),
  seat: roomSeatSchema,
  ready: z2.boolean()
}).strict();
var roomSnapshotSchema = z2.object({
  roomId: identifierSchema,
  roomCode: roomCodeSchema,
  status: roomStatusSchema,
  boardSize: boardSizeSchema,
  ruleset: rulesetSchema,
  komi: z2.number().finite().min(0.5).max(7.5).multipleOf(0.5),
  handicap: z2.number().int().min(0).max(4),
  creatorColorPreference: creatorColorPreferenceSchema.optional(),
  creatorMembershipId: identifierSchema.optional(),
  players: z2.array(roomPlayerSnapshotSchema).min(1).max(2)
}).strict().superRefine((room, context) => {
  const expectedKomi = room.handicap === 0 ? room.komi === 7.5 : room.komi === room.handicap / 2;
  if (!expectedKomi) {
    context.addIssue({
      code: "custom",
      message: "Chinese game compensation must match the selected game type",
      path: ["komi"]
    });
  }
  const memberships = /* @__PURE__ */ new Set();
  const seats = /* @__PURE__ */ new Set();
  room.players.forEach((player, index) => {
    if (memberships.has(player.membershipId)) {
      context.addIssue({
        code: "custom",
        message: "Room players must have unique membership ids",
        path: ["players", index, "membershipId"]
      });
    }
    if (seats.has(player.seat)) {
      context.addIssue({
        code: "custom",
        message: "Room players must have unique seats",
        path: ["players", index, "seat"]
      });
    }
    memberships.add(player.membershipId);
    seats.add(player.seat);
  });
});
var gameSnapshotSchema = z2.object({
  gameId: identifierSchema,
  stateVersion: stateVersionSchema,
  status: gameStatusSchema,
  room: roomSnapshotSchema,
  board: boardSnapshotSchema,
  currentSequence: z2.number().int().nonnegative(),
  consecutivePasses: z2.number().int().min(0).max(2),
  startedAt: serverTimestampSchema.nullable(),
  scoringStartedAt: serverTimestampSchema.nullable(),
  nigiri: nigiriSnapshotSchema.optional()
}).strict().superRefine((snapshot, context) => {
  if (snapshot.board.size !== snapshot.room.boardSize) {
    context.addIssue({
      code: "custom",
      message: "Board and room sizes must match",
      path: ["board", "size"]
    });
  }
  if (!expectedRoomStatuses[snapshot.status].includes(snapshot.room.status)) {
    context.addIssue({
      code: "custom",
      message: "Game and room lifecycle states do not match",
      path: ["room", "status"]
    });
  }
  if (snapshot.nigiri !== void 0) {
    const selector = snapshot.room.players.find(
      (player) => player.membershipId === snapshot.nigiri?.selectorMembershipId
    );
    const guesser = snapshot.room.players.find(
      (player) => player.membershipId === snapshot.nigiri?.guesserMembershipId
    );
    const finalBlack = snapshot.room.players.find(
      (player) => player.membershipId === snapshot.nigiri?.finalBlackMembershipId
    );
    if (snapshot.room.creatorColorPreference !== "RANDOM" || selector === void 0 || guesser === void 0 || snapshot.nigiri.stage !== "RESOLVED" && (snapshot.status !== "READY" || selector.seat !== "WHITE" || guesser.seat !== "BLACK") || snapshot.nigiri.stage === "RESOLVED" && finalBlack?.seat !== "BLACK") {
      context.addIssue({
        code: "custom",
        message: "Nigiri projection must match room players and lifecycle",
        path: ["nigiri"]
      });
    }
  }
  const mustHaveStarted = snapshot.status === "PLAYING" || snapshot.status === "COUNTING_REQUESTED" || snapshot.status === "SCORING" || snapshot.status === "FINISHED";
  const mustNotHaveStarted = snapshot.status === "CREATED" || snapshot.status === "WAITING" || snapshot.status === "READY";
  if (mustHaveStarted && snapshot.startedAt === null || mustNotHaveStarted && snapshot.startedAt !== null) {
    context.addIssue({
      code: "custom",
      message: "startedAt must match the game lifecycle",
      path: ["startedAt"]
    });
  }
  const mustNotHaveStartedScoring = snapshot.status === "CREATED" || snapshot.status === "WAITING" || snapshot.status === "READY" || snapshot.status === "PLAYING" || snapshot.status === "COUNTING_REQUESTED";
  if (snapshot.status === "SCORING" && snapshot.scoringStartedAt === null || mustNotHaveStartedScoring && snapshot.scoringStartedAt !== null) {
    context.addIssue({
      code: "custom",
      message: "scoringStartedAt must match the game lifecycle",
      path: ["scoringStartedAt"]
    });
  }
});
var gameMoveSnapshotSchema = z2.object({
  sequence: z2.number().int().positive(),
  kind: moveKindSchema,
  color: stoneColorSchema,
  point: boardPointSchema.nullable(),
  captured: z2.array(boardPointSchema),
  positionHashBefore: positionHashSchema,
  positionHashAfter: positionHashSchema
}).strict().superRefine((move, context) => {
  if (move.kind === "PLAY" && move.point === null) {
    context.addIssue({
      code: "custom",
      message: "A played move requires a board point",
      path: ["point"]
    });
  }
  if (move.kind === "PASS" && move.point !== null) {
    context.addIssue({
      code: "custom",
      message: "A pass cannot contain a board point",
      path: ["point"]
    });
  }
  if (move.kind === "PASS" && move.captured.length > 0) {
    context.addIssue({
      code: "custom",
      message: "A pass cannot capture stones",
      path: ["captured"]
    });
  }
});
var gameErrorCodeSchema = z2.enum([
  "EBOOK_SCHEMA_NOT_READY",
  "EBOOK_SERVICE_NOT_READY",
  "AUTH_REQUIRED",
  "ACCOUNT_LOGIN_FAILED",
  "ACCOUNT_NOT_FOUND",
  "ACCOUNT_LOCKED",
  "ACCOUNT_ALREADY_EXISTS",
  "WECHAT_LOGIN_FAILED",
  "INVITATION_INVALID",
  "ROLE_REQUIRED",
  "MEMBERSHIP_INACTIVE",
  "FORBIDDEN",
  "USER_NOT_FOUND",
  "ORGANIZATION_NOT_FOUND",
  "ORGANIZATION_SLUG_CONFLICT",
  "ORGANIZATION_MEMBER_NOT_FOUND",
  "CLASS_NOT_FOUND",
  "CLASS_MEMBER_NOT_FOUND",
  "LAST_ADMIN_REQUIRED",
  "SESSION_REPLAYED",
  "RESOURCE_VERSION_CONFLICT",
  "REPORT_NOT_READY",
  "TIMING_REPORT_UNAVAILABLE",
  "REVIEW_NOT_FOUND",
  "REVIEW_NOT_PUBLISHED",
  "REVIEW_VERSION_CONFLICT",
  "PROBLEM_NOT_FOUND",
  "PROBLEM_SOURCE_INVALID",
  "HOMEWORK_NOT_FOUND",
  "HOMEWORK_VERSION_CONFLICT",
  "HOMEWORK_NOT_EDITABLE",
  "ASSIGNMENT_NOT_FOUND",
  "ASSIGNMENT_NOT_OPEN",
  "ATTEMPT_LIMIT_REACHED",
  "SUBMISSION_NOT_FOUND",
  "SUBMISSION_VERSION_CONFLICT",
  "MATCHMAKING_CONFLICT",
  "MATCHMAKING_TICKET_NOT_FOUND",
  "MATCHMAKING_VERSION_CONFLICT",
  "TAKEBACK_NOT_AVAILABLE",
  "TAKEBACK_NOT_FOUND",
  "TAKEBACK_VERSION_CONFLICT",
  "GAME_NOT_FOUND",
  "ROOM_FULL",
  "INVALID_GAME_STATUS",
  "NOT_SEATED",
  "NOT_YOUR_TURN",
  "NIGIRI_NOT_ACTIVE",
  "NIGIRI_ACTION_FORBIDDEN",
  "STATE_VERSION_CONFLICT",
  "REQUEST_ID_REUSE",
  "REQUEST_IN_PROGRESS",
  "INVALID_PAYLOAD",
  "FEATURE_NOT_AVAILABLE",
  "INVALID_BOARD_SIZE",
  "INVALID_MOVE",
  "WRONG_TURN",
  "OUT_OF_BOUNDS",
  "OCCUPIED",
  "SUICIDE",
  "KO_RECAPTURE",
  "POSITION_REPETITION",
  "TIME_EXPIRED",
  "RESOURCE_NOT_FOUND",
  "TEACHER_ROLE_REQUIRED",
  "TEACHER_CLASS_SCOPE_DENIED",
  "GAME_PAUSED",
  "GAME_ALREADY_PAUSED",
  "GAME_NOT_PAUSED",
  "PAUSE_OWNERSHIP_REQUIRED",
  "INTERVENTION_REASON_REQUIRED",
  "COUNTING_REQUEST_NOT_FOUND",
  "COUNTING_REQUEST_NOT_PENDING",
  "COUNTING_RESPONSE_FORBIDDEN",
  "INVALID_DEAD_STONE",
  "SCORING_PROPOSAL_NOT_FOUND",
  "SCORING_SESSION_NOT_ACTIVE",
  "SCORING_VERSION_CONFLICT",
  "ADJUDICATION_CONFLICT",
  "DISPUTED_GROUP_NOT_FOUND",
  "INTERNAL_STATE_CORRUPTED",
  "SERVICE_UNAVAILABLE",
  "AI_ROLE_REQUIRED",
  "AI_ANALYSIS_NOT_FOUND",
  "AI_MANAGE_ACCESS_REQUIRED",
  "AI_GAME_NOT_FINISHED",
  "AI_POSITION_NOT_FOUND",
  "AI_SOURCE_VERSION_MISMATCH",
  "AI_DISABLED",
  "AI_EXTERNAL_PROCESSING_NOT_APPROVED",
  "AI_PROVIDER_PROFILE_UNAVAILABLE",
  "AI_QUOTA_EXCEEDED",
  "AI_QUEUE_LIMIT_REACHED",
  "AI_POLICY_VERSION_CONFLICT",
  "AI_ANALYSIS_VERSION_CONFLICT",
  "AI_ANALYSIS_NOT_CANCELLABLE",
  "AI_PROVIDER_TEMPORARILY_UNAVAILABLE",
  "AI_RESULT_INVALID",
  "AI_SOURCE_CORRUPTED",
  "AI_REQUEST_ID_REUSED",
  "AI_PROCESSING_FAILED"
]);
var gameCommandErrorSchema = z2.object({
  code: gameErrorCodeSchema,
  message: z2.string().trim().min(1),
  currentStateVersion: stateVersionSchema.optional(),
  syncRequired: z2.boolean().optional(),
  details: z2.unknown().optional()
}).strict().superRefine((error, context) => {
  if ((error.code === "STATE_VERSION_CONFLICT" || error.code === "SCORING_VERSION_CONFLICT") && (error.currentStateVersion === void 0 || error.syncRequired !== true)) {
    context.addIssue({
      code: "custom",
      message: "A version conflict requires the current Game version and syncRequired=true",
      path: ["currentStateVersion"]
    });
  }
});
var clientGameEventSchema = z2.enum([
  "room.join",
  "room.ready",
  "game.nigiri.select",
  "game.nigiri.guess",
  "game.move",
  "game.pass",
  "game.sync.request",
  "game.resign",
  "game.counting.request",
  "game.counting.respond",
  "game.scoring.propose",
  "game.scoring.confirm",
  "game.scoring.reject",
  "game.scoring.resume"
]);
var serverGameEventSchema = z2.enum([
  "room.joined",
  "room.updated",
  "game.start",
  "game.nigiri.selected",
  "game.nigiri.resolved",
  "game.move.accepted",
  "game.move.rejected",
  "game.scoring.start",
  "game.counting.requested",
  "game.counting.rejected",
  "game.scoring.proposed",
  "game.scoring.confirmed",
  "game.scoring.rejected",
  "game.play.resumed",
  "game.teacher.paused",
  "game.teacher.resumed",
  "game.teacher.counting.resolved",
  "game.takeback.requested",
  "game.takeback.resolved",
  "game.disconnection.paused",
  "game.disconnection.resumed",
  "game.finish",
  "game.timeout",
  "game.state.delta",
  "game.state.sync",
  "command.rejected"
]);

// src/ai.ts
var AI_POSITION_SCHEMA_VERSION = "GO_AI_POSITION_V1";
var AI_RESULT_SCHEMA_VERSION = "GO_AI_RESULT_V1";
var MAX_AI_BEST_MOVES = 10;
var MAX_AI_PV_MOVES = 60;
var MAX_AI_ANALYSES_PAGE_SIZE = 50;
var MAX_AI_POSITION_OPTIONS_PAGE_SIZE = 200;
var MAX_AI_CURSOR_LENGTH = 512;
var MAX_AI_DAILY_ANALYSIS_LIMIT = 1e4;
var MAX_AI_CONCURRENT_ANALYSES = 32;
var MAX_AI_QUEUED_ANALYSES = 1e3;
var MIN_AI_RETENTION_DAYS = 1;
var MAX_AI_RETENTION_DAYS = 365;
var DEFAULT_AI_RETENTION_DAYS = 90;
var MAX_AI_VISITS = 1e9;
function boundedSafeText(min, max) {
  return z3.string().trim().min(min).max(max).refine((value) => !containsC0ControlCharacter(value), {
    message: "Text cannot contain C0 control characters"
  });
}
function addAiIssue(context, message, path) {
  context.addIssue({
    code: "custom",
    message,
    path
  });
}
function sameAiMove(left, right) {
  return left.kind === right.kind && (left.kind === "PASS" || right.kind === "PLAY" && left.point.x === right.point.x && left.point.y === right.point.y);
}
function aiMoveKey(move) {
  return move.kind === "PASS" ? "PASS" : `${move.point.x}:${move.point.y}`;
}
function maxAiScoreLeadHalfPoints(boardSize) {
  return boardSize * boardSize * 2 + 15;
}
var aiStateVersionSchema = z3.number().int().safe().positive();
var aiPolicyVersionSchema = stateVersionSchema;
var aiPositionRevisionSchema = z3.number().int().safe().nonnegative();
var aiMoveSequenceSchema = z3.number().int().safe().nonnegative().nullable();
var aiWinRateBpsSchema = z3.number().int().min(0).max(1e4);
var aiScoreLeadHalfPointsSchema = z3.number().int().min(-maxAiScoreLeadHalfPoints(19)).max(maxAiScoreLeadHalfPoints(19));
var aiVisitsSchema = z3.number().int().safe().positive().max(MAX_AI_VISITS);
var aiHashSchema = z3.string().regex(/^[a-f0-9]{64}$/u);
var aiCursorSchema = z3.string().trim().min(1).max(MAX_AI_CURSOR_LENGTH).regex(/^[A-Za-z0-9_-]+$/u);
var aiProviderProfileCodeSchema = z3.string().trim().min(1).max(64).regex(/^[A-Z][A-Z0-9_]*$/u);
var aiProviderComponentCodeSchema = z3.string().trim().min(1).max(64).regex(/^[A-Za-z0-9][A-Za-z0-9._-]*$/u);
var aiProviderVersionSchema = boundedSafeText(1, 128);
var aiProcessingNoticeVersionSchema = boundedSafeText(1, 64);
var aiExecutionModeSchema = z3.enum(["DISABLED", "LOCAL_ONLY", "EXTERNAL_ANONYMIZED"]);
var aiEnabledExecutionModeSchema = z3.enum(["LOCAL_ONLY", "EXTERNAL_ANONYMIZED"]);
var aiAnalysisStatusSchema = z3.enum([
  "QUEUED",
  "RUNNING",
  "SUCCEEDED",
  "FAILED",
  "CANCELLED",
  "PURGED"
]);
var aiCancelReasonSchema = z3.enum(["ACTOR_REQUESTED", "POLICY_REVOKED"]);
var aiPolicyConfigurationShape = {
  enabled: z3.boolean(),
  executionMode: aiExecutionModeSchema,
  providerProfileCode: aiProviderProfileCodeSchema.nullable(),
  dailyAnalysisLimit: z3.number().int().min(1).max(MAX_AI_DAILY_ANALYSIS_LIMIT),
  maxConcurrentAnalyses: z3.number().int().min(1).max(MAX_AI_CONCURRENT_ANALYSES),
  maxQueuedAnalyses: z3.number().int().min(1).max(MAX_AI_QUEUED_ANALYSES),
  retentionDays: z3.number().int().min(MIN_AI_RETENTION_DAYS).max(MAX_AI_RETENTION_DAYS),
  processingNoticeVersion: aiProcessingNoticeVersionSchema.nullable()
};
function refineAiPolicyConfiguration(policy, context) {
  if (policy.executionMode === "DISABLED") {
    if (policy.enabled) {
      addAiIssue(context, "A disabled AI policy cannot be enabled", ["enabled"]);
    }
    if (policy.providerProfileCode !== null) {
      addAiIssue(context, "A disabled AI policy cannot select a provider profile", [
        "providerProfileCode"
      ]);
    }
    if (policy.processingNoticeVersion !== null) {
      addAiIssue(context, "A disabled AI policy cannot retain an external processing notice", [
        "processingNoticeVersion"
      ]);
    }
    return;
  }
  if (!policy.enabled) {
    addAiIssue(context, "An enabled execution mode requires enabled=true", ["enabled"]);
  }
  if (policy.providerProfileCode === null) {
    addAiIssue(context, "An enabled AI policy requires an allowlisted provider profile", [
      "providerProfileCode"
    ]);
  }
  if (policy.executionMode === "EXTERNAL_ANONYMIZED" && policy.processingNoticeVersion === null) {
    addAiIssue(context, "External processing requires an approved notice version", [
      "processingNoticeVersion"
    ]);
  }
  if (policy.executionMode === "LOCAL_ONLY" && policy.processingNoticeVersion !== null) {
    addAiIssue(context, "A local-only policy cannot retain an external processing notice", [
      "processingNoticeVersion"
    ]);
  }
}
var aiPolicySchema = z3.object({
  organizationId: identifierSchema,
  policyVersion: aiPolicyVersionSchema,
  ...aiPolicyConfigurationShape,
  approvedByMembershipId: identifierSchema.nullable(),
  approvedAt: serverTimestampSchema.nullable(),
  updatedAt: serverTimestampSchema.nullable()
}).strict().superRefine((policy, context) => {
  refineAiPolicyConfiguration(policy, context);
  const requiresApproval = policy.executionMode === "EXTERNAL_ANONYMIZED";
  if (requiresApproval !== (policy.approvedByMembershipId !== null && policy.approvedAt !== null)) {
    addAiIssue(
      context,
      "External processing approval actor and time must exist only for external mode",
      ["approvedAt"]
    );
  }
  if (policy.policyVersion === 0 && policy.updatedAt !== null) {
    addAiIssue(context, "The effective default policy cannot have an update time", ["updatedAt"]);
  }
});
var aiPolicyUpdateBodySchema = z3.object({
  policyVersion: aiPolicyVersionSchema,
  ...aiPolicyConfigurationShape
}).strict().superRefine(refineAiPolicyConfiguration);
var aiProviderProfileMetadataSchema = z3.object({
  profileCode: aiProviderProfileCodeSchema,
  displayName: boundedSafeText(1, 80),
  executionMode: aiEnabledExecutionModeSchema,
  adapterCode: aiProviderComponentCodeSchema,
  providerCode: aiProviderComponentCodeSchema,
  modelCode: aiProviderComponentCodeSchema,
  modelVersion: aiProviderVersionSchema,
  weightsDigest: aiHashSchema,
  analysisConfigVersion: aiProviderVersionSchema
}).strict();
var aiAnalysisProviderMetadataSchema = aiProviderProfileMetadataSchema.omit({
  displayName: true,
  executionMode: true
});
var aiPolicyResponseSchema = z3.object({
  requestId: requestIdSchema,
  policyVersion: aiPolicyVersionSchema,
  serverTimestamp: serverTimestampSchema,
  data: z3.object({
    policy: aiPolicySchema,
    availableProfiles: z3.array(aiProviderProfileMetadataSchema).max(32)
  }).strict()
}).strict().superRefine((response, context) => {
  if (response.policyVersion !== response.data.policy.policyVersion) {
    addAiIssue(context, "Policy envelope and data versions must match", [
      "data",
      "policy",
      "policyVersion"
    ]);
  }
  const profileCodes = /* @__PURE__ */ new Set();
  response.data.availableProfiles.forEach((profile, index) => {
    if (profileCodes.has(profile.profileCode)) {
      addAiIssue(context, "Available provider profile codes must be unique", [
        "data",
        "availableProfiles",
        index,
        "profileCode"
      ]);
    }
    profileCodes.add(profile.profileCode);
  });
});
var getAiPolicyResponseSchema = aiPolicyResponseSchema;
var updateAiPolicyResponseSchema = aiPolicyResponseSchema;
var aiPlayMoveSchema = z3.object({
  kind: z3.literal("PLAY"),
  point: boardPointSchema
}).strict();
var aiPassMoveSchema = z3.object({
  kind: z3.literal("PASS")
}).strict();
var aiMoveSchema = z3.discriminatedUnion("kind", [aiPlayMoveSchema, aiPassMoveSchema]);
var aiBestMoveSchema = z3.object({
  move: aiMoveSchema,
  winRateBps: aiWinRateBpsSchema,
  scoreLeadHalfPoints: aiScoreLeadHalfPointsSchema,
  visits: aiVisitsSchema,
  pv: z3.array(aiMoveSchema).min(1).max(MAX_AI_PV_MOVES)
}).strict().superRefine((candidate, context) => {
  const firstPvMove = candidate.pv[0];
  if (firstPvMove !== void 0 && !sameAiMove(candidate.move, firstPvMove)) {
    addAiIssue(context, "A principal variation must start with its candidate move", ["pv", 0]);
  }
});
var aiAnalysisResultSchema = z3.object({
  schemaVersion: z3.literal(AI_RESULT_SCHEMA_VERSION),
  perspective: z3.literal("BLACK"),
  winRateBps: aiWinRateBpsSchema,
  scoreLeadHalfPoints: aiScoreLeadHalfPointsSchema,
  bestMoves: z3.array(aiBestMoveSchema).min(1).max(MAX_AI_BEST_MOVES)
}).strict().superRefine((result, context) => {
  const candidateMoves = /* @__PURE__ */ new Set();
  result.bestMoves.forEach((candidate, index) => {
    const moveKey = aiMoveKey(candidate.move);
    if (candidateMoves.has(moveKey)) {
      addAiIssue(context, "Best-move candidates must be unique", ["bestMoves", index, "move"]);
    }
    candidateMoves.add(moveKey);
  });
});
var createAiAnalysisBodySchema = z3.object({
  gameId: identifierSchema,
  sourceGameStateVersion: stateVersionSchema,
  positionRevision: aiPositionRevisionSchema,
  profileCode: aiProviderProfileCodeSchema
}).strict();
var cancelAiAnalysisBodySchema = z3.object({
  stateVersion: aiStateVersionSchema
}).strict();
var aiAnalysisFailureCodeSchema = z3.enum([
  "AI_EXTERNAL_PROCESSING_NOT_APPROVED",
  "AI_PROVIDER_PROFILE_UNAVAILABLE",
  "AI_PROVIDER_TEMPORARILY_UNAVAILABLE",
  "AI_RESULT_INVALID",
  "AI_SOURCE_CORRUPTED",
  "AI_PROCESSING_FAILED"
]);
var aiAnalysisTraceShape = {
  analysisId: identifierSchema,
  gameId: identifierSchema,
  stateVersion: aiStateVersionSchema,
  sourceGameStateVersion: stateVersionSchema,
  positionRevision: aiPositionRevisionSchema,
  moveSequence: aiMoveSequenceSchema
};
var aiAnalysisStatusShape = {
  status: aiAnalysisStatusSchema,
  attemptCount: z3.number().int().safe().nonnegative(),
  nextAttemptAt: serverTimestampSchema.nullable(),
  cancelRequestedAt: serverTimestampSchema.nullable(),
  cancelReason: aiCancelReasonSchema.nullable(),
  errorCode: aiAnalysisFailureCodeSchema.nullable(),
  resultHash: aiHashSchema.nullable(),
  queuedAt: serverTimestampSchema,
  startedAt: serverTimestampSchema.nullable(),
  finishedAt: serverTimestampSchema.nullable(),
  expiresAt: serverTimestampSchema.nullable(),
  purgedAt: serverTimestampSchema.nullable(),
  updatedAt: serverTimestampSchema
};
function refineAiAnalysisStatus(analysis, context) {
  const hasCancellation = analysis.cancelRequestedAt !== null && analysis.cancelReason !== null;
  if (analysis.cancelRequestedAt === null !== (analysis.cancelReason === null)) {
    addAiIssue(context, "Cancellation time and reason must be present together", [
      "cancelRequestedAt"
    ]);
  }
  if (analysis.status === "QUEUED") {
    if (analysis.nextAttemptAt === null || hasCancellation || analysis.errorCode !== null || analysis.resultHash !== null || analysis.finishedAt !== null || analysis.expiresAt !== null || analysis.purgedAt !== null) {
      addAiIssue(context, "A queued analysis can only expose queue and prior attempt facts", [
        "status"
      ]);
    }
    return;
  }
  if (analysis.status === "RUNNING") {
    if (analysis.attemptCount < 1 || analysis.startedAt === null || analysis.nextAttemptAt !== null || analysis.errorCode !== null || analysis.resultHash !== null || analysis.finishedAt !== null || analysis.expiresAt !== null || analysis.purgedAt !== null) {
      addAiIssue(context, "A running analysis requires a started attempt and no terminal facts", [
        "status"
      ]);
    }
    return;
  }
  if (analysis.nextAttemptAt !== null || analysis.finishedAt === null || analysis.expiresAt === null) {
    addAiIssue(context, "A terminal analysis requires terminal times and no retry schedule", [
      "status"
    ]);
  }
  if (analysis.status === "SUCCEEDED") {
    if (analysis.attemptCount < 1 || hasCancellation || analysis.errorCode !== null || analysis.resultHash === null || analysis.purgedAt !== null) {
      addAiIssue(context, "A succeeded analysis requires only successful result facts", ["status"]);
    }
    return;
  }
  if (analysis.status === "FAILED") {
    if (analysis.attemptCount < 1 || hasCancellation || analysis.errorCode === null || analysis.resultHash !== null || analysis.purgedAt !== null) {
      addAiIssue(context, "A failed analysis requires one safe error and no result facts", [
        "status"
      ]);
    }
    return;
  }
  if (analysis.status === "CANCELLED") {
    if (!hasCancellation || analysis.errorCode !== null || analysis.resultHash !== null || analysis.purgedAt !== null) {
      addAiIssue(context, "A cancelled analysis requires cancellation facts and no result", [
        "status"
      ]);
    }
    return;
  }
  if (analysis.purgedAt === null || analysis.errorCode !== null) {
    addAiIssue(context, "A purged analysis must retain only safe minimal terminal facts", [
      "status"
    ]);
  }
}
var aiAnalysisSchema = z3.object({
  ...aiAnalysisTraceShape,
  inputSchemaVersion: z3.literal(AI_POSITION_SCHEMA_VERSION),
  ruleset: rulesetSchema,
  boardSize: boardSizeSchema,
  komiHalfPoints: z3.literal(15),
  sourcePositionHash: aiHashSchema,
  inputHash: aiHashSchema,
  provider: aiAnalysisProviderMetadataSchema,
  ...aiAnalysisStatusShape,
  resultSchemaVersion: z3.literal(AI_RESULT_SCHEMA_VERSION).nullable(),
  result: aiAnalysisResultSchema.nullable(),
  createdAt: serverTimestampSchema
}).strict().superRefine((analysis, context) => {
  refineAiAnalysisStatus(analysis, context);
  if (analysis.moveSequence !== null && analysis.moveSequence > analysis.positionRevision) {
    addAiIssue(context, "A move sequence cannot exceed its position revision", ["moveSequence"]);
  }
  const hasResult = analysis.result !== null;
  if (analysis.status === "SUCCEEDED" && (!hasResult || analysis.resultSchemaVersion !== AI_RESULT_SCHEMA_VERSION || analysis.resultHash === null)) {
    addAiIssue(context, "A succeeded analysis requires its versioned normalized result", [
      "result"
    ]);
  }
  if (analysis.status !== "SUCCEEDED" && (hasResult || analysis.resultSchemaVersion !== null)) {
    addAiIssue(context, "Only a succeeded analysis can expose a result body", ["result"]);
  }
  if (analysis.result === null) {
    return;
  }
  const scoreLimit = maxAiScoreLeadHalfPoints(analysis.boardSize);
  if (Math.abs(analysis.result.scoreLeadHalfPoints) > scoreLimit) {
    addAiIssue(context, "Root score lead exceeds the theoretical board bound", [
      "result",
      "scoreLeadHalfPoints"
    ]);
  }
  analysis.result.bestMoves.forEach((candidate, candidateIndex) => {
    if (Math.abs(candidate.scoreLeadHalfPoints) > scoreLimit) {
      addAiIssue(context, "Candidate score lead exceeds the theoretical board bound", [
        "result",
        "bestMoves",
        candidateIndex,
        "scoreLeadHalfPoints"
      ]);
    }
    [candidate.move, ...candidate.pv].forEach((move, moveIndex) => {
      if (move.kind === "PLAY" && (move.point.x >= analysis.boardSize || move.point.y >= analysis.boardSize)) {
        addAiIssue(context, "AI move coordinates must fit the source board", [
          "result",
          "bestMoves",
          candidateIndex,
          moveIndex === 0 ? "move" : "pv",
          ...moveIndex === 0 ? [] : [moveIndex - 1]
        ]);
      }
    });
  });
});
var aiAnalysisSummarySchema = z3.object({
  ...aiAnalysisTraceShape,
  provider: aiAnalysisProviderMetadataSchema,
  ...aiAnalysisStatusShape
}).strict().superRefine(refineAiAnalysisStatus);
var aiAnalysisResponseSchema = z3.object({
  requestId: requestIdSchema,
  analysisId: identifierSchema,
  gameId: identifierSchema,
  stateVersion: aiStateVersionSchema,
  sourceGameStateVersion: stateVersionSchema,
  serverTimestamp: serverTimestampSchema,
  data: z3.object({
    analysis: aiAnalysisSchema
  }).strict()
}).strict().superRefine((response, context) => {
  const analysis = response.data.analysis;
  if (response.analysisId !== analysis.analysisId || response.gameId !== analysis.gameId || response.stateVersion !== analysis.stateVersion || response.sourceGameStateVersion !== analysis.sourceGameStateVersion) {
    addAiIssue(context, "Analysis response trace must match its data", ["data", "analysis"]);
  }
});
var createAiAnalysisResponseSchema = aiAnalysisResponseSchema;
var getAiAnalysisResponseSchema = aiAnalysisResponseSchema;
var cancelAiAnalysisResponseSchema = aiAnalysisResponseSchema;
var aiAnalysesQuerySchema = z3.object({
  positionRevision: aiPositionRevisionSchema.optional(),
  status: z3.array(aiAnalysisStatusSchema).max(6).default([]),
  after: aiCursorSchema.optional(),
  limit: z3.number().int().min(1).max(MAX_AI_ANALYSES_PAGE_SIZE).default(20)
}).strict().superRefine((query, context) => {
  if (new Set(query.status).size !== query.status.length) {
    addAiIssue(context, "Analysis status filters must be unique", ["status"]);
  }
});
var aiAnalysesResponseSchema = z3.object({
  requestId: requestIdSchema,
  gameId: identifierSchema,
  sourceGameStateVersion: stateVersionSchema,
  serverTimestamp: serverTimestampSchema,
  data: z3.object({
    analyses: z3.array(aiAnalysisSummarySchema).max(MAX_AI_ANALYSES_PAGE_SIZE),
    nextCursor: aiCursorSchema.nullable()
  }).strict()
}).strict().superRefine((response, context) => {
  const analysisIds = /* @__PURE__ */ new Set();
  response.data.analyses.forEach((analysis, index) => {
    if (analysis.gameId !== response.gameId || analysis.sourceGameStateVersion !== response.sourceGameStateVersion) {
      addAiIssue(context, "Listed analyses must match the response source game anchor", [
        "data",
        "analyses",
        index
      ]);
    }
    if (analysisIds.has(analysis.analysisId)) {
      addAiIssue(context, "An analysis page cannot contain duplicate analyses", [
        "data",
        "analyses",
        index,
        "analysisId"
      ]);
    }
    analysisIds.add(analysis.analysisId);
  });
});
var getGameAiAnalysesResponseSchema = aiAnalysesResponseSchema;
var aiGameAnalysisConfigurationQuerySchema = z3.object({
  beforePositionRevision: aiStateVersionSchema.optional(),
  limit: z3.number().int().min(1).max(MAX_AI_POSITION_OPTIONS_PAGE_SIZE).default(MAX_AI_POSITION_OPTIONS_PAGE_SIZE)
}).strict();
var aiGameAnalysisConfigurationResponseSchema = z3.object({
  requestId: requestIdSchema,
  gameId: identifierSchema,
  sourceGameStateVersion: stateVersionSchema,
  serverTimestamp: serverTimestampSchema,
  data: z3.object({
    finalPositionRevision: aiPositionRevisionSchema,
    positionRevisions: z3.array(aiPositionRevisionSchema).min(1).max(MAX_AI_POSITION_OPTIONS_PAGE_SIZE),
    nextBeforePositionRevision: aiStateVersionSchema.nullable(),
    profile: aiProviderProfileMetadataSchema,
    processingNoticeVersion: aiProcessingNoticeVersionSchema.nullable()
  }).strict()
}).strict().superRefine((response, context) => {
  const revisions = response.data.positionRevisions;
  revisions.forEach((revision, index) => {
    if (revision > response.data.finalPositionRevision || index > 0 && revision !== revisions[index - 1] - 1) {
      addAiIssue(
        context,
        "AI position options must be a descending contiguous authoritative page",
        ["data", "positionRevisions", index]
      );
    }
  });
  const last = revisions.at(-1);
  if (response.data.nextBeforePositionRevision === null !== (last === 0) || response.data.nextBeforePositionRevision !== null && response.data.nextBeforePositionRevision !== last) {
    addAiIssue(context, "The next position cursor must continue before the last option", [
      "data",
      "nextBeforePositionRevision"
    ]);
  }
});
var aiAnalysisOutboxEventTypeSchema = z3.enum([
  "ai.analysis.queued",
  "ai.analysis.running",
  "ai.analysis.succeeded",
  "ai.analysis.failed",
  "ai.analysis.cancelled",
  "ai.analysis.purged"
]);
var aiAnalysisOutboxEventSchema = z3.object({
  event: aiAnalysisOutboxEventTypeSchema,
  eventId: eventIdSchema,
  requestId: requestIdSchema,
  ...aiAnalysisTraceShape,
  status: aiAnalysisStatusSchema,
  serverTimestamp: serverTimestampSchema
}).strict().superRefine((event, context) => {
  const expectedEvent = `ai.analysis.${event.status.toLowerCase()}`;
  if (event.event !== expectedEvent) {
    addAiIssue(context, "AI analysis event and status must match", ["event"]);
  }
});
var aiPolicyUpdatedOutboxEventSchema = z3.object({
  event: z3.literal("ai.policy.updated"),
  eventId: eventIdSchema,
  requestId: requestIdSchema,
  organizationId: identifierSchema,
  policyVersion: aiPolicyVersionSchema,
  executionMode: aiExecutionModeSchema,
  providerProfileCode: aiProviderProfileCodeSchema.nullable(),
  serverTimestamp: serverTimestampSchema
}).strict().superRefine((event, context) => {
  if (event.executionMode === "DISABLED" !== (event.providerProfileCode === null)) {
    addAiIssue(context, "Policy event mode and provider profile must match", [
      "providerProfileCode"
    ]);
  }
});
var aiOutboxEventSchema = z3.union([
  aiAnalysisOutboxEventSchema,
  aiPolicyUpdatedOutboxEventSchema
]);
var aiErrorCodeSchema = z3.enum([
  "AI_ROLE_REQUIRED",
  "AI_ANALYSIS_NOT_FOUND",
  "AI_MANAGE_ACCESS_REQUIRED",
  "AI_GAME_NOT_FINISHED",
  "AI_POSITION_NOT_FOUND",
  "AI_SOURCE_VERSION_MISMATCH",
  "AI_DISABLED",
  "AI_EXTERNAL_PROCESSING_NOT_APPROVED",
  "AI_PROVIDER_PROFILE_UNAVAILABLE",
  "AI_QUOTA_EXCEEDED",
  "AI_QUEUE_LIMIT_REACHED",
  "AI_POLICY_VERSION_CONFLICT",
  "AI_ANALYSIS_VERSION_CONFLICT",
  "AI_ANALYSIS_NOT_CANCELLABLE",
  "AI_PROVIDER_TEMPORARILY_UNAVAILABLE",
  "AI_RESULT_INVALID",
  "AI_SOURCE_CORRUPTED",
  "AI_REQUEST_ID_REUSED",
  "AI_PROCESSING_FAILED"
]);
var aiApiErrorSchema = z3.object({
  code: aiErrorCodeSchema,
  message: boundedSafeText(1, 200),
  requestId: requestIdSchema,
  analysisId: identifierSchema.optional(),
  gameId: identifierSchema.optional(),
  stateVersion: aiStateVersionSchema.optional(),
  sourceGameStateVersion: stateVersionSchema.optional(),
  policyVersion: aiPolicyVersionSchema.optional(),
  syncRequired: z3.boolean().optional(),
  retryAfterMs: z3.number().int().min(1e3).max(3e5).optional(),
  serverTimestamp: serverTimestampSchema
}).strict().superRefine((error, context) => {
  if (error.code === "AI_ANALYSIS_VERSION_CONFLICT" && (error.analysisId === void 0 || error.gameId === void 0 || error.stateVersion === void 0 || error.sourceGameStateVersion === void 0 || error.syncRequired !== true)) {
    addAiIssue(context, "An analysis version conflict requires full sync trace", [
      "syncRequired"
    ]);
  }
  if (error.code === "AI_POLICY_VERSION_CONFLICT" && (error.policyVersion === void 0 || error.syncRequired !== true)) {
    addAiIssue(context, "A policy version conflict requires the current policy version", [
      "policyVersion"
    ]);
  }
  const retryableCodes = /* @__PURE__ */ new Set([
    "AI_QUOTA_EXCEEDED",
    "AI_QUEUE_LIMIT_REACHED",
    "AI_PROVIDER_TEMPORARILY_UNAVAILABLE"
  ]);
  if (retryableCodes.has(error.code) !== (error.retryAfterMs !== void 0)) {
    addAiIssue(context, "Only bounded retryable AI errors can expose retryAfterMs", [
      "retryAfterMs"
    ]);
  }
});

// src/error-messages.ts
var ERROR_MESSAGES_ZH = Object.freeze({
  EBOOK_SCHEMA_NOT_READY: "\u6559\u6750\u6570\u636E\u7ED3\u6784\u5C1A\u672A\u5C31\u7EEA\uFF0C\u8BF7\u7BA1\u7406\u5458\u5B8C\u6210\u6559\u6750\u6570\u636E\u5E93\u8FC1\u79FB\u540E\u91CD\u8BD5\u3002",
  EBOOK_SERVICE_NOT_READY: "\u6559\u6750\u670D\u52A1\u6682\u4E0D\u53EF\u7528\uFF0C\u8BF7\u7A0D\u540E\u91CD\u8BD5\u6216\u8054\u7CFB\u7BA1\u7406\u5458\u3002",
  AUTH_REQUIRED: "\u767B\u5F55\u72B6\u6001\u5DF2\u5931\u6548\uFF0C\u8BF7\u91CD\u65B0\u767B\u5F55\u3002",
  ACCOUNT_NOT_FOUND: "\u767B\u5F55\u8D26\u6237\u4E0D\u5B58\u5728\u3002",
  ACCOUNT_LOGIN_FAILED: "\u8D26\u53F7\u5BC6\u7801\u9519\u8BEF\u3002",
  ACCOUNT_LOCKED: "\u8D26\u53F7\u5DF2\u4E34\u65F6\u9501\u5B9A\uFF0C\u8BF7\u7A0D\u540E\u91CD\u8BD5\u6216\u8054\u7CFB\u7BA1\u7406\u5458\u89E3\u9501\u3002",
  ACCOUNT_ALREADY_EXISTS: "\u8BE5\u767B\u5F55\u8D26\u53F7\u5DF2\u7ECF\u5B58\u5728\u3002",
  WECHAT_LOGIN_FAILED: "\u5FAE\u4FE1\u767B\u5F55\u5931\u8D25\uFF0C\u8BF7\u91CD\u65B0\u767B\u5F55\u3002",
  INVITATION_INVALID: "\u9080\u8BF7\u7801\u65E0\u6548\u3001\u5DF2\u8FC7\u671F\u6216\u5DF2\u7528\u5B8C\u3002",
  ROLE_REQUIRED: "\u5F53\u524D\u8EAB\u4EFD\u6CA1\u6709\u6267\u884C\u6B64\u64CD\u4F5C\u7684\u6743\u9650\u3002",
  MEMBERSHIP_INACTIVE: "\u5F53\u524D\u673A\u6784\u8EAB\u4EFD\u5DF2\u505C\u7528\uFF0C\u8BF7\u8054\u7CFB\u7BA1\u7406\u5458\u3002",
  FORBIDDEN: "\u4F60\u6CA1\u6709\u6267\u884C\u6B64\u64CD\u4F5C\u7684\u6743\u9650\u3002",
  USER_NOT_FOUND: "\u8BE5\u7528\u6237\u4E0D\u5B58\u5728\u3002",
  ORGANIZATION_NOT_FOUND: "\u8BE5\u673A\u6784\u4E0D\u5B58\u5728\u3002",
  ORGANIZATION_SLUG_CONFLICT: "\u8BE5\u673A\u6784\u6807\u8BC6\u5DF2\u88AB\u4F7F\u7528\u3002",
  ORGANIZATION_MEMBER_NOT_FOUND: "\u8BE5\u673A\u6784\u6210\u5458\u4E0D\u5B58\u5728\u3002",
  CLASS_NOT_FOUND: "\u8BE5\u73ED\u7EA7\u4E0D\u5B58\u5728\u3002",
  CLASS_MEMBER_NOT_FOUND: "\u8BE5\u73ED\u7EA7\u6210\u5458\u4E0D\u5B58\u5728\u3002",
  LAST_ADMIN_REQUIRED: "\u673A\u6784\u81F3\u5C11\u9700\u8981\u4FDD\u7559\u4E00\u540D\u7BA1\u7406\u5458\u3002",
  SESSION_REPLAYED: "\u767B\u5F55\u72B6\u6001\u5DF2\u5931\u6548\uFF0C\u8BF7\u91CD\u65B0\u767B\u5F55\u3002",
  RESOURCE_VERSION_CONFLICT: "\u5185\u5BB9\u5DF2\u66F4\u65B0\uFF0C\u8BF7\u5237\u65B0\u540E\u91CD\u8BD5\u3002",
  REPORT_NOT_READY: "\u62A5\u544A\u5C1A\u672A\u751F\u6210\uFF0C\u8BF7\u7A0D\u540E\u67E5\u770B\u3002",
  TIMING_REPORT_UNAVAILABLE: "\u672C\u5C40\u6682\u65E0\u53EF\u7528\u7684\u7528\u65F6\u62A5\u544A\u3002",
  REVIEW_NOT_FOUND: "\u8BE5\u590D\u76D8\u4E0D\u5B58\u5728\u3002",
  REVIEW_NOT_PUBLISHED: "\u8BE5\u590D\u76D8\u5C1A\u672A\u53D1\u5E03\u3002",
  REVIEW_VERSION_CONFLICT: "\u590D\u76D8\u5185\u5BB9\u5DF2\u66F4\u65B0\uFF0C\u8BF7\u5237\u65B0\u540E\u91CD\u8BD5\u3002",
  PROBLEM_NOT_FOUND: "\u8BE5\u9898\u76EE\u4E0D\u5B58\u5728\u3002",
  PROBLEM_SOURCE_INVALID: "\u9898\u76EE\u6765\u6E90\u65E0\u6548\uFF0C\u8BF7\u91CD\u65B0\u9009\u62E9\u3002",
  HOMEWORK_NOT_FOUND: "\u8BE5\u4F5C\u4E1A\u4E0D\u5B58\u5728\u3002",
  HOMEWORK_VERSION_CONFLICT: "\u4F5C\u4E1A\u5185\u5BB9\u5DF2\u66F4\u65B0\uFF0C\u8BF7\u5237\u65B0\u540E\u91CD\u8BD5\u3002",
  HOMEWORK_NOT_EDITABLE: "\u5F53\u524D\u4F5C\u4E1A\u65E0\u6CD5\u7F16\u8F91\u3002",
  ASSIGNMENT_NOT_FOUND: "\u672A\u627E\u5230\u8BE5\u4F5C\u4E1A\u4EFB\u52A1\u3002",
  ASSIGNMENT_NOT_OPEN: "\u4F5C\u4E1A\u5C1A\u672A\u5F00\u653E\u6216\u5DF2\u622A\u6B62\u3002",
  ATTEMPT_LIMIT_REACHED: "\u5DF2\u8FBE\u5230\u672C\u9898\u4F5C\u7B54\u6B21\u6570\u4E0A\u9650\u3002",
  SUBMISSION_NOT_FOUND: "\u672A\u627E\u5230\u8BE5\u4F5C\u7B54\u8BB0\u5F55\u3002",
  SUBMISSION_VERSION_CONFLICT: "\u4F5C\u7B54\u8BB0\u5F55\u5DF2\u66F4\u65B0\uFF0C\u8BF7\u5237\u65B0\u540E\u91CD\u8BD5\u3002",
  MATCHMAKING_CONFLICT: "\u5F53\u524D\u65E0\u6CD5\u53D1\u8D77\u5339\u914D\uFF0C\u8BF7\u68C0\u67E5\u662F\u5426\u5DF2\u6709\u5F85\u5904\u7406\u7684\u5339\u914D\u6216\u5BF9\u5C40\u3002",
  MATCHMAKING_TICKET_NOT_FOUND: "\u672A\u627E\u5230\u5F53\u524D\u5339\u914D\uFF0C\u8BF7\u91CD\u65B0\u53D1\u8D77\u3002",
  MATCHMAKING_VERSION_CONFLICT: "\u5339\u914D\u72B6\u6001\u5DF2\u66F4\u65B0\uFF0C\u8BF7\u5237\u65B0\u540E\u91CD\u8BD5\u3002",
  TEACHER_ROLE_REQUIRED: "\u4EC5\u6559\u5E08\u53EF\u4EE5\u6267\u884C\u6B64\u64CD\u4F5C\u3002",
  TEACHER_CLASS_SCOPE_DENIED: "\u4F60\u6CA1\u6709\u7BA1\u7406\u8BE5\u73ED\u7EA7\u5BF9\u5C40\u7684\u6743\u9650\u3002",
  INTERVENTION_REASON_REQUIRED: "\u8BF7\u586B\u5199\u5E72\u9884\u539F\u56E0\u3002",
  AI_ROLE_REQUIRED: "\u5F53\u524D\u8EAB\u4EFD\u6CA1\u6709\u4F7F\u7528\u667A\u80FD\u5206\u6790\u7684\u6743\u9650\u3002",
  AI_ANALYSIS_NOT_FOUND: "\u672A\u627E\u5230\u8BE5\u5206\u6790\u4EFB\u52A1\u3002",
  AI_MANAGE_ACCESS_REQUIRED: "\u4F60\u6CA1\u6709\u7BA1\u7406\u667A\u80FD\u5206\u6790\u7684\u6743\u9650\u3002",
  AI_GAME_NOT_FINISHED: "\u5BF9\u5C40\u7ED3\u675F\u540E\u624D\u80FD\u8FDB\u884C\u5206\u6790\u3002",
  AI_POSITION_NOT_FOUND: "\u672A\u627E\u5230\u9700\u8981\u5206\u6790\u7684\u5C40\u9762\u3002",
  AI_SOURCE_VERSION_MISMATCH: "\u68CB\u8C31\u5DF2\u66F4\u65B0\uFF0C\u8BF7\u91CD\u65B0\u53D1\u8D77\u5206\u6790\u3002",
  AI_DISABLED: "\u667A\u80FD\u5206\u6790\u5C1A\u672A\u542F\u7528\uFF0C\u8BF7\u8054\u7CFB\u7BA1\u7406\u5458\u3002",
  AI_EXTERNAL_PROCESSING_NOT_APPROVED: "\u5916\u90E8\u5206\u6790\u670D\u52A1\u5C1A\u672A\u83B7\u51C6\u4F7F\u7528\uFF0C\u8BF7\u8054\u7CFB\u7BA1\u7406\u5458\u3002",
  AI_PROVIDER_PROFILE_UNAVAILABLE: "\u5206\u6790\u670D\u52A1\u914D\u7F6E\u6682\u4E0D\u53EF\u7528\uFF0C\u8BF7\u8054\u7CFB\u7BA1\u7406\u5458\u3002",
  AI_QUOTA_EXCEEDED: "\u5206\u6790\u989D\u5EA6\u5DF2\u7528\u5B8C\uFF0C\u8BF7\u8054\u7CFB\u7BA1\u7406\u5458\u3002",
  AI_QUEUE_LIMIT_REACHED: "\u5206\u6790\u4EFB\u52A1\u8F83\u591A\uFF0C\u8BF7\u7A0D\u540E\u518D\u8BD5\u3002",
  AI_POLICY_VERSION_CONFLICT: "\u5206\u6790\u8BBE\u7F6E\u5DF2\u66F4\u65B0\uFF0C\u8BF7\u5237\u65B0\u540E\u91CD\u8BD5\u3002",
  AI_ANALYSIS_VERSION_CONFLICT: "\u5206\u6790\u4EFB\u52A1\u5DF2\u66F4\u65B0\uFF0C\u8BF7\u5237\u65B0\u540E\u91CD\u8BD5\u3002",
  AI_ANALYSIS_NOT_CANCELLABLE: "\u5F53\u524D\u5206\u6790\u4EFB\u52A1\u65E0\u6CD5\u53D6\u6D88\u3002",
  AI_PROVIDER_TEMPORARILY_UNAVAILABLE: "\u5206\u6790\u670D\u52A1\u6682\u4E0D\u53EF\u7528\uFF0C\u8BF7\u7A0D\u540E\u91CD\u8BD5\u3002",
  AI_RESULT_INVALID: "\u5206\u6790\u7ED3\u679C\u65E0\u6548\uFF0C\u8BF7\u91CD\u65B0\u53D1\u8D77\u5206\u6790\u3002",
  AI_SOURCE_CORRUPTED: "\u68CB\u8C31\u6570\u636E\u5F02\u5E38\uFF0C\u6682\u65F6\u65E0\u6CD5\u5206\u6790\u3002",
  AI_REQUEST_ID_REUSED: "\u8BE5\u5206\u6790\u8BF7\u6C42\u5DF2\u88AB\u4F7F\u7528\uFF0C\u8BF7\u91CD\u65B0\u64CD\u4F5C\u3002",
  AI_PROCESSING_FAILED: "\u5206\u6790\u5931\u8D25\uFF0C\u8BF7\u7A0D\u540E\u91CD\u8BD5\u3002",
  GAME_NOT_FOUND: "\u672A\u627E\u5230\u8BE5\u5BF9\u5C40\u3002",
  ROOM_FULL: "\u623F\u95F4\u4EBA\u6570\u5DF2\u6EE1\u3002",
  INVALID_GAME_STATUS: "\u5F53\u524D\u5BF9\u5C40\u9636\u6BB5\u4E0D\u80FD\u6267\u884C\u6B64\u64CD\u4F5C\u3002",
  NOT_SEATED: "\u5F53\u524D\u8D26\u53F7\u4E0D\u662F\u672C\u5C40\u5BF9\u5F08\u8005\u3002",
  NOT_YOUR_TURN: "\u8FD8\u6CA1\u8F6E\u5230\u4F60\u843D\u5B50\u3002",
  NIGIRI_NOT_ACTIVE: "\u5F53\u524D\u4E0D\u5728\u731C\u5148\u9636\u6BB5\u3002",
  NIGIRI_ACTION_FORBIDDEN: "\u5F53\u524D\u8D26\u53F7\u4E0D\u80FD\u6267\u884C\u8FD9\u4E00\u6B65\u731C\u5148\u64CD\u4F5C\u3002",
  STATE_VERSION_CONFLICT: "\u68CB\u5C40\u72B6\u6001\u5DF2\u66F4\u65B0\uFF0C\u6B63\u5728\u540C\u6B65\u6700\u65B0\u5C40\u9762\u3002",
  REQUEST_ID_REUSE: "\u8BE5\u64CD\u4F5C\u8BF7\u6C42\u5DF2\u88AB\u4F7F\u7528\uFF0C\u8BF7\u91CD\u65B0\u64CD\u4F5C\u3002",
  REQUEST_IN_PROGRESS: "\u8BE5\u64CD\u4F5C\u6B63\u5728\u5904\u7406\u4E2D\uFF0C\u8BF7\u7A0D\u5019\u3002",
  INVALID_PAYLOAD: "\u64CD\u4F5C\u5185\u5BB9\u65E0\u6548\uFF0C\u8BF7\u91CD\u65B0\u64CD\u4F5C\u3002",
  FEATURE_NOT_AVAILABLE: "\u5F53\u524D\u529F\u80FD\u6682\u4E0D\u53EF\u7528\u3002",
  INVALID_BOARD_SIZE: "\u68CB\u76D8\u8DEF\u6570\u8BBE\u7F6E\u65E0\u6548\u3002",
  INVALID_MOVE: "\u8BE5\u70B9\u65E0\u6CD5\u843D\u5B50\u3002",
  WRONG_TURN: "\u8FD8\u6CA1\u8F6E\u5230\u4F60\u843D\u5B50\u3002",
  OUT_OF_BOUNDS: "\u843D\u5B50\u4F4D\u7F6E\u8D85\u51FA\u68CB\u76D8\u3002",
  OCCUPIED: "\u8BE5\u4F4D\u7F6E\u5DF2\u7ECF\u6709\u68CB\u5B50\u3002",
  SUICIDE: "\u6B64\u5904\u843D\u5B50\u4F1A\u4F7F\u5DF1\u65B9\u68CB\u5B50\u6CA1\u6709\u6C14\uFF0C\u4E0D\u80FD\u843D\u5B50\u3002",
  KO_RECAPTURE: "\u6B64\u5904\u7ACB\u5373\u63D0\u52AB\u4E0D\u5408\u6CD5\uFF0C\u8BF7\u5148\u5728\u5176\u4ED6\u4F4D\u7F6E\u843D\u5B50\u3002",
  POSITION_REPETITION: "\u6B64\u5904\u843D\u5B50\u4F1A\u9020\u6210\u5C40\u9762\u91CD\u590D\uFF0C\u4E0D\u80FD\u843D\u5B50\u3002",
  TIME_EXPIRED: "\u68CB\u949F\u65F6\u95F4\u5DF2\u7ECF\u7528\u5B8C\u3002",
  RESOURCE_NOT_FOUND: "\u672A\u627E\u5230\u6240\u9700\u5185\u5BB9\u3002",
  GAME_PAUSED: "\u68CB\u5C40\u5DF2\u88AB\u6559\u5E08\u6682\u505C\u3002",
  GAME_ALREADY_PAUSED: "\u68CB\u5C40\u5DF2\u7ECF\u5904\u4E8E\u6682\u505C\u72B6\u6001\u3002",
  GAME_NOT_PAUSED: "\u68CB\u5C40\u5F53\u524D\u6CA1\u6709\u6682\u505C\u3002",
  PAUSE_OWNERSHIP_REQUIRED: "\u5F53\u524D\u8D26\u53F7\u4E0D\u80FD\u6062\u590D\u8FD9\u6B21\u6682\u505C\u3002",
  COUNTING_REQUEST_NOT_FOUND: "\u672A\u627E\u5230\u672C\u6B21\u6570\u5B50\u7533\u8BF7\u3002",
  COUNTING_REQUEST_NOT_PENDING: "\u672C\u6B21\u6570\u5B50\u7533\u8BF7\u5DF2\u7ECF\u5904\u7406\u3002",
  COUNTING_RESPONSE_FORBIDDEN: "\u5F53\u524D\u8D26\u53F7\u4E0D\u80FD\u5904\u7406\u672C\u6B21\u6570\u5B50\u7533\u8BF7\u3002",
  INVALID_DEAD_STONE: "\u6B7B\u5B50\u6807\u8BB0\u65E0\u6548\uFF0C\u8BF7\u91CD\u65B0\u9009\u62E9\u3002",
  SCORING_PROPOSAL_NOT_FOUND: "\u672A\u627E\u5230\u672C\u6B21\u6570\u5B50\u7ED3\u679C\u3002",
  SCORING_SESSION_NOT_ACTIVE: "\u5F53\u524D\u4E0D\u5728\u6570\u5B50\u9636\u6BB5\u3002",
  SCORING_VERSION_CONFLICT: "\u6570\u5B50\u72B6\u6001\u5DF2\u66F4\u65B0\uFF0C\u8BF7\u6309\u6700\u65B0\u68CB\u76D8\u91CD\u65B0\u64CD\u4F5C\u3002",
  ADJUDICATION_CONFLICT: "\u7EC8\u5C40\u7ED3\u679C\u5DF2\u53D1\u751F\u53D8\u5316\uFF0C\u8BF7\u5237\u65B0\u540E\u67E5\u770B\u3002",
  DISPUTED_GROUP_NOT_FOUND: "\u672A\u627E\u5230\u6240\u9009\u4E89\u8BAE\u68CB\u5757\u3002",
  TAKEBACK_NOT_AVAILABLE: "\u5F53\u524D\u4E0D\u80FD\u7533\u8BF7\u6094\u68CB\u3002",
  TAKEBACK_NOT_FOUND: "\u672A\u627E\u5230\u672C\u6B21\u6094\u68CB\u7533\u8BF7\u3002",
  TAKEBACK_VERSION_CONFLICT: "\u6094\u68CB\u7533\u8BF7\u72B6\u6001\u5DF2\u66F4\u65B0\uFF0C\u8BF7\u91CD\u65B0\u67E5\u770B\u3002",
  INTERNAL_STATE_CORRUPTED: "\u68CB\u5C40\u72B6\u6001\u5F02\u5E38\uFF0C\u8BF7\u7A0D\u540E\u91CD\u8BD5\u3002",
  SERVICE_UNAVAILABLE: "\u670D\u52A1\u6682\u65F6\u4E0D\u53EF\u7528\uFF0C\u8BF7\u7A0D\u540E\u91CD\u8BD5\u3002"
});
var CLIENT_ERROR_MESSAGES_ZH = Object.freeze({
  NETWORK_UNAVAILABLE: "\u65E0\u6CD5\u8FDE\u63A5\u670D\u52A1\u5668\uFF0C\u8BF7\u68C0\u67E5\u7F51\u7EDC\u540E\u91CD\u8BD5\u3002",
  UNEXPECTED_RESPONSE: "\u670D\u52A1\u5668\u54CD\u5E94\u5F02\u5E38\uFF0C\u8BF7\u7A0D\u540E\u91CD\u8BD5\u3002",
  INVALID_SERVER_RESPONSE: "\u670D\u52A1\u5668\u54CD\u5E94\u5F02\u5E38\uFF0C\u8BF7\u5237\u65B0\u540E\u91CD\u8BD5\u3002",
  GAME_SOCKET_NOT_CONNECTED: "\u5BF9\u5C40\u8FDE\u63A5\u5C1A\u672A\u6062\u590D\uFF0C\u8BF7\u7A0D\u5019\u3002",
  TEACHER_SOCKET_NOT_CONNECTED: "\u5B9E\u65F6\u8FDE\u63A5\u5C1A\u672A\u6062\u590D\uFF0C\u8BF7\u7A0D\u5019\u3002",
  CLIENT_ENDPOINT_CONFIGURATION_REQUIRED: "\u670D\u52A1\u8FDE\u63A5\u914D\u7F6E\u7F3A\u5931\uFF0C\u8BF7\u8054\u7CFB\u7BA1\u7406\u5458\u3002",
  PROVIDER_UNAVAILABLE: "\u5206\u6790\u670D\u52A1\u6682\u4E0D\u53EF\u7528\uFF0C\u8BF7\u7A0D\u540E\u91CD\u8BD5\u3002",
  PROVIDER_TIMEOUT: "\u5206\u6790\u8BF7\u6C42\u8D85\u65F6\uFF0C\u8BF7\u7A0D\u540E\u91CD\u8BD5\u3002",
  RESULT_INVALID: "\u5206\u6790\u7ED3\u679C\u65E0\u6548\uFF0C\u8BF7\u91CD\u65B0\u53D1\u8D77\u5206\u6790\u3002"
});
function chineseMessage(message) {
  return typeof message === "string" && /[\u3400-\u9fff]/u.test(message) && !/^\s*(?:\[|\{)|\n\s*at\s/u.test(message);
}
function mappedMessage(code) {
  if (code === void 0) return void 0;
  if (Object.prototype.hasOwnProperty.call(ERROR_MESSAGES_ZH, code)) {
    return ERROR_MESSAGES_ZH[code];
  }
  return Object.prototype.hasOwnProperty.call(CLIENT_ERROR_MESSAGES_ZH, code) ? CLIENT_ERROR_MESSAGES_ZH[code] : void 0;
}
function localizeError(code, message, fallback = "\u64CD\u4F5C\u672A\u5B8C\u6210\uFF0C\u8BF7\u91CD\u8BD5\u3002") {
  if (chineseMessage(message)) return message.trim();
  return mappedMessage(code) ?? mappedMessage(message) ?? (chineseMessage(fallback) ? fallback : "\u64CD\u4F5C\u672A\u5B8C\u6210\uFF0C\u8BF7\u91CD\u8BD5\u3002");
}
function userErrorMessage(error, fallback) {
  if (typeof error === "object" && error !== null) {
    if ("issues" in error && Array.isArray(error.issues)) {
      return "\u8F93\u5165\u5185\u5BB9\u6709\u8BEF\uFF0C\u8BF7\u68C0\u67E5\u540E\u91CD\u8BD5\u3002";
    }
    const code = "code" in error && typeof error.code === "string" ? error.code : void 0;
    const message = "message" in error && typeof error.message === "string" ? error.message : "errMsg" in error && typeof error.errMsg === "string" ? error.errMsg : void 0;
    return localizeError(code, message, fallback);
  }
  return localizeError(void 0, typeof error === "string" ? error : void 0, fallback);
}

// src/classroom-sgf.ts
import { z as z7 } from "zod";

// src/fox-kifu.ts
import { z as z5 } from "zod";

// src/identity.ts
import { z as z4 } from "zod";
var MAX_AUTH_MEMBERSHIPS = 50;
var MAX_ORGANIZATION_MEMBERS_PAGE_SIZE = 100;
var MAX_CLASSES_PAGE_SIZE = 100;
var MAX_CLASS_MEMBERS_PAGE_SIZE = 100;
var boundedText = (minimum, maximum) => z4.string().trim().min(minimum).max(maximum).refine((value) => !containsC0ControlCharacter(value), {
  message: "Text cannot contain C0 control characters"
});
var displayNameSchema = boundedText(1, 64);
var organizationNameSchema = boundedText(1, 128);
var classNameSchema = boundedText(1, 128);
var termLabelSchema = boundedText(1, 64);
var membershipRoleSchema = z4.enum(["STUDENT", "TEACHER", "ADMIN"]);
var membershipStatusSchema = z4.enum(["ACTIVE", "INACTIVE"]);
var userStatusSchema = z4.enum(["ACTIVE", "SUSPENDED", "DISABLED"]);
var classStatusSchema = z4.enum(["ACTIVE", "ARCHIVED"]);
var classMemberStatusSchema = z4.enum(["ACTIVE", "REMOVED"]);
var organizationSlugSchema = z4.string().trim().min(3).max(64).regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/u);
var accountUsernameSchema = z4.string().trim().min(3).max(32).regex(/^[\p{L}\p{N}._-]+$/u);
var accountPasswordSchema = z4.string().min(6).max(128).refine((value) => !containsC0ControlCharacter(value), {
  message: "Password cannot contain C0 control characters"
});
var avatarUrlSchema = z4.string().trim().max(512).url().refine((value) => /^https:\/\//iu.test(value), {
  message: "Avatar URL must use HTTPS"
});
var invitationTokenSchema = z4.string().trim().min(32).max(256).regex(/^[A-Za-z0-9_-]+$/u);
var refreshTokenSchema = z4.string().trim().min(64).max(512).regex(/^[A-Za-z0-9._~-]+$/u);
var accessTokenSchema = z4.string().trim().min(64).max(4096).regex(/^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/u);
var publicUserSchema = z4.object({
  userId: identifierSchema,
  profileVersion: stateVersionSchema,
  displayName: displayNameSchema,
  avatarUrl: avatarUrlSchema.nullable(),
  status: userStatusSchema,
  isPlatformAdmin: z4.boolean().optional()
}).strict();
var organizationSummarySchema = z4.object({
  organizationId: identifierSchema,
  version: stateVersionSchema,
  name: organizationNameSchema,
  slug: organizationSlugSchema
}).strict();
var membershipSummarySchema = z4.object({
  membershipId: identifierSchema,
  organization: organizationSummarySchema,
  role: membershipRoleSchema,
  status: membershipStatusSchema
}).strict();
var sessionTokenPairSchema = z4.object({
  sessionId: identifierSchema,
  accessToken: accessTokenSchema.nullable(),
  accessTokenExpiresAt: serverTimestampSchema.nullable(),
  refreshToken: refreshTokenSchema,
  refreshTokenExpiresAt: serverTimestampSchema,
  selectedMembershipId: identifierSchema.nullable()
}).strict().superRefine((tokens, context) => {
  const accessAvailable = tokens.accessToken !== null;
  if (accessAvailable !== (tokens.accessTokenExpiresAt !== null) || accessAvailable !== (tokens.selectedMembershipId !== null)) {
    context.addIssue({
      code: "custom",
      message: "Access token, expiry, and selected membership must be available together",
      path: ["accessToken"]
    });
  }
});
var wechatLoginRequestSchema = z4.object({
  code: boundedText(1, 1024),
  invitationToken: invitationTokenSchema.optional(),
  membershipId: identifierSchema.optional(),
  displayName: displayNameSchema.optional(),
  avatarUrl: avatarUrlSchema.nullable().optional(),
  deviceId: boundedText(1, 128).optional()
}).strict();
var accountLoginRequestSchema = z4.object({
  username: accountUsernameSchema,
  password: accountPasswordSchema,
  membershipId: identifierSchema.optional(),
  deviceId: boundedText(1, 128).optional()
}).strict();
var refreshSessionRequestSchema = z4.object({
  refreshToken: refreshTokenSchema
}).strict();
var switchMembershipRequestSchema = z4.object({
  refreshToken: refreshTokenSchema,
  membershipId: identifierSchema
}).strict();
var logoutAllRequestSchema = z4.object({
  includeCurrent: z4.boolean().default(false)
}).strict();
var identityResponseShape = {
  requestId: requestIdSchema,
  serverTimestamp: serverTimestampSchema
};
var authMutationResponseSchema = z4.object({
  ...identityResponseShape,
  data: z4.object({
    accepted: z4.literal(true)
  }).strict()
}).strict();
var authSessionResponseSchema = z4.object({
  ...identityResponseShape,
  data: z4.object({
    user: publicUserSchema,
    memberships: z4.array(membershipSummarySchema).max(MAX_AUTH_MEMBERSHIPS),
    tokens: sessionTokenPairSchema
  }).strict()
}).strict().superRefine((response, context) => {
  const selectedMembershipId = response.data.tokens.selectedMembershipId;
  if (selectedMembershipId !== null && !response.data.memberships.some(
    (membership) => membership.membershipId === selectedMembershipId && membership.status === "ACTIVE"
  )) {
    context.addIssue({
      code: "custom",
      message: "The selected membership must be an active returned membership",
      path: ["data", "tokens", "selectedMembershipId"]
    });
  }
});
var currentUserResponseSchema = z4.object({
  ...identityResponseShape,
  data: z4.object({
    user: publicUserSchema,
    currentMembership: membershipSummarySchema
  }).strict()
}).strict();
var updateProfileBodySchema = z4.object({
  profileVersion: stateVersionSchema,
  displayName: displayNameSchema.optional(),
  avatarUrl: avatarUrlSchema.nullable().optional()
}).strict().superRefine((body, context) => {
  if (body.displayName === void 0 && body.avatarUrl === void 0) {
    context.addIssue({
      code: "custom",
      message: "At least one profile field must be updated",
      path: ["displayName"]
    });
  }
});
var membershipsResponseSchema = z4.object({
  ...identityResponseShape,
  data: z4.object({
    memberships: z4.array(membershipSummarySchema).max(MAX_AUTH_MEMBERSHIPS)
  }).strict()
}).strict();
var organizationResponseSchema = z4.object({
  ...identityResponseShape,
  organizationId: identifierSchema,
  version: stateVersionSchema,
  data: organizationSummarySchema
}).strict().superRefine((response, context) => {
  if (response.organizationId !== response.data.organizationId || response.version !== response.data.version) {
    context.addIssue({
      code: "custom",
      message: "Organization response envelope must match its data",
      path: ["data"]
    });
  }
});
var updateOrganizationBodySchema = z4.object({
  version: stateVersionSchema,
  name: organizationNameSchema
}).strict();
var createOrganizationBodySchema = z4.object({
  name: organizationNameSchema,
  slug: organizationSlugSchema
}).strict();
var createOrganizationResponseSchema = z4.object({
  ...identityResponseShape,
  organizationId: identifierSchema,
  version: stateVersionSchema,
  data: z4.object({
    organization: organizationSummarySchema,
    administratorMembership: membershipSummarySchema
  }).strict()
}).strict().superRefine((response, context) => {
  if (response.organizationId !== response.data.organization.organizationId || response.version !== response.data.organization.version || response.data.administratorMembership.organization.organizationId !== response.organizationId || response.data.administratorMembership.role !== "ADMIN" || response.data.administratorMembership.status !== "ACTIVE") {
    context.addIssue({
      code: "custom",
      message: "Created organization authority must match its response envelope",
      path: ["data"]
    });
  }
});
var organizationMembersQuerySchema = z4.object({
  role: membershipRoleSchema.optional(),
  status: membershipStatusSchema.optional(),
  search: boundedText(1, 64).optional(),
  after: identifierSchema.optional(),
  limit: z4.number().int().min(1).max(MAX_ORGANIZATION_MEMBERS_PAGE_SIZE).default(50)
}).strict();
var organizationMemberSchema = z4.object({
  membershipId: identifierSchema,
  version: stateVersionSchema,
  user: publicUserSchema,
  role: membershipRoleSchema,
  status: membershipStatusSchema,
  joinedAt: serverTimestampSchema
}).strict();
var managedOrganizationMemberSchema = organizationMemberSchema.extend({
  accountUsername: accountUsernameSchema.nullable(),
  loginLockedUntil: serverTimestampSchema.nullable().optional()
}).strict();
var organizationMembersResponseSchema = z4.object({
  ...identityResponseShape,
  organizationId: identifierSchema,
  data: z4.object({
    members: z4.array(managedOrganizationMemberSchema).max(MAX_ORGANIZATION_MEMBERS_PAGE_SIZE),
    nextAfter: identifierSchema.nullable()
  }).strict()
}).strict();
var platformOrganizationSchema = organizationSummarySchema.extend({
  memberCount: z4.number().int().nonnegative(),
  activeMemberCount: z4.number().int().nonnegative()
}).strict().refine((organization) => organization.activeMemberCount <= organization.memberCount, {
  message: "Active member count cannot exceed total member count",
  path: ["activeMemberCount"]
});
var platformOrganizationsQuerySchema = z4.object({
  search: boundedText(1, 128).optional()
}).strict();
var platformOrganizationsResponseSchema = z4.object({
  ...identityResponseShape,
  data: z4.object({
    organizations: z4.array(platformOrganizationSchema).max(1e3)
  }).strict()
}).strict();
var createManagedAccountBodySchema = z4.object({
  username: accountUsernameSchema,
  password: accountPasswordSchema,
  displayName: displayNameSchema,
  role: z4.enum(["STUDENT", "TEACHER"])
}).strict();
var createManagedAccountResponseSchema = z4.object({
  ...identityResponseShape,
  organizationId: identifierSchema,
  version: stateVersionSchema,
  data: z4.object({
    accountUsername: accountUsernameSchema,
    member: managedOrganizationMemberSchema
  }).strict()
}).strict().superRefine((response, context) => {
  if (response.version !== response.data.member.version || response.data.member.accountUsername !== response.data.accountUsername) {
    context.addIssue({
      code: "custom",
      message: "Managed account response must match the created membership",
      path: ["data"]
    });
  }
});
var updateManagedAccountProfileBodySchema = z4.object({
  profileVersion: stateVersionSchema,
  displayName: displayNameSchema
}).strict();
var resetManagedAccountPasswordBodySchema = z4.object({
  profileVersion: stateVersionSchema,
  password: accountPasswordSchema
}).strict();
var unlockManagedAccountBodySchema = z4.object({ profileVersion: stateVersionSchema }).strict();
var createInvitationBodySchema = z4.object({
  role: z4.enum(["STUDENT", "TEACHER"]),
  expiresInSeconds: z4.number().int().min(300).max(2592e3),
  maxUses: z4.number().int().min(1).max(100).default(1)
}).strict();
var organizationInvitationSchema = z4.object({
  invitationId: identifierSchema,
  organizationId: identifierSchema,
  version: stateVersionSchema,
  role: z4.enum(["STUDENT", "TEACHER"]),
  maxUses: z4.number().int().min(1).max(100),
  remainingUses: z4.number().int().min(0).max(100),
  expiresAt: serverTimestampSchema,
  revokedAt: serverTimestampSchema.nullable(),
  createdAt: serverTimestampSchema
}).strict().superRefine((invitation, context) => {
  if (invitation.remainingUses > invitation.maxUses) {
    context.addIssue({
      code: "custom",
      message: "Invitation remaining uses cannot exceed max uses",
      path: ["remainingUses"]
    });
  }
});
var createInvitationResponseSchema = z4.object({
  ...identityResponseShape,
  organizationId: identifierSchema,
  version: stateVersionSchema,
  data: z4.object({
    invitation: organizationInvitationSchema,
    invitationToken: invitationTokenSchema
  }).strict()
}).strict().superRefine((response, context) => {
  if (response.organizationId !== response.data.invitation.organizationId || response.version !== response.data.invitation.version) {
    context.addIssue({
      code: "custom",
      message: "Invitation response envelope must match its data",
      path: ["data", "invitation"]
    });
  }
});
var revokeInvitationBodySchema = z4.object({
  version: stateVersionSchema
}).strict();
var invitationResponseSchema = z4.object({
  ...identityResponseShape,
  organizationId: identifierSchema,
  version: stateVersionSchema,
  data: organizationInvitationSchema
}).strict();
var updateOrganizationMemberBodySchema = z4.object({
  version: stateVersionSchema,
  role: membershipRoleSchema.optional(),
  status: membershipStatusSchema.optional()
}).strict().superRefine((body, context) => {
  if (body.role === void 0 && body.status === void 0) {
    context.addIssue({
      code: "custom",
      message: "At least one organization member field must be updated",
      path: ["role"]
    });
  }
});
var organizationMemberResponseSchema = z4.object({
  ...identityResponseShape,
  organizationId: identifierSchema,
  version: stateVersionSchema,
  data: managedOrganizationMemberSchema
}).strict();
var classSummarySchema = z4.object({
  classId: identifierSchema,
  organizationId: identifierSchema,
  scopeVersion: stateVersionSchema,
  name: classNameSchema,
  termLabel: termLabelSchema.nullable(),
  status: classStatusSchema,
  teacher: organizationMemberSchema,
  activeStudentCount: z4.number().int().nonnegative()
}).strict().superRefine((value, context) => {
  if (value.teacher.role !== "TEACHER" && value.teacher.role !== "ADMIN") {
    context.addIssue({
      code: "custom",
      message: "A class teacher must have a teaching role",
      path: ["teacher", "role"]
    });
  }
});
var classesQuerySchema = z4.object({
  status: classStatusSchema.optional(),
  after: identifierSchema.optional(),
  limit: z4.number().int().min(1).max(MAX_CLASSES_PAGE_SIZE).default(50)
}).strict();
var classesResponseSchema = z4.object({
  ...identityResponseShape,
  data: z4.object({
    classes: z4.array(classSummarySchema).max(MAX_CLASSES_PAGE_SIZE),
    nextAfter: identifierSchema.nullable()
  }).strict()
}).strict();
var createClassBodySchema = z4.object({
  name: classNameSchema,
  termLabel: termLabelSchema.nullable().optional(),
  teacherMembershipId: identifierSchema
}).strict();
var updateClassBodySchema = z4.object({
  scopeVersion: stateVersionSchema,
  name: classNameSchema.optional(),
  termLabel: termLabelSchema.nullable().optional(),
  teacherMembershipId: identifierSchema.optional(),
  status: classStatusSchema.optional()
}).strict().superRefine((body, context) => {
  if (body.name === void 0 && body.termLabel === void 0 && body.teacherMembershipId === void 0 && body.status === void 0) {
    context.addIssue({
      code: "custom",
      message: "At least one class field must be updated",
      path: ["name"]
    });
  }
});
var classResponseSchema = z4.object({
  ...identityResponseShape,
  classId: identifierSchema,
  stateVersion: stateVersionSchema,
  data: classSummarySchema
}).strict().superRefine((response, context) => {
  if (response.classId !== response.data.classId || response.stateVersion !== response.data.scopeVersion) {
    context.addIssue({
      code: "custom",
      message: "Class response envelope must match its data",
      path: ["data"]
    });
  }
});
var classMembersQuerySchema = z4.object({
  status: classMemberStatusSchema.optional(),
  after: identifierSchema.optional(),
  limit: z4.number().int().min(1).max(MAX_CLASS_MEMBERS_PAGE_SIZE).default(50)
}).strict();
var classMemberSchema = z4.object({
  classMemberId: identifierSchema,
  membership: organizationMemberSchema,
  status: classMemberStatusSchema,
  joinedAt: serverTimestampSchema,
  removedAt: serverTimestampSchema.nullable()
}).strict().superRefine((member, context) => {
  if (member.membership.role !== "STUDENT") {
    context.addIssue({
      code: "custom",
      message: "A class member must be a student membership",
      path: ["membership", "role"]
    });
  }
  if (member.status === "ACTIVE" && member.removedAt !== null || member.status === "REMOVED" && member.removedAt === null) {
    context.addIssue({
      code: "custom",
      message: "Class member removal time must match its status",
      path: ["removedAt"]
    });
  }
});
var classMembersResponseSchema = z4.object({
  ...identityResponseShape,
  classId: identifierSchema,
  stateVersion: stateVersionSchema,
  data: z4.object({
    members: z4.array(classMemberSchema).max(MAX_CLASS_MEMBERS_PAGE_SIZE),
    nextAfter: identifierSchema.nullable()
  }).strict()
}).strict();
var addClassMemberBodySchema = z4.object({
  scopeVersion: stateVersionSchema,
  membershipId: identifierSchema
}).strict();
var removeClassMemberBodySchema = z4.object({
  scopeVersion: stateVersionSchema
}).strict();
var classMemberResponseSchema = z4.object({
  ...identityResponseShape,
  classId: identifierSchema,
  stateVersion: stateVersionSchema,
  data: classMemberSchema
}).strict();

// src/fox-kifu.ts
var foxSafeTextSchema = z5.string().trim().min(1).max(128).refine((value) => !containsC0ControlCharacter(value), {
  message: "Text cannot contain C0 control characters"
});
var foxKifuDateSchema = z5.iso.date();
var foxKifuQueryTextSchema = foxSafeTextSchema.min(2).max(64);
var foxKifuUidSchema = z5.string().trim().regex(/^\d{1,20}$/u);
var foxKifuRecordIdSchema = z5.string().trim().regex(/^[A-Za-z0-9_-]{1,128}$/u);
var foxKifuSearchQuerySchema = z5.object({
  query: foxKifuQueryTextSchema,
  from: foxKifuDateSchema.optional(),
  to: foxKifuDateSchema.optional(),
  page: z5.number().int().min(1).max(1e3).default(1),
  pageSize: z5.number().int().min(2).max(20).default(10)
}).strict().superRefine((query, context) => {
  if (query.from !== void 0 && query.to !== void 0 && query.from > query.to) {
    context.addIssue({
      code: "custom",
      message: "The start date cannot be later than the end date",
      path: ["from"]
    });
    return;
  }
  if (query.from !== void 0 && query.to !== void 0) {
    const from = Date.parse(`${query.from}T00:00:00Z`);
    const to = Date.parse(`${query.to}T00:00:00Z`);
    if (to - from > 366 * 864e5) {
      context.addIssue({
        code: "custom",
        message: "The date range cannot exceed 366 days",
        path: ["to"]
      });
    }
  }
});
var foxKifuPlayerSchema = z5.object({
  uid: foxKifuUidSchema,
  nickname: foxSafeTextSchema.max(64),
  rank: z5.string().trim().max(32)
}).strict();
var foxKifuGamePlayerSchema = z5.object({
  uid: foxKifuUidSchema,
  nickname: foxSafeTextSchema.max(64),
  rank: z5.string().trim().max(32)
}).strict();
var foxKifuGameFamilySchema = z5.enum(["GO", "FLYING_KNIFE"]);
var foxKifuRecordSummarySchema = z5.object({
  recordId: foxKifuRecordIdSchema,
  playedAt: serverTimestampSchema,
  durationSeconds: z5.number().int().safe().nonnegative().max(31622400).nullable(),
  title: z5.string().trim().max(256),
  gameFamily: foxKifuGameFamilySchema,
  gameTypeLabel: foxSafeTextSchema.max(64),
  boardSize: boardSizeSchema,
  moveCount: z5.number().int().safe().nonnegative().max(1e3),
  black: foxKifuGamePlayerSchema,
  white: foxKifuGamePlayerSchema,
  winner: stoneColorSchema.nullable(),
  resultText: foxSafeTextSchema.max(64),
  playerColor: stoneColorSchema,
  outcome: z5.enum(["WIN", "LOSS", "OTHER"])
}).strict();
var foxKifuStatisticsSchema = z5.object({
  total: z5.number().int().safe().nonnegative(),
  wins: z5.number().int().safe().nonnegative(),
  losses: z5.number().int().safe().nonnegative(),
  others: z5.number().int().safe().nonnegative(),
  winRate: z5.number().min(0).max(100),
  asBlack: z5.number().int().safe().nonnegative(),
  asWhite: z5.number().int().safe().nonnegative(),
  blackWins: z5.number().int().safe().nonnegative(),
  whiteWins: z5.number().int().safe().nonnegative(),
  blackWinRate: z5.number().min(0).max(100),
  whiteWinRate: z5.number().min(0).max(100),
  under15Minutes: z5.number().int().safe().nonnegative(),
  between15And30Minutes: z5.number().int().safe().nonnegative(),
  over30Minutes: z5.number().int().safe().nonnegative(),
  unknownDuration: z5.number().int().safe().nonnegative()
}).strict().superRefine((statistics, context) => {
  if (statistics.wins + statistics.losses + statistics.others !== statistics.total || statistics.asBlack + statistics.asWhite !== statistics.total || statistics.blackWins > statistics.asBlack || statistics.whiteWins > statistics.asWhite || statistics.under15Minutes + statistics.between15And30Minutes + statistics.over30Minutes + statistics.unknownDuration !== statistics.total) {
    context.addIssue({
      code: "custom",
      message: "Fox kifu statistics are internally inconsistent",
      path: ["total"]
    });
  }
});
var foxKifuSearchDataSchema = z5.object({
  player: foxKifuPlayerSchema,
  range: z5.object({
    from: foxKifuDateSchema.nullable(),
    to: foxKifuDateSchema.nullable()
  }).strict(),
  scanComplete: z5.boolean(),
  total: z5.number().int().safe().nonnegative(),
  page: z5.number().int().min(1),
  pageSize: z5.number().int().min(2).max(20),
  records: z5.array(foxKifuRecordSummarySchema).max(20),
  statistics: foxKifuStatisticsSchema
}).strict();
var foxKifuSearchResponseSchema = z5.object({
  requestId: requestIdSchema,
  serverTimestamp: serverTimestampSchema,
  data: foxKifuSearchDataSchema
}).strict();
var foxKifuReplayFrameSchema = z5.object({
  moveNumber: z5.number().int().safe().nonnegative().max(1e3),
  kind: z5.enum(["OPENING", "PLAY", "PASS"]),
  color: stoneColorSchema.nullable(),
  point: boardPointSchema.nullable(),
  stones: z5.array(stoneColorSchema.nullable()).max(361)
}).strict().superRefine((frame, context) => {
  const opening = frame.kind === "OPENING";
  if (opening !== (frame.moveNumber === 0 && frame.color === null && frame.point === null) || frame.kind === "PASS" && frame.point !== null || frame.kind === "PLAY" && (frame.color === null || frame.point === null)) {
    context.addIssue({
      code: "custom",
      message: "Fox kifu replay frame fields do not match its kind",
      path: ["kind"]
    });
  }
});
var foxKifuReplayRecordSchema = z5.object({
  recordId: foxKifuRecordIdSchema,
  title: z5.string().trim().max(256),
  playedDate: foxKifuDateSchema.nullable(),
  boardSize: boardSizeSchema,
  blackName: foxSafeTextSchema.max(64),
  whiteName: foxSafeTextSchema.max(64),
  blackRank: z5.string().trim().max(32),
  whiteRank: z5.string().trim().max(32),
  resultText: foxSafeTextSchema.max(64)
}).strict();
var foxKifuSourceBranchSchema = z5.object({
  id: z5.number().int().min(1).max(1e4),
  parentId: z5.number().int().min(0).max(1e4),
  baseMoveNumber: z5.number().int().min(0).max(1e3),
  frames: z5.array(foxKifuReplayFrameSchema).max(1e3)
}).strict();
var foxKifuRecordDataSchema = z5.object({
  record: foxKifuReplayRecordSchema,
  frames: z5.array(foxKifuReplayFrameSchema).min(1).max(1001),
  sourceBranches: z5.array(foxKifuSourceBranchSchema).max(1e4).optional()
}).strict();
function validateFoxKifuSourceBranches(data, context) {
  const lengths = /* @__PURE__ */ new Map([[0, data.frames.length - 1]]);
  let total = data.frames.length - 1;
  for (const [index, branch] of (data.sourceBranches ?? []).entries()) {
    const parentLength = lengths.get(branch.parentId);
    total += branch.frames.length;
    if (lengths.has(branch.id) || parentLength === void 0 || branch.baseMoveNumber > parentLength || branch.baseMoveNumber + branch.frames.length > 1e3 || total > 1e4 || branch.frames.some(
      (frame, offset) => frame.moveNumber !== branch.baseMoveNumber + offset + 1 || frame.stones.length !== data.record.boardSize ** 2 || frame.point !== null && (frame.point.x >= data.record.boardSize || frame.point.y >= data.record.boardSize)
    )) {
      context.addIssue({
        code: "custom",
        message: "Source branches must reference an earlier parent and contain contiguous replay frames",
        path: ["sourceBranches", index]
      });
    }
    lengths.set(branch.id, branch.baseMoveNumber + branch.frames.length);
  }
}
var foxKifuRecordResponseSchema = z5.object({
  requestId: requestIdSchema,
  serverTimestamp: serverTimestampSchema,
  data: foxKifuRecordDataSchema
}).strict().superRefine((response, context) => {
  validateFoxKifuSourceBranches(response.data, context);
  const expectedStones = response.data.record.boardSize ** 2;
  response.data.frames.forEach((frame, index) => {
    if (frame.moveNumber !== index || frame.stones.length !== expectedStones) {
      context.addIssue({
        code: "custom",
        message: "Fox kifu replay frames must be contiguous and match the board size",
        path: ["data", "frames", index]
      });
    }
  });
});
var foxKifuResearchBodySchema = z5.object({
  baseMoveNumber: z5.number().int().safe().nonnegative().max(1e3),
  moves: z5.array(boardPointSchema).max(500)
}).strict();
var foxKifuResearchDataSchema = z5.object({
  recordId: foxKifuRecordIdSchema,
  baseMoveNumber: z5.number().int().safe().nonnegative().max(1e3),
  variationMoveCount: z5.number().int().safe().nonnegative().max(500),
  nextColor: stoneColorSchema,
  stones: z5.array(stoneColorSchema.nullable()).max(361)
}).strict();
var foxKifuResearchResponseSchema = z5.object({
  requestId: requestIdSchema,
  serverTimestamp: serverTimestampSchema,
  data: foxKifuResearchDataSchema
}).strict();
var foxKifuObservationResearchBodySchema = foxKifuResearchBodySchema.extend({
  baseMoveNumber: z5.number().int().safe().nonnegative().max(1e4),
  stateVersion: stateVersionSchema
});
var foxKifuObservationResearchResponseSchema = foxKifuResearchResponseSchema.extend({
  gameId: identifierSchema,
  stateVersion: stateVersionSchema,
  data: foxKifuResearchDataSchema.extend({
    baseMoveNumber: z5.number().int().safe().nonnegative().max(1e4)
  })
}).superRefine((response, context) => {
  if (response.gameId !== response.data.recordId)
    context.addIssue({
      code: "custom",
      message: "Observation research must belong to its game",
      path: ["data", "recordId"]
    });
});
var foxKifuAiAnalysisStatusSchema = z5.enum([
  "QUEUED",
  "RUNNING",
  "SUCCEEDED",
  "FAILED",
  "CANCELLED"
]);
var foxKifuAiFailureCodeSchema = z5.enum([
  "PROVIDER_UNAVAILABLE",
  "PROVIDER_TIMEOUT",
  "RESULT_INVALID",
  "PROCESSING_FAILED"
]);
var foxKifuAiExclusiveTierSchema = z5.enum([
  "ON_DEMAND_1X",
  "ON_DEMAND_3X",
  "ON_DEMAND_6X",
  "ON_DEMAND_12X",
  "ON_DEMAND_24X"
]);
var foxKifuAiComputeTierSchema = z5.union([
  z5.literal("VIP_SHARED"),
  foxKifuAiExclusiveTierSchema
]);
var foxKifuAiComputeModeSchema = z5.enum(["VIP_SHARED", "EXCLUSIVE"]);
var foxKifuAiEngineWeightSchema = z5.string().regex(/^[A-Za-z0-9][A-Za-z0-9._+-]{0,31}$/);
var foxKifuAiEngineCatalogSchema = z5.object({
  weights: z5.array(
    z5.object({
      name: foxKifuAiEngineWeightSchema,
      description: z5.string().max(180)
    }).strict()
  ).min(1).max(200),
  serverVersion: z5.string().max(180),
  checkedAt: serverTimestampSchema.nullable(),
  source: z5.enum(["PROVIDER", "CACHE", "BUILT_IN"]),
  stale: z5.boolean(),
  warning: z5.string().max(300).nullable()
}).strict();
var foxKifuAiEngineCatalogResponseSchema = z5.object({
  requestId: requestIdSchema,
  serverTimestamp: serverTimestampSchema,
  data: foxKifuAiEngineCatalogSchema
}).strict();
var foxKifuAiMaxVisitsSchema = z5.number().int().min(0).max(1e5).refine((value) => value === 0 || value >= 100, {
  message: "Zhizi max visits must be 0 (unlimited) or at least 100"
});
var foxKifuAiComputeSelectionSchema = z5.discriminatedUnion("mode", [
  z5.object({
    mode: z5.literal("VIP_SHARED"),
    weight: foxKifuAiEngineWeightSchema,
    maxVisits: foxKifuAiMaxVisitsSchema
  }).strict(),
  z5.object({
    mode: z5.literal("EXCLUSIVE"),
    tier: foxKifuAiExclusiveTierSchema,
    weight: foxKifuAiEngineWeightSchema,
    maxVisits: foxKifuAiMaxVisitsSchema
  }).strict()
]);
var foxKifuAiEngineModelSchema = z5.string().min(1).max(180);
var foxKifuAiPublicProfileSchema = z5.object({
  computeTier: foxKifuAiComputeTierSchema,
  computeMode: foxKifuAiComputeModeSchema,
  exclusiveTier: foxKifuAiExclusiveTierSchema.nullable(),
  engineWeight: foxKifuAiEngineWeightSchema,
  engineModel: foxKifuAiEngineModelSchema,
  engineBackend: z5.literal("TensorRT"),
  recommendationCount: z5.literal(10),
  maxVisits: foxKifuAiMaxVisitsSchema
}).strict().superRefine((profile, context) => {
  const exclusive = profile.computeMode === "EXCLUSIVE";
  if (exclusive !== (profile.exclusiveTier !== null)) {
    context.addIssue({
      code: "custom",
      message: "Exclusive Zhizi compute mode requires exactly one exclusive tier",
      path: ["exclusiveTier"]
    });
  }
  if (profile.computeTier !== (profile.computeMode === "VIP_SHARED" ? "VIP_SHARED" : profile.exclusiveTier)) {
    context.addIssue({
      code: "custom",
      message: "Zhizi public compute mode and tier are inconsistent",
      path: ["computeTier"]
    });
  }
});
var foxKifuAiAccountIdentitySchema = z5.object({
  configurationId: identifierSchema,
  accountMasked: z5.string().trim().min(1).max(320),
  accountLabel: z5.string().trim().max(60).optional()
}).strict();
var foxKifuAiAccountChoiceSchema = foxKifuAiAccountIdentitySchema.extend({
  profile: foxKifuAiPublicProfileSchema
});
var foxKifuAiDefaultBodySchema = z5.object({
  configurationId: identifierSchema,
  version: stateVersionSchema
}).strict();
var foxKifuAiDefaultResponseSchema = z5.object({
  requestId: requestIdSchema,
  serverTimestamp: serverTimestampSchema,
  data: foxKifuAiDefaultBodySchema
}).strict();
var foxKifuAiStatusResponseSchema = z5.object({
  requestId: requestIdSchema,
  serverTimestamp: serverTimestampSchema,
  data: z5.object({
    enabled: z5.boolean(),
    configured: z5.boolean(),
    authorized: z5.boolean(),
    available: z5.boolean(),
    profile: foxKifuAiPublicProfileSchema.nullable(),
    account: foxKifuAiAccountIdentitySchema.nullable().optional(),
    accounts: z5.array(foxKifuAiAccountChoiceSchema).max(20).optional(),
    defaultConfigurationId: identifierSchema.nullable().optional(),
    defaultVersion: stateVersionSchema.optional(),
    selectionSource: z5.enum(["DEFAULT", "ORGANIZATION", "LEGACY", "ASSIGNED", "NONE"]).optional()
  }).strict()
}).strict().superRefine((response, context) => {
  if (response.data.available && (!response.data.enabled || !response.data.configured || !response.data.authorized)) {
    context.addIssue({
      code: "custom",
      message: "Fox kifu AI availability requires enablement, configuration, and authorization",
      path: ["data", "available"]
    });
  }
  if (response.data.configured !== (response.data.profile !== null)) {
    context.addIssue({
      code: "custom",
      message: "Configured provider state requires a public compute profile",
      path: ["data", "profile"]
    });
  }
});
var foxKifuAiAnalysisBodySchema = foxKifuResearchBodySchema.extend({
  configurationId: identifierSchema.optional()
});
var foxKifuObservationAiAnalysisBodySchema = foxKifuObservationResearchBodySchema.extend({ configurationId: identifierSchema.optional() }).refine((body) => body.baseMoveNumber + body.moves.length <= 1500, {
  message: "AI analysis supports at most 1500 combined game and research moves",
  path: ["baseMoveNumber"]
});
var foxKifuClassroomPositionBodySchema = z5.object({
  boardSize: boardSizeSchema,
  currentPlayer: stoneColorSchema,
  stones: z5.array(stoneColorSchema.nullable()).max(361),
  // Imported record prefix, including passes, before the classroom variation.
  history: z5.array(
    z5.object({
      color: stoneColorSchema,
      point: boardPointSchema.nullable()
    }).strict()
  ).max(1e3).optional()
}).strict().superRefine((position, context) => {
  if (position.stones.length !== position.boardSize * position.boardSize) {
    context.addIssue({
      code: "custom",
      message: "Classroom AI board position must match its board size",
      path: ["stones"]
    });
  }
});
var foxKifuClassroomAiAnalysisBodySchema = foxKifuClassroomPositionBodySchema.safeExtend({
  // Stones describe the setup; replay the actual moves to retain ko/superko history.
  moves: z5.array(boardPointSchema).max(500).optional(),
  configurationId: identifierSchema.optional()
});
var foxKifuClassroomResearchBodySchema = foxKifuClassroomPositionBodySchema.safeExtend({
  moves: z5.array(boardPointSchema).max(500)
});
var foxKifuAiAnalysisSchema = z5.object({
  analysisId: identifierSchema,
  account: foxKifuAiAccountIdentitySchema.optional(),
  recordId: foxKifuRecordIdSchema,
  status: foxKifuAiAnalysisStatusSchema,
  nextColor: stoneColorSchema,
  sourcePositionHash: z5.string().regex(/^[a-f0-9]{64}$/u),
  result: aiAnalysisResultSchema.nullable(),
  errorCode: foxKifuAiFailureCodeSchema.nullable(),
  createdAt: serverTimestampSchema,
  updatedAt: serverTimestampSchema
}).strict().superRefine((analysis, context) => {
  if (Date.parse(analysis.updatedAt) < Date.parse(analysis.createdAt)) {
    context.addIssue({
      code: "custom",
      message: "Fox kifu AI update time cannot precede creation time",
      path: ["updatedAt"]
    });
  }
  const running = analysis.status === "RUNNING";
  const succeeded = analysis.status === "SUCCEEDED";
  const failed = analysis.status === "FAILED";
  const resultPresent = analysis.result !== null;
  if (succeeded && !resultPresent || !running && !succeeded && resultPresent || failed !== (analysis.errorCode !== null)) {
    context.addIssue({
      code: "custom",
      message: "Fox kifu AI terminal fields must match its status",
      path: ["status"]
    });
  }
});
var foxKifuAiAnalysisResponseSchema = z5.object({
  requestId: requestIdSchema,
  serverTimestamp: serverTimestampSchema,
  data: z5.object({
    analysis: foxKifuAiAnalysisSchema
  }).strict()
}).strict();
var foxKifuObservationAiAnalysisResponseSchema = foxKifuAiAnalysisResponseSchema.extend({
  gameId: identifierSchema,
  stateVersion: stateVersionSchema,
  baseMoveNumber: z5.number().int().safe().nonnegative().max(1e4)
}).superRefine((response, context) => {
  if (response.gameId !== response.data.analysis.recordId)
    context.addIssue({
      code: "custom",
      message: "Observation analysis must belong to its game",
      path: ["data", "analysis", "recordId"]
    });
});
var foxKifuAiCandidateRankSchema = z5.number().int().min(1).max(10);
var foxKifuAiVariationFrameSchema = z5.object({
  moveNumber: z5.number().int().min(1).max(11),
  kind: z5.enum(["PLAY", "PASS"]),
  color: stoneColorSchema,
  point: boardPointSchema.nullable()
}).strict().superRefine((frame, context) => {
  if (frame.kind === "PLAY" !== (frame.point !== null)) {
    context.addIssue({
      code: "custom",
      message: "AI variation play frames require a point and pass frames forbid one",
      path: ["point"]
    });
  }
});
var foxKifuAiVariationDataSchema = z5.object({
  analysisId: identifierSchema,
  candidateRank: foxKifuAiCandidateRankSchema,
  boardSize: boardSizeSchema,
  moveCount: z5.number().int().min(1).max(11),
  nextColor: stoneColorSchema,
  stones: z5.array(stoneColorSchema.nullable()).max(361),
  frames: z5.array(foxKifuAiVariationFrameSchema).min(1).max(11)
}).strict().superRefine((variation, context) => {
  if (variation.stones.length !== variation.boardSize * variation.boardSize || variation.frames.length !== variation.moveCount || variation.frames.some((frame, index) => frame.moveNumber !== index + 1)) {
    context.addIssue({
      code: "custom",
      message: "AI variation frames and final board must be contiguous and size-consistent",
      path: ["frames"]
    });
  }
});
var foxKifuAiVariationResponseSchema = z5.object({
  requestId: requestIdSchema,
  serverTimestamp: serverTimestampSchema,
  data: foxKifuAiVariationDataSchema
}).strict();
var zhiziAccountSchema = z5.string().trim().min(3).max(320).refine((value) => !containsC0ControlCharacter(value), {
  message: "Zhizi account cannot contain control characters"
});
var zhiziPasswordSchema = z5.string().min(1).max(8192).refine((value) => !containsC0ControlCharacter(value), {
  message: "Zhizi password cannot contain control characters"
});
var foxKifuAiProviderConfigurationBodySchema = z5.object({
  version: stateVersionSchema,
  account: zhiziAccountSchema,
  password: zhiziPasswordSchema,
  compute: foxKifuAiComputeSelectionSchema
}).strict();
var foxKifuAiProviderConfigurationDataSchema = z5.object({
  enabled: z5.boolean(),
  configured: z5.boolean(),
  accountMasked: z5.string().trim().min(1).max(320).nullable(),
  version: stateVersionSchema,
  updatedAt: serverTimestampSchema.nullable(),
  workerAvailable: z5.boolean(),
  profile: foxKifuAiPublicProfileSchema.nullable()
}).strict().superRefine((configuration, context) => {
  if (configuration.configured !== (configuration.accountMasked !== null && configuration.updatedAt !== null)) {
    context.addIssue({
      code: "custom",
      message: "Configured provider state requires masked account metadata",
      path: ["configured"]
    });
  }
  if (configuration.configured !== (configuration.profile !== null)) {
    context.addIssue({
      code: "custom",
      message: "Configured provider state requires a public compute profile",
      path: ["profile"]
    });
  }
});
var foxKifuAiProviderConfigurationResponseSchema = z5.object({
  requestId: requestIdSchema,
  serverTimestamp: serverTimestampSchema,
  data: foxKifuAiProviderConfigurationDataSchema
}).strict();
var foxKifuAiAccountCreateBodySchema = z5.object({ account: zhiziAccountSchema, password: zhiziPasswordSchema }).strict();
var foxKifuAiAccountProfileBodySchema = z5.object({
  version: stateVersionSchema,
  compute: foxKifuAiComputeSelectionSchema,
  accountLabel: z5.string().trim().max(60).optional()
}).strict();
var foxKifuAiAccountSchema = z5.object({
  configurationId: identifierSchema,
  accountMasked: z5.string().trim().min(1).max(320),
  accountLabel: z5.string().trim().max(60).optional(),
  version: stateVersionSchema,
  updatedAt: serverTimestampSchema,
  profile: foxKifuAiPublicProfileSchema
}).strict();
var foxKifuAiAccountListResponseSchema = z5.object({
  requestId: requestIdSchema,
  serverTimestamp: serverTimestampSchema,
  data: z5.object({
    enabled: z5.boolean(),
    workerAvailable: z5.boolean(),
    accounts: z5.array(foxKifuAiAccountSchema).max(20)
  }).strict()
}).strict();
var foxKifuAiAccountResponseSchema = z5.object({
  requestId: requestIdSchema,
  serverTimestamp: serverTimestampSchema,
  data: foxKifuAiAccountSchema
}).strict();
var foxKifuAiUserAccessSchema = z5.object({
  userId: identifierSchema,
  displayName: displayNameSchema,
  accountUsername: accountUsernameSchema.nullable(),
  userStatus: userStatusSchema,
  role: membershipRoleSchema,
  membershipStatus: membershipStatusSchema,
  authorized: z5.boolean(),
  version: stateVersionSchema
}).strict();
var foxKifuAiUserAccessListResponseSchema = z5.object({
  requestId: requestIdSchema,
  serverTimestamp: serverTimestampSchema,
  data: z5.object({
    organizationId: identifierSchema,
    users: z5.array(foxKifuAiUserAccessSchema).max(100)
  }).strict()
}).strict();
var foxKifuAiUserAccessBodySchema = z5.object({
  version: stateVersionSchema,
  authorized: z5.boolean()
}).strict();
var foxKifuAiUserAccessResponseSchema = z5.object({
  requestId: requestIdSchema,
  serverTimestamp: serverTimestampSchema,
  data: foxKifuAiUserAccessSchema
}).strict();
var foxKifuAiAccountAccessListResponseSchema = z5.object({
  requestId: requestIdSchema,
  serverTimestamp: serverTimestampSchema,
  data: z5.object({
    configurationId: identifierSchema,
    organizationId: identifierSchema,
    users: z5.array(foxKifuAiUserAccessSchema).max(100)
  }).strict()
}).strict();
var foxKifuAiAccountAccessResponseSchema = z5.object({
  requestId: requestIdSchema,
  serverTimestamp: serverTimestampSchema,
  data: z5.object({
    configurationId: identifierSchema,
    access: foxKifuAiUserAccessSchema
  }).strict()
}).strict();
var foxKifuObservationTimelineBodySchema = z5.object({
  stateVersion: stateVersionSchema,
  configurationId: identifierSchema.optional(),
  retry: z5.boolean().optional()
}).strict();
var foxKifuObservationTimelineQuerySchema = z5.object({
  stateVersion: z5.preprocess(
    (value) => typeof value === "string" && /^(0|[1-9]\d*)$/u.test(value) ? Number(value) : value,
    stateVersionSchema
  ),
  configurationId: identifierSchema.optional()
}).strict();
var foxKifuObservationTimelineResponseSchema = z5.object({
  requestId: requestIdSchema,
  gameId: identifierSchema,
  stateVersion: stateVersionSchema,
  serverTimestamp: serverTimestampSchema,
  data: z5.object({
    profileKey: z5.string().regex(/^[a-f0-9]{64}$/u),
    komi: z5.number().finite(),
    handicap: z5.number().int().min(0).max(4),
    completed: z5.number().int().min(0).max(1501),
    total: z5.number().int().min(1).max(1501),
    pendingMoveNumber: z5.number().int().min(0).max(1500).nullable(),
    error: z5.string().max(200).nullable(),
    savedSnapshot: z5.object({
      configurationId: identifierSchema,
      configurationVersion: z5.number().int().positive(),
      savedAt: serverTimestampSchema,
      matchesCurrentConfiguration: z5.boolean()
    }).strict().nullable().optional(),
    points: z5.array(
      z5.object({
        moveNumber: z5.number().int().min(0).max(1500),
        blackWinRateBps: z5.number().int().min(0).max(1e4),
        blackScoreLeadHalfPoints: z5.number().int(),
        visits: z5.number().int().nonnegative(),
        analyzedAt: serverTimestampSchema
      }).strict()
    ).max(1501)
  }).strict()
}).strict().refine(
  ({ data }) => data.completed === data.points.length && data.completed <= data.total && (data.pendingMoveNumber === null || data.pendingMoveNumber < data.total) && data.points.every(
    (point, index) => point.moveNumber < data.total && (index === 0 || point.moveNumber > data.points[index - 1].moveNumber)
  ),
  { message: "Timeline progress must match its ordered measured points" }
);

// src/problem-annotations.ts
import { z as z6 } from "zod";
var problemBoardAnnotationSchema = z6.discriminatedUnion("kind", [
  z6.object({ kind: z6.literal("LABEL"), point: boardPointSchema, text: z6.string().min(1).max(256) }).strict(),
  z6.object({
    kind: z6.enum(["CIRCLE", "TRIANGLE", "SQUARE", "CROSS", "SELECT"]),
    point: boardPointSchema
  }).strict(),
  z6.object({ kind: z6.enum(["ARROW", "LINE"]), point: boardPointSchema, to: boardPointSchema }).strict()
]);
var problemBoardAnnotationsSchema = z6.array(problemBoardAnnotationSchema).max(1500);

// src/classroom-sgf.ts
var MAX_CLASSROOM_SGF_BYTES = 1024 * 1024;
var classroomSgfFileSchema = z7.object({
  filename: z7.string().min(5).max(255).regex(/\.sgf$/iu).refine((name) => !containsC0ControlCharacter(name) && !/[/\\]/u.test(name)),
  size: z7.number().int().min(1).max(MAX_CLASSROOM_SGF_BYTES)
}).strict();
var classroomSgfUploadFieldsSchema = classroomSgfFileSchema.pick({ filename: true });
var classroomSgfDataSchema = foxKifuRecordDataSchema.extend({
  filename: classroomSgfFileSchema.shape.filename,
  initialPlayer: stoneColorSchema,
  annotations: z7.array(problemBoardAnnotationSchema).max(1500)
}).superRefine((data, context) => {
  validateFoxKifuSourceBranches(data, context);
  const size = data.record.boardSize;
  data.frames.forEach((frame, index) => {
    if (frame.moveNumber !== index || frame.stones.length !== size * size || frame.point !== null && (frame.point.x >= size || frame.point.y >= size)) {
      context.addIssue({
        code: "custom",
        message: "SGF frames must match the board size and move order",
        path: ["frames", index]
      });
    }
  });
  if (data.annotations.some(
    (mark) => mark.point.x >= size || mark.point.y >= size || "to" in mark && (mark.to.x >= size || mark.to.y >= size)
  )) {
    context.addIssue({
      code: "custom",
      message: "SGF markup must be inside the board",
      path: ["annotations"]
    });
  }
});
var classroomSgfImportResponseSchema = z7.object({
  requestId: requestIdSchema,
  serverTimestamp: serverTimestampSchema,
  data: classroomSgfDataSchema
}).strict();

// src/disconnection.ts
import { z as z8 } from "zod";
var GAME_DISCONNECTION_GRACE_MS = 1e5;
var gameDisconnectionSnapshotSchema = z8.object({
  startedAt: serverTimestampSchema,
  deadlineAt: serverTimestampSchema,
  blackDisconnectedAt: serverTimestampSchema.nullable(),
  whiteDisconnectedAt: serverTimestampSchema.nullable()
}).strict().superRefine((disconnection, context) => {
  const startedAt = Date.parse(disconnection.startedAt);
  const deadlineAt = Date.parse(disconnection.deadlineAt);
  if (deadlineAt - startedAt !== GAME_DISCONNECTION_GRACE_MS) {
    context.addIssue({
      code: "custom",
      message: "A disconnection window must have an exact 100-second grace period",
      path: ["deadlineAt"]
    });
  }
  if (disconnection.blackDisconnectedAt === null && disconnection.whiteDisconnectedAt === null) {
    context.addIssue({
      code: "custom",
      message: "An active disconnection window requires at least one offline player",
      path: ["blackDisconnectedAt"]
    });
  }
  for (const [field, value] of [
    ["blackDisconnectedAt", disconnection.blackDisconnectedAt],
    ["whiteDisconnectedAt", disconnection.whiteDisconnectedAt]
  ]) {
    if (value !== null && (Date.parse(value) < startedAt || Date.parse(value) > deadlineAt)) {
      context.addIssue({
        code: "custom",
        message: "A player disconnection must fall inside the active grace window",
        path: [field]
      });
    }
  }
});

// src/http.ts
import { z as z12 } from "zod";

// src/scoring.ts
import { z as z9 } from "zod";
var CHINESE_AREA_KOMI = 7.5;
var CHINESE_AREA_KOMI_HALF_POINTS = 15;
var chineseAreaKomiHalfPointsSchema = z9.union([
  z9.literal(1),
  z9.literal(2),
  z9.literal(3),
  z9.literal(4),
  z9.literal(15)
]);
var MAX_DEAD_STONE_ANCHORS = 361;
var MAX_SCORING_HALF_POINTS = 737;
var scoringHashSchema = z9.string().regex(/^[0-9a-f]{64}$/);
var countingResolutionSourceSchema = z9.enum(["PLAYER", "TEACHER"]);
var scoreResolutionSourceSchema = z9.enum(["PLAYER_CONFIRMED", "TEACHER_ADJUDICATED"]);
function addScoringIssue(context, message, path) {
  context.addIssue({
    code: "custom",
    message,
    path
  });
}
function pointKey(point) {
  return `${point.x}:${point.y}`;
}
function isCanonicalPointList(points) {
  const seen = /* @__PURE__ */ new Set();
  let previous;
  for (const point of points) {
    const key = pointKey(point);
    const followsPrevious = previous === void 0 || point.y > previous.y || point.y === previous.y && point.x > previous.x;
    if (seen.has(key) || !followsPrevious) return false;
    seen.add(key);
    previous = point;
  }
  return true;
}
var canonicalBoardPointListSchema = z9.array(boardPointSchema).max(MAX_DEAD_STONE_ANCHORS).superRefine((points, context) => {
  points.forEach((point, index) => {
    if (point.x >= 19 || point.y >= 19) {
      addScoringIssue(context, "Authoritative scoring points cannot exceed a 19 line board", [
        index
      ]);
    }
  });
  if (!isCanonicalPointList(points)) {
    addScoringIssue(context, "Board points must be unique and sorted in row-major order", []);
  }
});
var scoringAreaBreakdownSchema = z9.object({
  livingStoneCount: z9.number().int().safe().nonnegative().max(MAX_DEAD_STONE_ANCHORS),
  territoryPointCount: z9.number().int().safe().nonnegative().max(MAX_DEAD_STONE_ANCHORS),
  areaPointCount: z9.number().int().safe().nonnegative().max(MAX_DEAD_STONE_ANCHORS),
  territory: canonicalBoardPointListSchema,
  totalHalfPoints: z9.number().int().safe().nonnegative().max(MAX_SCORING_HALF_POINTS),
  total: z9.number().finite().nonnegative().max(MAX_SCORING_HALF_POINTS / 2).multipleOf(0.5)
}).strict().superRefine((area, context) => {
  if (area.territoryPointCount !== area.territory.length) {
    addScoringIssue(
      context,
      "Territory point count must equal the authoritative territory list length",
      ["territoryPointCount"]
    );
  }
  if (area.areaPointCount !== area.livingStoneCount + area.territoryPointCount) {
    addScoringIssue(context, "Area must equal living stones plus territory points", [
      "areaPointCount"
    ]);
  }
  if (area.total !== area.totalHalfPoints / 2) {
    addScoringIssue(context, "Displayed total must be derived from integer half-points", [
      "total"
    ]);
  }
});
var countingRequestStatusSchema = z9.enum(["PENDING", "ACCEPTED", "REJECTED"]);
var countingRequestSnapshotSchema = z9.object({
  countingRequestId: identifierSchema,
  requester: stoneColorSchema,
  responder: stoneColorSchema,
  status: countingRequestStatusSchema,
  requestedAt: serverTimestampSchema,
  respondedAt: serverTimestampSchema.nullable(),
  resolutionSource: countingResolutionSourceSchema.optional(),
  resolutionInterventionId: identifierSchema.optional()
}).strict().superRefine((request, context) => {
  if (request.requester === request.responder) {
    addScoringIssue(context, "Counting requester and responder must be opposing colors", [
      "responder"
    ]);
  }
  if (request.status === "PENDING" && request.respondedAt !== null || request.status !== "PENDING" && request.respondedAt === null) {
    addScoringIssue(context, "Counting response time must match the request lifecycle", [
      "respondedAt"
    ]);
  }
  if (request.respondedAt !== null && Date.parse(request.respondedAt) < Date.parse(request.requestedAt)) {
    addScoringIssue(context, "Counting response cannot precede the request", ["respondedAt"]);
  }
  if (request.status === "PENDING" && (request.resolutionSource !== void 0 || request.resolutionInterventionId !== void 0)) {
    addScoringIssue(context, "A pending counting request cannot expose resolution provenance", [
      "resolutionSource"
    ]);
  }
  if (request.resolutionSource === "TEACHER" && request.resolutionInterventionId === void 0) {
    addScoringIssue(context, "A teacher counting resolution requires its intervention id", [
      "resolutionInterventionId"
    ]);
  }
  if (request.resolutionSource !== "TEACHER" && request.resolutionInterventionId !== void 0) {
    addScoringIssue(context, "Only a teacher counting resolution can reference an intervention", [
      "resolutionInterventionId"
    ]);
  }
});
var scoringEntryReasonSchema = z9.enum([
  "DOUBLE_PASS",
  "AGREED_REQUEST",
  "TEACHER_DECISION"
]);
var scoringSessionSnapshotSchema = z9.object({
  sessionId: identifierSchema,
  round: z9.number().int().safe().positive(),
  entryReason: scoringEntryReasonSchema,
  baseSequence: z9.number().int().safe().nonnegative(),
  basePositionRevision: z9.number().int().safe().nonnegative(),
  basePositionHash: scoringHashSchema,
  startedAt: serverTimestampSchema
}).strict().superRefine((session, context) => {
  if (session.basePositionRevision < session.baseSequence) {
    addScoringIssue(context, "Position revision cannot be lower than the base move sequence", [
      "basePositionRevision"
    ]);
  }
});
var scoringResultSchema = z9.union([
  z9.literal("0"),
  z9.string().regex(/^[BW]\+(?:0\.5|[1-9]\d*(?:\.5)?)$/)
]);
function formattedScoreResult(winner, marginHalfPoints) {
  if (winner === null) return "0";
  const margin = marginHalfPoints / 2;
  return `${winner === "BLACK" ? "B" : "W"}+${Number.isInteger(margin) ? margin : margin.toFixed(1)}`;
}
var scoringProposalSnapshotSchema = z9.object({
  scoringRecordId: identifierSchema,
  scoringVersion: z9.number().int().safe().positive(),
  scoringHash: scoringHashSchema,
  komiHalfPoints: chineseAreaKomiHalfPointsSchema.default(CHINESE_AREA_KOMI_HALF_POINTS),
  deadStones: canonicalBoardPointListSchema,
  black: scoringAreaBreakdownSchema,
  white: scoringAreaBreakdownSchema,
  neutralPoints: canonicalBoardPointListSchema,
  winner: stoneColorSchema.nullable(),
  marginHalfPoints: z9.number().int().safe().nonnegative().max(MAX_SCORING_HALF_POINTS),
  margin: z9.number().finite().nonnegative().max(MAX_SCORING_HALF_POINTS / 2).multipleOf(0.5),
  finalResult: scoringResultSchema,
  submittedBy: z9.object({
    blackAt: serverTimestampSchema.nullable(),
    whiteAt: serverTimestampSchema.nullable()
  }).strict().default({ blackAt: null, whiteAt: null }),
  confirmedBy: z9.object({
    blackAt: serverTimestampSchema.nullable(),
    whiteAt: serverTimestampSchema.nullable()
  }).strict(),
  resolutionSource: scoreResolutionSourceSchema.optional(),
  adjudication: z9.object({
    interventionId: identifierSchema,
    adjudicatedAt: serverTimestampSchema
  }).strict().optional()
}).strict().superRefine((proposal, context) => {
  if (proposal.confirmedBy.blackAt !== null && proposal.submittedBy.blackAt === null || proposal.confirmedBy.whiteAt !== null && proposal.submittedBy.whiteAt === null) {
    addScoringIssue(context, "A result confirmation requires both dead-stone submissions", [
      "confirmedBy"
    ]);
  }
  if ((proposal.confirmedBy.blackAt !== null || proposal.confirmedBy.whiteAt !== null) && (proposal.submittedBy.blackAt === null || proposal.submittedBy.whiteAt === null)) {
    addScoringIssue(
      context,
      "Result confirmation cannot start before both players submit marks",
      ["submittedBy"]
    );
  }
  const expectedBlackHalfPoints = proposal.black.areaPointCount * 2;
  const expectedWhiteHalfPoints = proposal.white.areaPointCount * 2 + proposal.komiHalfPoints;
  if (proposal.black.totalHalfPoints !== expectedBlackHalfPoints) {
    addScoringIssue(context, "Black half-points must equal twice the Chinese-area point count", [
      "black",
      "totalHalfPoints"
    ]);
  }
  if (proposal.white.totalHalfPoints !== expectedWhiteHalfPoints) {
    addScoringIssue(context, "White half-points must include the authoritative compensation", [
      "white",
      "totalHalfPoints"
    ]);
  }
  const expectedMarginHalfPoints = Math.abs(expectedBlackHalfPoints - expectedWhiteHalfPoints);
  const expectedWinner = expectedMarginHalfPoints === 0 ? null : expectedBlackHalfPoints > expectedWhiteHalfPoints ? "BLACK" : "WHITE";
  const expectedMargin = expectedMarginHalfPoints / 2;
  const expectedResult = formattedScoreResult(expectedWinner, expectedMarginHalfPoints);
  if (proposal.winner !== expectedWinner) {
    addScoringIssue(context, "Winner must be derived from authoritative integer half-points", [
      "winner"
    ]);
  }
  if (proposal.marginHalfPoints !== expectedMarginHalfPoints) {
    addScoringIssue(context, "Margin half-points must equal the Chinese-area score difference", [
      "marginHalfPoints"
    ]);
  }
  if (proposal.margin !== expectedMargin) {
    addScoringIssue(context, "Displayed margin must be derived from integer half-points", [
      "margin"
    ]);
  }
  if (proposal.finalResult !== expectedResult) {
    addScoringIssue(context, "Final result must identify the authoritative score winner", [
      "finalResult"
    ]);
  }
  const classifiedEmptyPoints = /* @__PURE__ */ new Set();
  const pointLists = [
    ["black", proposal.black.territory],
    ["white", proposal.white.territory],
    ["neutralPoints", proposal.neutralPoints]
  ];
  pointLists.forEach(([field, points]) => {
    points.forEach((point, index) => {
      const key = pointKey(point);
      if (classifiedEmptyPoints.has(key)) {
        addScoringIssue(
          context,
          "Black territory, white territory and neutral points must be disjoint",
          [field, index]
        );
      }
      classifiedEmptyPoints.add(key);
    });
  });
  if (proposal.resolutionSource === "TEACHER_ADJUDICATED") {
    if (proposal.adjudication === void 0) {
      addScoringIssue(context, "A teacher adjudication requires its intervention provenance", [
        "adjudication"
      ]);
    }
    if (proposal.confirmedBy.blackAt !== null || proposal.confirmedBy.whiteAt !== null) {
      addScoringIssue(context, "A teacher adjudication cannot fabricate player confirmations", [
        "confirmedBy"
      ]);
    }
  } else if (proposal.adjudication !== void 0) {
    addScoringIssue(context, "Only a teacher adjudication can expose adjudication provenance", [
      "adjudication"
    ]);
  }
});
var scoringSnapshotSchema = z9.object({
  status: z9.enum(["REQUESTED", "SCORING", "FINISHED"]),
  countingRequest: countingRequestSnapshotSchema.nullable(),
  session: scoringSessionSnapshotSchema.nullable(),
  proposal: scoringProposalSnapshotSchema.nullable()
}).strict().superRefine((scoring, context) => {
  if (scoring.status === "REQUESTED") {
    if (scoring.countingRequest?.status !== "PENDING" || scoring.session !== null || scoring.proposal !== null) {
      addScoringIssue(
        context,
        "A requested scoring snapshot requires only one pending counting request",
        ["status"]
      );
    }
    return;
  }
  if (scoring.session === null) {
    addScoringIssue(context, "Scoring and finished snapshots require an active session", [
      "session"
    ]);
    return;
  }
  if (scoring.session.entryReason === "DOUBLE_PASS" && scoring.countingRequest !== null) {
    addScoringIssue(
      context,
      "A double-pass scoring session cannot fabricate a counting request",
      ["countingRequest"]
    );
  }
  if (scoring.session.entryReason === "AGREED_REQUEST" && scoring.countingRequest?.status !== "ACCEPTED") {
    addScoringIssue(context, "An agreed-request session requires its accepted counting request", [
      "countingRequest"
    ]);
  }
  if (scoring.session.entryReason === "AGREED_REQUEST" && scoring.countingRequest?.resolutionSource === "TEACHER") {
    addScoringIssue(context, "An agreed request cannot be resolved by a teacher", [
      "countingRequest",
      "resolutionSource"
    ]);
  }
  if (scoring.session.entryReason === "TEACHER_DECISION" && (scoring.countingRequest?.status !== "ACCEPTED" || scoring.countingRequest.resolutionSource !== "TEACHER")) {
    addScoringIssue(
      context,
      "A teacher-decision session requires its teacher-resolved counting request",
      ["countingRequest"]
    );
  }
  if ((scoring.session.entryReason === "AGREED_REQUEST" || scoring.session.entryReason === "TEACHER_DECISION") && scoring.countingRequest?.respondedAt !== scoring.session.startedAt) {
    addScoringIssue(context, "Counting resolution and scoring session must start atomically", [
      "session",
      "startedAt"
    ]);
  }
  if (scoring.proposal !== null) {
    const sessionStartedAt = Date.parse(scoring.session.startedAt);
    const confirmations = [
      scoring.proposal.confirmedBy.blackAt,
      scoring.proposal.confirmedBy.whiteAt
    ];
    const submissions = [
      scoring.proposal.submittedBy.blackAt,
      scoring.proposal.submittedBy.whiteAt
    ];
    submissions.forEach((submittedAt, index) => {
      if (submittedAt !== null && Date.parse(submittedAt) < sessionStartedAt) {
        addScoringIssue(context, "A dead-stone submission cannot precede its session", [
          "proposal",
          "submittedBy",
          index === 0 ? "blackAt" : "whiteAt"
        ]);
      }
    });
    confirmations.forEach((confirmedAt, index) => {
      if (confirmedAt !== null && Date.parse(confirmedAt) < sessionStartedAt) {
        addScoringIssue(context, "A scoring confirmation cannot precede its session", [
          "proposal",
          "confirmedBy",
          index === 0 ? "blackAt" : "whiteAt"
        ]);
      }
    });
    if (scoring.proposal.adjudication !== void 0 && Date.parse(scoring.proposal.adjudication.adjudicatedAt) < sessionStartedAt) {
      addScoringIssue(context, "A teacher adjudication cannot precede its scoring session", [
        "proposal",
        "adjudication",
        "adjudicatedAt"
      ]);
    }
  }
  const bothConfirmed = scoring.proposal !== null && scoring.proposal.confirmedBy.blackAt !== null && scoring.proposal.confirmedBy.whiteAt !== null;
  const bothSubmitted = scoring.proposal !== null && scoring.proposal.submittedBy.blackAt !== null && scoring.proposal.submittedBy.whiteAt !== null;
  const teacherAdjudicated = scoring.proposal?.resolutionSource === "TEACHER_ADJUDICATED";
  if (scoring.status === "SCORING" && (bothConfirmed || scoring.proposal?.resolutionSource !== void 0)) {
    addScoringIssue(
      context,
      "An active proposal cannot already contain terminal resolution provenance",
      ["proposal", "resolutionSource"]
    );
  }
  if (scoring.status === "FINISHED" && (scoring.proposal === null || !teacherAdjudicated && (!bothSubmitted || !bothConfirmed))) {
    addScoringIssue(
      context,
      "A finished scoring snapshot requires player confirmations or teacher adjudication",
      ["proposal"]
    );
  }
});
var timeoutTerminalSnapshotSchema = z9.object({
  resultType: z9.literal("TIMEOUT"),
  loser: stoneColorSchema,
  winner: stoneColorSchema,
  finalResult: z9.enum(["B+T", "W+T"]),
  finishedAt: serverTimestampSchema
}).strict();
var resignationTerminalSnapshotSchema = z9.object({
  resultType: z9.literal("RESIGNATION"),
  loser: stoneColorSchema,
  winner: stoneColorSchema,
  finalResult: z9.enum(["B+R", "W+R"]),
  finishedAt: serverTimestampSchema
}).strict();
var scoreTerminalSnapshotSchema = z9.object({
  resultType: z9.literal("SCORE"),
  winner: stoneColorSchema.nullable(),
  marginHalfPoints: z9.number().int().safe().nonnegative().max(MAX_SCORING_HALF_POINTS),
  margin: z9.number().finite().nonnegative().max(MAX_SCORING_HALF_POINTS / 2).multipleOf(0.5),
  finalResult: scoringResultSchema,
  scoringRecordId: identifierSchema,
  resolutionSource: scoreResolutionSourceSchema.optional(),
  resultInterventionId: identifierSchema.optional(),
  finishedAt: serverTimestampSchema
}).strict();
var forfeitTerminalSnapshotSchema = z9.object({
  resultType: z9.literal("FORFEIT"),
  loser: stoneColorSchema,
  winner: stoneColorSchema,
  finalResult: z9.enum(["B+F", "W+F"]),
  resultInterventionId: identifierSchema,
  finishedAt: serverTimestampSchema
}).strict();
var disconnectionTerminalSnapshotSchema = z9.object({
  resultType: z9.literal("DISCONNECTION"),
  loser: stoneColorSchema.nullable(),
  winner: stoneColorSchema.nullable(),
  finalResult: z9.enum(["B+D", "W+D", "0"]),
  finishedAt: serverTimestampSchema
}).strict();
var gameTerminalSnapshotSchema = z9.discriminatedUnion("resultType", [
  scoreTerminalSnapshotSchema,
  resignationTerminalSnapshotSchema,
  timeoutTerminalSnapshotSchema,
  forfeitTerminalSnapshotSchema,
  disconnectionTerminalSnapshotSchema
]).superRefine((terminal, context) => {
  if (terminal.resultType === "SCORE") {
    if (terminal.margin !== terminal.marginHalfPoints / 2) {
      addScoringIssue(context, "Score margin must be derived from the half-point difference", [
        "margin"
      ]);
    }
    const expectedResult2 = formattedScoreResult(terminal.winner, terminal.marginHalfPoints);
    if (terminal.finalResult !== expectedResult2) {
      addScoringIssue(context, "Score result must identify the authoritative winner and margin", [
        "finalResult"
      ]);
    }
    if (terminal.resolutionSource === "TEACHER_ADJUDICATED" && terminal.resultInterventionId === void 0) {
      addScoringIssue(context, "A teacher score requires its intervention provenance", [
        "resultInterventionId"
      ]);
    }
    if (terminal.resolutionSource !== "TEACHER_ADJUDICATED" && terminal.resultInterventionId !== void 0) {
      addScoringIssue(context, "Only a teacher score can reference an intervention", [
        "resultInterventionId"
      ]);
    }
    return;
  }
  if (terminal.resultType === "DISCONNECTION") {
    const draw = terminal.winner === null && terminal.loser === null;
    const decisive = terminal.winner !== null && terminal.loser !== null && terminal.winner !== terminal.loser;
    const expected = terminal.winner === null ? "0" : terminal.winner === "BLACK" ? "B+D" : "W+D";
    if (!draw && !decisive || terminal.finalResult !== expected) {
      addScoringIssue(context, "Disconnection result must identify one loser or a draw", [
        "finalResult"
      ]);
    }
    return;
  }
  if (terminal.loser === terminal.winner) {
    addScoringIssue(context, "Terminal loser and winner must be opposing colors", ["winner"]);
  }
  const suffix = terminal.resultType === "TIMEOUT" ? "T" : terminal.resultType === "RESIGNATION" ? "R" : "F";
  const expectedResult = `${terminal.winner === "BLACK" ? "B" : "W"}+${suffix}`;
  if (terminal.finalResult !== expectedResult) {
    addScoringIssue(context, "Terminal result must identify the authoritative winner", [
      "finalResult"
    ]);
  }
});

// src/timing.ts
import { z as z11 } from "zod";

// src/supervision.ts
import { z as z10 } from "zod";
var teacherInterventionReasonCodeSchema = z10.enum([
  "INSTRUCTION",
  "TECHNICAL",
  "FAIR_PLAY",
  "COUNTING_DISPUTE",
  "OTHER"
]);
var teacherCountingResolutionSchema = z10.enum(["CANCEL_AND_RESUME", "ENTER_SCORING"]);
var supervisionPauseSnapshotSchema = z10.object({
  interventionId: identifierSchema,
  pausedByMembershipId: identifierSchema,
  pausedByDisplayName: z10.string().trim().min(1).max(64),
  reasonCode: teacherInterventionReasonCodeSchema,
  publicMessage: z10.string().trim().min(1).max(160).refine((value) => !containsC0ControlCharacter(value), {
    message: "Public pause messages cannot contain C0 control characters"
  }).optional(),
  pausedAt: serverTimestampSchema
}).strict();
var supervisionSnapshotSchema = z10.object({
  pause: supervisionPauseSnapshotSchema.nullable()
}).strict();

// src/timing.ts
var DEFAULT_MAIN_TIME_MS = 18e5;
var MIN_MAIN_TIME_MS = 6e4;
var MAX_MAIN_TIME_MS = 108e5;
var durationMsSchema = z11.number().int().safe().nonnegative();
var mainTimeMsSchema = durationMsSchema.min(MIN_MAIN_TIME_MS).max(MAX_MAIN_TIME_MS).multipleOf(1e3);
var byoyomiPeriodTimeMsSchema = z11.union([
  z11.literal(1e4),
  z11.literal(15e3),
  z11.literal(3e4),
  z11.literal(6e4)
]);
var byoyomiPeriodsSchema = z11.union([
  z11.literal(1),
  z11.literal(2),
  z11.literal(3),
  z11.literal(5),
  z11.literal(10)
]);
var byoyomiConfigSchema = z11.object({
  periodTimeMs: byoyomiPeriodTimeMsSchema,
  periods: byoyomiPeriodsSchema
}).strict();
var noTimingConfigSchema = z11.object({
  ruleCode: z11.literal("NONE_V1")
}).strict();
var absoluteTimingConfigSchema = z11.object({
  ruleCode: z11.literal("ABSOLUTE_V1"),
  mainTimeMs: mainTimeMsSchema,
  byoyomi: byoyomiConfigSchema.optional()
}).strict();
var timingConfigSchema = z11.discriminatedUnion("ruleCode", [
  noTimingConfigSchema,
  absoluteTimingConfigSchema
]);
var publicTimingConfigSchema = absoluteTimingConfigSchema;
var clockStatusSchema = z11.enum(["DISABLED", "NOT_STARTED", "RUNNING", "STOPPED"]);
var playerTimingSnapshotSchema = z11.object({
  elapsedMs: durationMsSchema,
  remainingMs: durationMsSchema.nullable(),
  byoyomiPeriodsRemaining: z11.number().int().min(0).max(10).nullable().optional(),
  byoyomiPeriodRemainingMs: durationMsSchema.max(6e4).nullable().optional()
}).strict();
function addTimingIssue(context, message, path) {
  context.addIssue({
    code: "custom",
    message,
    path
  });
}
var gameTimingSnapshotSchema = z11.object({
  ruleCode: z11.enum(["NONE_V1", "ABSOLUTE_V1"]),
  status: clockStatusSchema,
  mainTimeMs: mainTimeMsSchema.nullable(),
  byoyomi: byoyomiConfigSchema.nullable().optional(),
  activeColor: stoneColorSchema.nullable(),
  turnStartedAt: serverTimestampSchema.nullable(),
  deadlineAt: serverTimestampSchema.nullable(),
  stoppedAt: serverTimestampSchema.nullable(),
  asOf: serverTimestampSchema,
  timedDurationMs: durationMsSchema.nullable(),
  wallDurationMs: durationMsSchema.nullable(),
  black: playerTimingSnapshotSchema,
  white: playerTimingSnapshotSchema
}).strict().superRefine((timing, context) => {
  if (timing.ruleCode === "NONE_V1") {
    const disabledShapeIsValid = timing.status === "DISABLED" && timing.mainTimeMs === null && timing.activeColor === null && timing.turnStartedAt === null && timing.deadlineAt === null && timing.stoppedAt === null && timing.timedDurationMs === null && timing.wallDurationMs === null && timing.black.elapsedMs === 0 && timing.black.remainingMs === null && timing.white.elapsedMs === 0 && timing.white.remainingMs === null && (timing.byoyomi === void 0 || timing.byoyomi === null);
    if (!disabledShapeIsValid) {
      addTimingIssue(context, "NONE_V1 must expose a disabled clock without timing values", [
        "status"
      ]);
    }
    return;
  }
  if (timing.status === "DISABLED") {
    addTimingIssue(context, "ABSOLUTE_V1 cannot use a disabled clock", ["status"]);
  }
  if (timing.mainTimeMs === null || timing.timedDurationMs === null || timing.wallDurationMs === null || timing.black.remainingMs === null || timing.white.remainingMs === null) {
    addTimingIssue(context, "ABSOLUTE_V1 requires its configured time and duration projections", [
      "mainTimeMs"
    ]);
    return;
  }
  const playerTimings = [
    ["black", timing.black],
    ["white", timing.white]
  ];
  playerTimings.forEach(([color, player]) => {
    const expectedRemaining = Math.max(0, timing.mainTimeMs - player.elapsedMs);
    if (player.remainingMs !== expectedRemaining) {
      addTimingIssue(context, "Remaining time must equal main time minus elapsed time", [
        color,
        "remainingMs"
      ]);
    }
    if (timing.byoyomi === null || timing.byoyomi === void 0) {
      if (player.elapsedMs > timing.mainTimeMs) {
        addTimingIssue(context, "Elapsed time cannot exceed main time without byoyomi", [
          color,
          "elapsedMs"
        ]);
      }
      if (player.byoyomiPeriodsRemaining !== void 0 && player.byoyomiPeriodsRemaining !== null || player.byoyomiPeriodRemainingMs !== void 0 && player.byoyomiPeriodRemainingMs !== null) {
        addTimingIssue(context, "A clock without byoyomi cannot expose period state", [color]);
      }
    } else if (player.byoyomiPeriodsRemaining === null || player.byoyomiPeriodsRemaining === void 0 || player.byoyomiPeriodRemainingMs === null || player.byoyomiPeriodRemainingMs === void 0 || player.byoyomiPeriodsRemaining > timing.byoyomi.periods || player.byoyomiPeriodRemainingMs > timing.byoyomi.periodTimeMs || player.byoyomiPeriodsRemaining === 0 && player.byoyomiPeriodRemainingMs !== 0) {
      addTimingIssue(context, "Byoyomi player state must match its configured bounds", [color]);
    }
  });
  if (timing.timedDurationMs !== timing.black.elapsedMs + timing.white.elapsedMs) {
    addTimingIssue(context, "Timed duration must equal both players\u2019 elapsed time", [
      "timedDurationMs"
    ]);
  }
  if (timing.wallDurationMs < timing.timedDurationMs) {
    addTimingIssue(context, "Wall duration cannot be lower than timed duration", [
      "wallDurationMs"
    ]);
  }
  if (timing.status === "NOT_STARTED") {
    const notStartedShapeIsValid = timing.activeColor === null && timing.turnStartedAt === null && timing.deadlineAt === null && timing.stoppedAt === null && timing.timedDurationMs === 0 && timing.wallDurationMs === 0 && timing.black.elapsedMs === 0 && timing.black.remainingMs === timing.mainTimeMs && timing.white.elapsedMs === 0 && timing.white.remainingMs === timing.mainTimeMs && (timing.byoyomi === null || timing.byoyomi === void 0 || timing.black.byoyomiPeriodsRemaining === timing.byoyomi.periods && timing.white.byoyomiPeriodsRemaining === timing.byoyomi.periods);
    if (!notStartedShapeIsValid) {
      addTimingIssue(context, "A clock that has not started must expose its initial values", [
        "status"
      ]);
    }
  }
  if (timing.status === "RUNNING") {
    if (timing.activeColor === null || timing.turnStartedAt === null || timing.deadlineAt === null || timing.stoppedAt !== null) {
      addTimingIssue(context, "A running clock requires an active turn and deadline", ["status"]);
    } else if (Date.parse(timing.deadlineAt) <= Date.parse(timing.turnStartedAt)) {
      addTimingIssue(context, "The deadline must be later than the turn start", ["deadlineAt"]);
    } else if (Date.parse(timing.turnStartedAt) > Date.parse(timing.asOf)) {
      addTimingIssue(context, "The active turn cannot start after the snapshot time", [
        "turnStartedAt"
      ]);
    }
  }
  if (timing.status === "STOPPED") {
    if (timing.activeColor !== null || timing.turnStartedAt !== null || timing.deadlineAt !== null || timing.stoppedAt === null) {
      addTimingIssue(context, "A stopped clock must clear its active turn and record stoppedAt", [
        "status"
      ]);
    } else if (Date.parse(timing.stoppedAt) > Date.parse(timing.asOf)) {
      addTimingIssue(context, "The clock cannot stop after the snapshot time", ["stoppedAt"]);
    }
  }
});
var expectedAbsoluteClockStatuses = {
  CREATED: ["NOT_STARTED"],
  WAITING: ["NOT_STARTED"],
  READY: ["NOT_STARTED"],
  PLAYING: ["RUNNING"],
  COUNTING_REQUESTED: ["STOPPED"],
  SCORING: ["STOPPED"],
  FINISHED: ["STOPPED"],
  ABORTED: ["STOPPED"]
};
var timedGameSnapshotSchema = gameSnapshotSchema.safeExtend({
  timing: gameTimingSnapshotSchema,
  terminal: gameTerminalSnapshotSchema.optional(),
  scoring: scoringSnapshotSchema.optional(),
  supervision: supervisionSnapshotSchema.optional(),
  disconnection: gameDisconnectionSnapshotSchema.nullable().optional()
}).superRefine((snapshot, context) => {
  const activePause = snapshot.supervision?.pause;
  const activeDisconnection = snapshot.disconnection;
  if (activeDisconnection !== null && activeDisconnection !== void 0 && snapshot.status !== "PLAYING" && snapshot.status !== "COUNTING_REQUESTED" && snapshot.status !== "SCORING") {
    addTimingIssue(context, "A disconnection pause requires an active Game lifecycle", [
      "disconnection"
    ]);
  }
  if (activePause !== null && activePause !== void 0 && snapshot.status !== "PLAYING" && snapshot.status !== "COUNTING_REQUESTED" && snapshot.status !== "SCORING") {
    addTimingIssue(context, "A supervision pause requires an active Game lifecycle", [
      "supervision",
      "pause"
    ]);
  }
  if (activePause !== null && activePause !== void 0 && Date.parse(activePause.pausedAt) > Date.parse(snapshot.timing.asOf)) {
    addTimingIssue(context, "A supervision pause cannot begin after the snapshot time", [
      "supervision",
      "pause",
      "pausedAt"
    ]);
  }
  if (activePause !== null && activePause !== void 0 && snapshot.startedAt !== null && Date.parse(activePause.pausedAt) < Date.parse(snapshot.startedAt)) {
    addTimingIssue(context, "A supervision pause cannot precede the Game start", [
      "supervision",
      "pause",
      "pausedAt"
    ]);
  }
  if (snapshot.status === "FINISHED" && snapshot.terminal === void 0) {
    addTimingIssue(context, "A finished game requires one authoritative terminal result", [
      "terminal"
    ]);
  }
  if (snapshot.status !== "FINISHED" && snapshot.terminal !== void 0) {
    addTimingIssue(context, "Only a finished game can expose a terminal result", ["terminal"]);
  }
  if (snapshot.scoring !== void 0) {
    const expectedScoringStatus = snapshot.status === "COUNTING_REQUESTED" ? "REQUESTED" : snapshot.status === "SCORING" ? "SCORING" : snapshot.status === "FINISHED" && snapshot.terminal?.resultType === "SCORE" ? "FINISHED" : null;
    if (expectedScoringStatus === null || snapshot.scoring.status !== expectedScoringStatus) {
      addTimingIssue(
        context,
        "Scoring projection must match the Game lifecycle and result type",
        ["scoring", "status"]
      );
    }
    if (snapshot.scoring.session !== null && snapshot.scoring.session.startedAt !== snapshot.scoringStartedAt) {
      addTimingIssue(
        context,
        "Scoring session start must match the authoritative Game lifecycle timestamp",
        ["scoring", "session", "startedAt"]
      );
    }
    const proposal = snapshot.scoring.proposal;
    if (proposal !== null) {
      if (proposal.komiHalfPoints !== snapshot.room.komi * 2) {
        addTimingIssue(context, "Scoring compensation must match the authoritative room", [
          "scoring",
          "proposal",
          "komiHalfPoints"
        ]);
      }
      const boundedLists = [
        ["deadStones", proposal.deadStones],
        ["black", proposal.black.territory],
        ["white", proposal.white.territory],
        ["neutralPoints", proposal.neutralPoints]
      ];
      boundedLists.forEach(([field, points]) => {
        points.forEach((point, index) => {
          if (point.x >= snapshot.board.size || point.y >= snapshot.board.size) {
            addTimingIssue(context, "Scoring points must be inside the authoritative board", [
              "scoring",
              "proposal",
              field,
              index
            ]);
          }
        });
      });
    }
  }
  if (snapshot.terminal?.resultType === "SCORE") {
    const proposal = snapshot.scoring?.proposal;
    const proposalResolutionSource = proposal?.resolutionSource ?? "PLAYER_CONFIRMED";
    const terminalResolutionSource = snapshot.terminal.resolutionSource ?? "PLAYER_CONFIRMED";
    const confirmationTimes = proposal === null || proposal === void 0 ? [] : [proposal.confirmedBy.blackAt, proposal.confirmedBy.whiteAt].filter((value) => value !== null).map((value) => Date.parse(value));
    if (snapshot.scoring?.status !== "FINISHED" || proposal === null || proposal === void 0 || proposal.scoringRecordId !== snapshot.terminal.scoringRecordId || proposal.winner !== snapshot.terminal.winner || proposal.marginHalfPoints !== snapshot.terminal.marginHalfPoints || proposal.margin !== snapshot.terminal.margin || proposal.finalResult !== snapshot.terminal.finalResult || proposalResolutionSource !== terminalResolutionSource || (proposalResolutionSource === "PLAYER_CONFIRMED" ? confirmationTimes.length !== 2 || Math.max(...confirmationTimes) !== Date.parse(snapshot.terminal.finishedAt) : proposal.adjudication?.interventionId !== snapshot.terminal.resultInterventionId || proposal.adjudication?.adjudicatedAt !== snapshot.terminal.finishedAt)) {
      addTimingIssue(
        context,
        "A score terminal projection must match its authoritative proposal and provenance",
        ["terminal"]
      );
    }
  }
  if (snapshot.terminal !== void 0 && Date.parse(snapshot.terminal.finishedAt) > Date.parse(snapshot.timing.asOf)) {
    addTimingIssue(context, "A terminal result cannot finish after the snapshot time", [
      "terminal",
      "finishedAt"
    ]);
  }
  if (snapshot.terminal?.resultType === "TIMEOUT") {
    const loserTiming = snapshot.terminal.loser === "BLACK" ? snapshot.timing.black : snapshot.timing.white;
    if (snapshot.status !== "FINISHED" || snapshot.timing.ruleCode !== "ABSOLUTE_V1" || snapshot.timing.status !== "STOPPED" || snapshot.terminal.loser !== snapshot.board.currentPlayer || loserTiming.remainingMs !== 0 || snapshot.timing.byoyomi !== null && snapshot.timing.byoyomi !== void 0 && loserTiming.byoyomiPeriodsRemaining !== 0 || (snapshot.timing.byoyomi === null || snapshot.timing.byoyomi === void 0) && loserTiming.elapsedMs !== snapshot.timing.mainTimeMs || snapshot.timing.stoppedAt !== snapshot.terminal.finishedAt) {
      addTimingIssue(
        context,
        "A timeout terminal projection must match the exhausted authoritative clock",
        ["terminal"]
      );
    }
  }
  if (snapshot.terminal?.resultType === "FORFEIT" && snapshot.timing.ruleCode === "ABSOLUTE_V1" && snapshot.timing.stoppedAt !== snapshot.terminal.finishedAt) {
    addTimingIssue(context, "A forfeit must stop the authoritative clock atomically", [
      "terminal",
      "finishedAt"
    ]);
  }
  if (snapshot.timing.ruleCode === "NONE_V1") {
    if (snapshot.timing.status !== "DISABLED") {
      addTimingIssue(context, "NONE_V1 games must keep their clock disabled", [
        "timing",
        "status"
      ]);
    }
    return;
  }
  const expectedClockStatuses = snapshot.status === "PLAYING" && (activePause !== null && activePause !== void 0 || activeDisconnection !== null && activeDisconnection !== void 0) ? ["STOPPED"] : expectedAbsoluteClockStatuses[snapshot.status];
  if (!expectedClockStatuses.includes(snapshot.timing.status)) {
    addTimingIssue(context, "The game lifecycle and clock status do not match", [
      "timing",
      "status"
    ]);
  }
  if (snapshot.timing.status === "RUNNING" && snapshot.timing.activeColor !== snapshot.board.currentPlayer) {
    addTimingIssue(context, "The active clock must match the board current player", [
      "timing",
      "activeColor"
    ]);
  }
});
var phase6TimedGameSnapshotSchema = timedGameSnapshotSchema.safeExtend({
  supervision: supervisionSnapshotSchema
});
var timedGameMoveSnapshotSchema = gameMoveSnapshotSchema.safeExtend({
  elapsedMs: durationMsSchema.nullable(),
  retracted: z11.boolean().default(false)
});

// src/http.ts
var idempotencyKeySchema = requestIdSchema;
var MAX_TIMING_SEGMENT_MOVES = 1e3;
var GAME_SGF_MEDIA_TYPE = "application/x-go-sgf; charset=utf-8";
var apiErrorSchema = z12.object({
  code: gameErrorCodeSchema,
  message: z12.string().trim().min(1),
  requestId: requestIdSchema,
  gameId: identifierSchema.optional(),
  stateVersion: stateVersionSchema.optional(),
  syncRequired: z12.boolean().optional(),
  details: z12.unknown().optional(),
  serverTimestamp: serverTimestampSchema
}).strict().superRefine((error, context) => {
  if (error.gameId === void 0 !== (error.stateVersion === void 0)) {
    context.addIssue({
      code: "custom",
      message: "gameId and stateVersion must be provided together",
      path: error.gameId === void 0 ? ["gameId"] : ["stateVersion"]
    });
  }
  if ((error.code === "STATE_VERSION_CONFLICT" || error.code === "SCORING_VERSION_CONFLICT") && (error.gameId === void 0 || error.stateVersion === void 0 || error.syncRequired !== true)) {
    context.addIssue({
      code: "custom",
      message: "A version conflict requires game context and syncRequired=true",
      path: ["syncRequired"]
    });
  }
});
var healthResponseSchema = z12.object({
  status: z12.literal("ok"),
  service: z12.literal("wx-weiqi-backend"),
  version: z12.string().trim().min(1),
  serverTimestamp: serverTimestampSchema,
  workers: z12.object({
    authoritativeGameTimeout: z12.object({
      status: z12.enum(["disabled", "idle", "running", "degraded"]),
      enabled: z12.boolean(),
      failureStreak: z12.number().int().nonnegative(),
      lastScanAt: serverTimestampSchema.nullable()
    }).strict()
  }).strict()
}).strict();
var createRoomRequestSchema = z12.object({
  boardSize: boardSizeSchema,
  timing: publicTimingConfigSchema.optional(),
  gameType: gameTypeSchema.optional(),
  creatorColor: creatorColorPreferenceSchema.optional()
}).strict();
var createClassGameRequestSchema = z12.object({
  playerOneMembershipId: identifierSchema,
  playerTwoMembershipId: identifierSchema,
  boardSize: boardSizeSchema,
  timing: publicTimingConfigSchema.optional(),
  gameType: gameTypeSchema.optional(),
  playerOneColor: creatorColorPreferenceSchema.optional()
}).strict().refine((request) => request.playerOneMembershipId !== request.playerTwoMembershipId, {
  message: "The two class game players must be different",
  path: ["playerTwoMembershipId"]
});
var createGuidedGameRequestSchema = z12.object({
  studentMembershipId: identifierSchema,
  boardSize: boardSizeSchema,
  timing: publicTimingConfigSchema.optional(),
  gameType: gameTypeSchema.optional()
}).strict();
var assignedClassGameSchema = z12.object({
  assignmentId: identifierSchema,
  classId: identifierSchema,
  className: z12.string().trim().min(1).max(128),
  gameId: identifierSchema,
  stateVersion: stateVersionSchema,
  status: gameStatusSchema,
  boardSize: boardSizeSchema,
  roomCode: z12.string().trim().min(6).max(12),
  createdAt: serverTimestampSchema,
  players: z12.array(
    z12.object({
      membershipId: identifierSchema,
      displayName: z12.string().trim().min(1).max(64),
      color: stoneColorSchema
    }).strict()
  ).length(2)
}).strict().superRefine((game, context) => {
  if (new Set(game.players.map((player) => player.membershipId)).size !== 2 || new Set(game.players.map((player) => player.color)).size !== 2) {
    context.addIssue({
      code: "custom",
      message: "An assigned class game requires two distinct player seats",
      path: ["players"]
    });
  }
});
var assignedClassGamesResponseSchema = z12.object({
  requestId: requestIdSchema,
  serverTimestamp: serverTimestampSchema,
  data: z12.object({
    games: z12.array(assignedClassGameSchema).max(100)
  }).strict()
}).strict();
var joinRoomByCodeRequestSchema = z12.object({}).strict();
var joinRoomRequestSchema = z12.object({
  stateVersion: stateVersionSchema
}).strict();
var gameStateResponseShape = {
  requestId: requestIdSchema,
  gameId: identifierSchema,
  stateVersion: stateVersionSchema,
  serverTimestamp: serverTimestampSchema,
  data: timedGameSnapshotSchema
};
function createGameStateResponseSchema() {
  return z12.object(gameStateResponseShape).strict().superRefine((response, context) => {
    if (response.gameId !== response.data.gameId) {
      context.addIssue({
        code: "custom",
        message: "Envelope and snapshot game ids must match",
        path: ["data", "gameId"]
      });
    }
    if (response.stateVersion !== response.data.stateVersion) {
      context.addIssue({
        code: "custom",
        message: "Envelope and snapshot state versions must match",
        path: ["data", "stateVersion"]
      });
    }
  });
}
var createRoomResponseSchema = createGameStateResponseSchema();
var createClassGameResponseSchema = createGameStateResponseSchema();
var joinRoomResponseSchema = createGameStateResponseSchema();
var getGameResponseSchema = createGameStateResponseSchema();
var gameMutationResponseSchema = createGameStateResponseSchema();
var gameCountingRequestBodySchema = z12.object({
  stateVersion: stateVersionSchema
}).strict();
var gameCountingResponseBodySchema = z12.object({
  stateVersion: stateVersionSchema,
  accepted: z12.boolean()
}).strict();
var gameScoringProposalBodySchema = z12.object({
  stateVersion: stateVersionSchema,
  deadStoneAnchors: z12.array(boardPointSchema).max(MAX_DEAD_STONE_ANCHORS)
}).strict();
var gameScoringConfirmationBodySchema = z12.object({
  stateVersion: stateVersionSchema,
  scoringVersion: z12.number().int().safe().positive(),
  scoringHash: scoringHashSchema
}).strict();
var gameScoringResumeBodySchema = z12.object({
  stateVersion: stateVersionSchema,
  scoringVersion: z12.number().int().safe().positive(),
  scoringHash: scoringHashSchema,
  disputedGroupAnchor: boardPointSchema
}).strict();
var gameResignationBodySchema = z12.object({
  stateVersion: stateVersionSchema
}).strict();
var gameSgfChecksumSchema = scoringHashSchema;
var gameTimingMovesQuerySchema = z12.object({
  afterSequence: z12.number().int().nonnegative().default(0),
  limit: z12.number().int().min(1).max(100).default(100)
}).strict();
var gameTimingMoveSchema = z12.object({
  sequence: z12.number().int().positive(),
  kind: moveKindSchema,
  color: stoneColorSchema,
  elapsedMs: durationMsSchema.nullable(),
  accumulatedMs: durationMsSchema.nullable(),
  moveReceivedAt: serverTimestampSchema,
  retracted: z12.boolean()
}).strict().superRefine((move, context) => {
  if (move.elapsedMs === null !== (move.accumulatedMs === null)) {
    context.addIssue({
      code: "custom",
      message: "Elapsed and accumulated time must be available together",
      path: ["accumulatedMs"]
    });
  }
  if (move.elapsedMs !== null && move.accumulatedMs !== null && move.accumulatedMs < move.elapsedMs) {
    context.addIssue({
      code: "custom",
      message: "Accumulated time cannot be lower than the current move time",
      path: ["accumulatedMs"]
    });
  }
});
var gameTimingMovesDataSchema = z12.object({
  moves: z12.array(gameTimingMoveSchema).max(100),
  nextAfterSequence: z12.number().int().positive().nullable()
}).strict().superRefine((data, context) => {
  data.moves.forEach((move, index) => {
    if (index > 0 && move.sequence <= data.moves[index - 1].sequence) {
      context.addIssue({
        code: "custom",
        message: "Timing moves must be strictly ordered by sequence",
        path: ["moves", index, "sequence"]
      });
    }
  });
  const lastMove = data.moves.at(-1);
  if (lastMove === void 0 && data.nextAfterSequence !== null || lastMove !== void 0 && data.nextAfterSequence !== null && data.nextAfterSequence !== lastMove.sequence) {
    context.addIssue({
      code: "custom",
      message: "The next cursor must identify the last returned move",
      path: ["nextAfterSequence"]
    });
  }
});
var timingResponseEnvelopeShape = {
  requestId: requestIdSchema,
  gameId: identifierSchema,
  stateVersion: stateVersionSchema,
  serverTimestamp: serverTimestampSchema
};
var gameTimingMovesResponseSchema = z12.object({
  ...timingResponseEnvelopeShape,
  data: gameTimingMovesDataSchema
}).strict();
var gameTimingSegmentsQuerySchema = z12.object({
  fromSequence: z12.number().int().positive().default(1),
  toSequence: z12.number().int().positive().optional()
}).strict().superRefine((query, context) => {
  if (query.toSequence !== void 0 && query.toSequence < query.fromSequence) {
    context.addIssue({
      code: "custom",
      message: "toSequence cannot be lower than fromSequence",
      path: ["toSequence"]
    });
  }
  if (query.toSequence !== void 0 && query.toSequence - query.fromSequence + 1 > MAX_TIMING_SEGMENT_MOVES) {
    context.addIssue({
      code: "custom",
      message: `A timing segment cannot exceed ${MAX_TIMING_SEGMENT_MOVES} moves`,
      path: ["toSequence"]
    });
  }
});
var gameTimingSideStatisticsSchema = z12.object({
  moveCount: z12.number().int().nonnegative(),
  totalMs: durationMsSchema,
  averageMs: z12.number().finite().nonnegative(),
  minMs: durationMsSchema.nullable(),
  maxMs: durationMsSchema.nullable()
}).strict().superRefine((statistics, context) => {
  if (statistics.moveCount === 0) {
    if (statistics.totalMs !== 0 || statistics.averageMs !== 0 || statistics.minMs !== null || statistics.maxMs !== null) {
      context.addIssue({
        code: "custom",
        message: "Empty timing statistics must use stable zero values",
        path: ["moveCount"]
      });
    }
    return;
  }
  if (statistics.minMs === null || statistics.maxMs === null) {
    if (statistics.totalMs !== 0 || statistics.averageMs !== 0) {
      context.addIssue({
        code: "custom",
        message: "Unavailable legacy timing statistics must use stable zero values",
        path: ["minMs"]
      });
    }
    return;
  }
  if (statistics.minMs > statistics.maxMs) {
    context.addIssue({
      code: "custom",
      message: "Minimum move time cannot exceed maximum move time",
      path: ["minMs"]
    });
  }
  const expectedAverage = statistics.totalMs / statistics.moveCount;
  if (Math.abs(statistics.averageMs - expectedAverage) > Number.EPSILON) {
    context.addIssue({
      code: "custom",
      message: "Average move time must equal total time divided by move count",
      path: ["averageMs"]
    });
  }
  if (statistics.averageMs < statistics.minMs || statistics.averageMs > statistics.maxMs) {
    context.addIssue({
      code: "custom",
      message: "Average move time must fall between the minimum and maximum",
      path: ["averageMs"]
    });
  }
});
var gameTimingSegmentsDataSchema = z12.object({
  fromSequence: z12.number().int().positive().nullable(),
  toSequence: z12.number().int().positive().nullable(),
  timedDurationMs: durationMsSchema,
  wallDurationMs: durationMsSchema,
  black: gameTimingSideStatisticsSchema,
  white: gameTimingSideStatisticsSchema
}).strict().superRefine((data, context) => {
  if (data.fromSequence === null !== (data.toSequence === null)) {
    context.addIssue({
      code: "custom",
      message: "Actual segment bounds must be available together",
      path: ["toSequence"]
    });
    return;
  }
  if (data.fromSequence === null || data.toSequence === null) {
    const isStableEmptyResult = data.timedDurationMs === 0 && data.wallDurationMs === 0 && data.black.moveCount === 0 && data.white.moveCount === 0;
    if (!isStableEmptyResult) {
      context.addIssue({
        code: "custom",
        message: "An empty segment must expose stable zero statistics",
        path: ["fromSequence"]
      });
    }
    return;
  }
  if (data.toSequence < data.fromSequence) {
    context.addIssue({
      code: "custom",
      message: "Actual segment bounds cannot be reversed",
      path: ["toSequence"]
    });
  }
  const expectedMoveCount = data.toSequence - data.fromSequence + 1;
  if (data.black.moveCount + data.white.moveCount !== expectedMoveCount) {
    context.addIssue({
      code: "custom",
      message: "Side move counts must cover the complete actual segment",
      path: ["black", "moveCount"]
    });
  }
  if (data.timedDurationMs !== data.black.totalMs + data.white.totalMs) {
    context.addIssue({
      code: "custom",
      message: "Timed duration must equal both sides\u2019 total time",
      path: ["timedDurationMs"]
    });
  }
  if (data.wallDurationMs < data.timedDurationMs) {
    context.addIssue({
      code: "custom",
      message: "Wall duration cannot be lower than timed duration",
      path: ["wallDurationMs"]
    });
  }
});
var gameTimingSegmentsResponseSchema = z12.object({
  ...timingResponseEnvelopeShape,
  data: gameTimingSegmentsDataSchema
}).strict();
var gameTimingReportMoveMarkerSchema = z12.object({
  sequence: z12.number().int().positive(),
  kind: moveKindSchema,
  color: stoneColorSchema,
  elapsedMs: durationMsSchema,
  moveReceivedAt: serverTimestampSchema,
  retracted: z12.boolean()
}).strict();
var gameTimingReportSideSchema = z12.object({
  moveCount: z12.number().int().nonnegative(),
  moveTimeTotalMs: durationMsSchema,
  clockTotalMs: durationMsSchema,
  unattributedMs: durationMsSchema,
  averageMoveMs: z12.number().finite().nonnegative(),
  minMoveMs: durationMsSchema.nullable(),
  maxMoveMs: durationMsSchema.nullable(),
  shortestMove: gameTimingReportMoveMarkerSchema.nullable(),
  longestMove: gameTimingReportMoveMarkerSchema.nullable()
}).strict().superRefine((side, context) => {
  if (side.clockTotalMs !== side.moveTimeTotalMs + side.unattributedMs) {
    context.addIssue({
      code: "custom",
      message: "Clock total must equal move time plus unattributed terminal time",
      path: ["clockTotalMs"]
    });
  }
  if (side.moveCount === 0) {
    if (side.moveTimeTotalMs !== 0 || side.averageMoveMs !== 0 || side.minMoveMs !== null || side.maxMoveMs !== null || side.shortestMove !== null || side.longestMove !== null) {
      context.addIssue({
        code: "custom",
        message: "A side without moves must expose stable empty move statistics",
        path: ["moveCount"]
      });
    }
    return;
  }
  if (side.minMoveMs === null || side.maxMoveMs === null || side.shortestMove === null || side.longestMove === null || side.shortestMove.elapsedMs !== side.minMoveMs || side.longestMove.elapsedMs !== side.maxMoveMs || side.averageMoveMs !== side.moveTimeTotalMs / side.moveCount) {
    context.addIssue({
      code: "custom",
      message: "Move extrema and average must match the authoritative move statistics",
      path: ["averageMoveMs"]
    });
  }
});
var gameTimingReportSegmentSchema = z12.object({
  fromSequence: z12.number().int().positive(),
  toSequence: z12.number().int().positive(),
  moveCount: z12.number().int().positive().max(30),
  moveTimeTotalMs: durationMsSchema,
  blackMoveCount: z12.number().int().nonnegative(),
  whiteMoveCount: z12.number().int().nonnegative()
}).strict().superRefine((segment, context) => {
  if (segment.toSequence - segment.fromSequence + 1 !== segment.moveCount || segment.blackMoveCount + segment.whiteMoveCount !== segment.moveCount) {
    context.addIssue({
      code: "custom",
      message: "A report segment must cover one contiguous move interval",
      path: ["moveCount"]
    });
  }
});
var gameTimingReportResponseSchema = z12.object({
  ...timingResponseEnvelopeShape,
  data: z12.object({
    status: gameStatusSchema.refine((status) => status === "FINISHED" || status === "ABORTED"),
    timingRuleCode: z12.literal("ABSOLUTE_V1"),
    generatedAt: serverTimestampSchema,
    startedAt: serverTimestampSchema,
    finishedAt: serverTimestampSchema,
    moveCount: z12.number().int().nonnegative(),
    timedDurationMs: durationMsSchema,
    wallDurationMs: durationMsSchema,
    pauseDurationMs: durationMsSchema,
    black: gameTimingReportSideSchema,
    white: gameTimingReportSideSchema,
    segments: z12.array(gameTimingReportSegmentSchema),
    movesPath: z12.string().regex(/^\/games\/[0-9A-HJKMNP-TV-Z]{26}\/timing\/moves$/)
  }).strict()
}).strict().superRefine((report, context) => {
  const data = report.data;
  if (data.generatedAt !== report.serverTimestamp || data.moveCount !== data.black.moveCount + data.white.moveCount || data.timedDurationMs !== data.black.clockTotalMs + data.white.clockTotalMs || data.wallDurationMs !== data.timedDurationMs + data.pauseDurationMs || data.segments.reduce((total, segment) => total + segment.moveCount, 0) !== data.moveCount) {
    context.addIssue({
      code: "custom",
      message: "Timing report totals must match its authoritative components",
      path: ["data"]
    });
  }
});

// src/homework.ts
import { z as z14 } from "zod";

// src/question-requirements.ts
import { z as z13 } from "zod";
var QUESTION_REQUIREMENTS_MAX_LENGTH = 4e3;
var QUESTION_REQUIREMENTS_MAX_RUNS = 1e3;
var safeCharacters = (text2) => !containsC0ControlCharacter(text2.replace(/[\r\n\t]/gu, ""));
var questionRequirementsSchema = z13.string().trim().max(QUESTION_REQUIREMENTS_MAX_LENGTH).refine(safeCharacters, "Requirements cannot contain control characters");
var questionRequirementsRichTextSchema = z13.array(
  z13.object({
    text: z13.string().min(1).max(QUESTION_REQUIREMENTS_MAX_LENGTH).refine(safeCharacters),
    bold: z13.boolean().optional(),
    color: z13.enum(["BLACK", "RED"]).optional()
  }).strict()
).max(QUESTION_REQUIREMENTS_MAX_RUNS).refine(
  (runs) => runs.reduce((length, run) => length + run.text.length, 0) <= QUESTION_REQUIREMENTS_MAX_LENGTH,
  "Requirements cannot exceed 4000 characters"
);
function normalizeQuestionRequirements(runs) {
  const text2 = runs.map((run) => run.text).join("");
  const start = text2.length - text2.trimStart().length;
  const end = text2.trimEnd().length;
  const normalized = [];
  let offset = 0;
  for (const run of runs) {
    const value = run.text.slice(
      Math.max(0, start - offset),
      Math.max(0, Math.min(run.text.length, end - offset))
    );
    offset += run.text.length;
    if (!value) continue;
    const next = { text: value, bold: run.bold === true, color: run.color ?? "BLACK" };
    const previous = normalized.at(-1);
    if (previous && previous.bold === next.bold && previous.color === next.color)
      previous.text += value;
    else normalized.push({ ...next });
  }
  return { requirements: text2.trim(), requirementsRichText: normalized.length ? normalized : null };
}

// src/homework.ts
var PROBLEM_SCHEMA_VERSION = "GO_PROBLEM_V1";
var SOLUTION_TREE_SCHEMA_VERSION = "GO_SOLUTION_TREE_V1";
var MAX_SOLUTION_LINES = 32;
var MAX_SOLUTION_LINE_MOVES = 128;
var MAX_HOMEWORK_ITEMS = 100;
var MAX_HOMEWORK_ATTEMPTS = 10;
var MAX_ASSIGNMENT_PAGE_SIZE = 50;
var MAX_HOMEWORK_TRACE_NODES = 512;
var MAX_HOMEWORK_TRACE_NODE_MOVES = 4;
var MAX_HOMEWORK_TRACE_STEPS = MAX_HOMEWORK_TRACE_NODES * MAX_HOMEWORK_TRACE_NODE_MOVES;
var MAX_HOMEWORK_ANSWER_ELAPSED_MS = 3e5;
var safeText = (minimum, maximum) => z14.string().trim().min(minimum).max(maximum).refine((value) => !containsC0ControlCharacter(value), {
  message: "Text cannot contain C0 control characters"
});
var problemTitleSchema = safeText(1, 160);
var problemDescriptionSchema = safeText(1, 4e3);
var homeworkTitleSchema = safeText(1, 160);
var homeworkDescriptionSchema = safeText(1, 4e3);
var homeworkFeedbackSchema = safeText(1, 2e3);
var problemMoveIntentSchema = z14.discriminatedUnion("kind", [
  z14.object({
    kind: z14.literal("PLAY"),
    point: boardPointSchema
  }).strict(),
  z14.object({
    kind: z14.literal("PASS")
  }).strict()
]);
var homeworkAnswerTraceNodeSchema = z14.object({
  parentNodeIndex: z14.number().int().safe().nonnegative().nullable(),
  moves: z14.array(problemMoveIntentSchema).min(1).max(MAX_HOMEWORK_TRACE_NODE_MOVES)
}).strict();
var homeworkAnswerTraceSchema = z14.object({
  nodes: z14.array(homeworkAnswerTraceNodeSchema).max(MAX_HOMEWORK_TRACE_NODES),
  submittedNodeIndex: z14.number().int().safe().nonnegative().nullable()
}).strict().superRefine((trace, context) => {
  if (trace.submittedNodeIndex !== null && trace.submittedNodeIndex >= trace.nodes.length) {
    context.addIssue({
      code: "custom",
      message: "The submitted trace node must exist",
      path: ["submittedNodeIndex"]
    });
  }
  trace.nodes.forEach((node, nodeIndex) => {
    if (node.parentNodeIndex !== null && node.parentNodeIndex >= nodeIndex) {
      context.addIssue({
        code: "custom",
        message: "A trace node parent must precede its child",
        path: ["nodes", nodeIndex, "parentNodeIndex"]
      });
    }
  });
});
var homeworkAnswerSessionBodySchema = z14.object({
  problemId: identifierSchema,
  draft: z14.object({
    expectedVersion: stateVersionSchema,
    answer: z14.array(problemMoveIntentSchema).max(MAX_SOLUTION_LINE_MOVES),
    answerTrace: homeworkAnswerTraceSchema
  }).strict().optional()
}).strict();
var homeworkAnswerSessionResponseSchema = z14.object({
  requestId: requestIdSchema,
  serverTimestamp: serverTimestampSchema,
  data: z14.object({
    assignmentId: identifierSchema,
    problemId: identifierSchema,
    version: stateVersionSchema,
    startedAt: serverTimestampSchema,
    deadlineAt: serverTimestampSchema,
    answerTrace: homeworkAnswerTraceSchema,
    frames: z14.array(
      z14.object({
        answer: z14.array(problemMoveIntentSchema),
        board: boardSnapshotSchema,
        solutionComplete: z14.boolean(),
        traceNodeIndex: z14.number().int().nonnegative()
      }).strict()
    )
  }).strict()
}).strict();
var solutionLineOutcomeSchema = z14.enum(["CORRECT", "NEEDS_REVIEW"]);
var solutionLineSchema = z14.object({
  moves: z14.array(problemMoveIntentSchema).min(1).max(MAX_SOLUTION_LINE_MOVES),
  outcome: solutionLineOutcomeSchema
}).strict();
var problemSolutionTreeSchema = z14.object({
  schemaVersion: z14.literal(SOLUTION_TREE_SCHEMA_VERSION),
  lines: z14.array(solutionLineSchema).min(1).max(MAX_SOLUTION_LINES)
}).strict().superRefine((tree, context) => {
  if (!tree.lines.some((line) => line.outcome === "CORRECT")) {
    context.addIssue({
      code: "custom",
      message: "A solution tree requires at least one correct line",
      path: ["lines"]
    });
  }
  const encoded = tree.lines.map((line) => JSON.stringify(line.moves));
  if (new Set(encoded).size !== encoded.length) {
    context.addIssue({
      code: "custom",
      message: "Solution lines must be unique",
      path: ["lines"]
    });
  }
});
var problemSnapshotShape = {
  problemId: identifierSchema,
  organizationId: identifierSchema,
  problemVersion: stateVersionSchema,
  schemaVersion: z14.literal(PROBLEM_SCHEMA_VERSION),
  sourceGameId: identifierSchema,
  sourceGameStateVersion: stateVersionSchema,
  sourcePositionRevision: z14.number().int().safe().nonnegative(),
  sourcePositionHash: z14.string().regex(/^[0-9a-f]{64}$/),
  sourceMoveSequence: z14.number().int().safe().nonnegative().nullable(),
  title: problemTitleSchema,
  description: problemDescriptionSchema.nullable(),
  requirements: questionRequirementsSchema.nullable().optional(),
  requirementsRichText: questionRequirementsRichTextSchema.nullable().optional(),
  initialPosition: boardSnapshotSchema,
  initialAnnotations: problemBoardAnnotationsSchema.optional(),
  solutionTree: problemSolutionTreeSchema,
  createdAt: serverTimestampSchema
};
var problemSnapshotSchema = z14.object(problemSnapshotShape).strict().superRefine((problem, context) => {
  if (problem.initialPosition.positionHash !== problem.sourcePositionHash || problem.problemVersion < 1) {
    context.addIssue({
      code: "custom",
      message: "Problem source and version facts are inconsistent",
      path: ["sourcePositionHash"]
    });
  }
});
var {
  organizationId: _organizationIdSchema,
  solutionTree: _solutionTreeSchema,
  ...studentProblemSnapshotShape
} = problemSnapshotShape;
var studentProblemSnapshotSchema = z14.object(studentProblemSnapshotShape).strict().superRefine((problem, context) => {
  if (problem.initialPosition.positionHash !== problem.sourcePositionHash || problem.problemVersion < 1) {
    context.addIssue({
      code: "custom",
      message: "Problem source and version facts are inconsistent",
      path: ["sourcePositionHash"]
    });
  }
});
var createProblemBodySchema = z14.object({
  sourceGameStateVersion: stateVersionSchema,
  sourcePositionRevision: z14.number().int().safe().nonnegative(),
  title: problemTitleSchema,
  description: problemDescriptionSchema.nullable().optional(),
  solutionTree: problemSolutionTreeSchema
}).strict();
var problemResponseSchema = z14.object({
  requestId: requestIdSchema,
  gameId: identifierSchema,
  stateVersion: stateVersionSchema,
  serverTimestamp: serverTimestampSchema,
  data: z14.object({ problem: problemSnapshotSchema }).strict()
}).strict().superRefine((response, context) => {
  const problem = response.data.problem;
  if (response.gameId !== problem.sourceGameId || response.stateVersion !== problem.sourceGameStateVersion) {
    context.addIssue({
      code: "custom",
      message: "Problem response trace must match its frozen source Game",
      path: ["stateVersion"]
    });
  }
});
var homeworkStatusSchema = z14.enum(["DRAFT", "PUBLISHED", "CLOSED", "ARCHIVED"]);
var homeworkAnswerModeSchema = z14.enum(["MANUAL", "AUTOMATIC"]);
var homeworkTimeLimitSecondsSchema = z14.union([
  z14.literal(30),
  z14.literal(45),
  z14.literal(60),
  z14.literal(120),
  z14.literal(180),
  z14.literal(300)
]);
var assignmentStatusSchema = z14.enum([
  "ASSIGNED",
  "IN_PROGRESS",
  "SUBMITTED",
  "REVIEWED",
  "EXPIRED"
]);
var submissionStatusSchema = z14.enum(["SUBMITTED", "CORRECT", "INCORRECT", "NEEDS_REVIEW"]);
var homeworkItemSchema = z14.object({
  itemId: identifierSchema,
  problemId: identifierSchema,
  sortOrder: z14.number().int().safe().nonnegative(),
  title: problemTitleSchema
}).strict();
var homeworkSnapshotSchema = z14.object({
  homeworkId: identifierSchema,
  organizationId: identifierSchema,
  classId: identifierSchema,
  homeworkVersion: stateVersionSchema,
  classScopeVersion: stateVersionSchema,
  title: homeworkTitleSchema,
  description: homeworkDescriptionSchema.nullable(),
  status: homeworkStatusSchema,
  dueAt: serverTimestampSchema.nullable(),
  maxAttempts: z14.number().int().min(1).max(MAX_HOMEWORK_ATTEMPTS),
  answerMode: homeworkAnswerModeSchema,
  timeLimitSeconds: homeworkTimeLimitSecondsSchema,
  items: z14.array(homeworkItemSchema).min(1).max(MAX_HOMEWORK_ITEMS),
  publishedAt: serverTimestampSchema.nullable(),
  closedAt: serverTimestampSchema.nullable(),
  createdAt: serverTimestampSchema
}).strict().superRefine((homework, context) => {
  if (homework.homeworkVersion < 1 || homework.classScopeVersion < 1) {
    context.addIssue({
      code: "custom",
      message: "Homework versions must be positive",
      path: ["homeworkVersion"]
    });
  }
  if (homework.status === "DRAFT" !== (homework.publishedAt === null)) {
    context.addIssue({
      code: "custom",
      message: "Only draft Homework can have no publication timestamp",
      path: ["publishedAt"]
    });
  }
  if ((homework.status === "CLOSED" || homework.status === "ARCHIVED") !== (homework.closedAt !== null)) {
    context.addIssue({
      code: "custom",
      message: "Closed and archived Homework require a closure timestamp",
      path: ["closedAt"]
    });
  }
  const problemIds = homework.items.map((item) => item.problemId);
  const orders = homework.items.map((item) => item.sortOrder);
  if (new Set(problemIds).size !== problemIds.length || new Set(orders).size !== orders.length || [...orders].sort((left, right) => left - right).some((order, index) => order !== index)) {
    context.addIssue({
      code: "custom",
      message: "Homework items require unique problems and contiguous ordering",
      path: ["items"]
    });
  }
});
var createHomeworkBodySchema = z14.object({
  title: homeworkTitleSchema,
  description: homeworkDescriptionSchema.nullable().optional(),
  dueAt: serverTimestampSchema.nullable().optional(),
  maxAttempts: z14.number().int().min(1).max(MAX_HOMEWORK_ATTEMPTS).default(3),
  answerMode: homeworkAnswerModeSchema.default("AUTOMATIC"),
  timeLimitSeconds: homeworkTimeLimitSecondsSchema.default(60),
  problemIds: z14.array(identifierSchema).min(1).max(MAX_HOMEWORK_ITEMS).optional(),
  questionBankProblemIds: z14.array(identifierSchema).min(1).max(MAX_HOMEWORK_ITEMS).optional()
}).strict().superRefine((body, context) => {
  const sources = [body.problemIds, body.questionBankProblemIds].filter(
    (value) => value !== void 0
  );
  if (sources.length !== 1) {
    context.addIssue({
      code: "custom",
      message: "Homework requires exactly one problem source list",
      path: ["problemIds"]
    });
  }
  const ids = sources[0] ?? [];
  if (new Set(ids).size !== ids.length) {
    context.addIssue({
      code: "custom",
      message: "Homework problem ids must be unique",
      path: ["problemIds"]
    });
  }
  if (body.answerMode === "MANUAL" && body.maxAttempts !== 1) {
    context.addIssue({
      code: "custom",
      message: "Manual Homework requires exactly one attempt",
      path: ["maxAttempts"]
    });
  }
});
var updateHomeworkBodySchema = z14.object({
  expectedHomeworkVersion: stateVersionSchema,
  title: homeworkTitleSchema,
  description: homeworkDescriptionSchema.nullable().optional(),
  dueAt: serverTimestampSchema.nullable().optional(),
  maxAttempts: z14.number().int().min(1).max(MAX_HOMEWORK_ATTEMPTS),
  answerMode: homeworkAnswerModeSchema.default("AUTOMATIC"),
  timeLimitSeconds: homeworkTimeLimitSecondsSchema.default(60),
  problemIds: z14.array(identifierSchema).min(1).max(MAX_HOMEWORK_ITEMS)
}).strict().superRefine((body, context) => {
  if (new Set(body.problemIds).size !== body.problemIds.length) {
    context.addIssue({
      code: "custom",
      message: "Homework problem ids must be unique",
      path: ["problemIds"]
    });
  }
  if (body.answerMode === "MANUAL" && body.maxAttempts !== 1) {
    context.addIssue({
      code: "custom",
      message: "Manual Homework requires exactly one attempt",
      path: ["maxAttempts"]
    });
  }
});
var homeworkVersionBodySchema = z14.object({
  expectedHomeworkVersion: stateVersionSchema,
  studentMembershipIds: z14.array(identifierSchema).min(1).max(500).optional()
}).strict().refine(
  (body) => body.studentMembershipIds === void 0 || new Set(body.studentMembershipIds).size === body.studentMembershipIds.length,
  { message: "Student membership ids must be unique", path: ["studentMembershipIds"] }
);
var supplementAssignmentBodySchema = z14.object({
  expectedHomeworkVersion: stateVersionSchema,
  studentMembershipId: identifierSchema
}).strict();
var homeworkResponseSchema = z14.object({
  requestId: requestIdSchema,
  serverTimestamp: serverTimestampSchema,
  data: z14.object({ homework: homeworkSnapshotSchema }).strict()
}).strict();
var homeworkReviewProgressDataSchema = z14.object({
  homeworkId: identifierSchema,
  totalAssignments: z14.number().int().safe().nonnegative(),
  reviewedAssignments: z14.number().int().safe().nonnegative(),
  fullyGraded: z14.boolean()
}).strict().superRefine((progress, context) => {
  if (progress.reviewedAssignments > progress.totalAssignments) {
    context.addIssue({
      code: "custom",
      message: "Reviewed Assignment count cannot exceed total Assignment count",
      path: ["reviewedAssignments"]
    });
  }
  const expectedFullyGraded = progress.totalAssignments > 0 && progress.reviewedAssignments === progress.totalAssignments;
  if (progress.fullyGraded !== expectedFullyGraded) {
    context.addIssue({
      code: "custom",
      message: "Homework fully graded state must match Assignment counts",
      path: ["fullyGraded"]
    });
  }
});
var homeworkReviewProgressResponseSchema = z14.object({
  requestId: requestIdSchema,
  serverTimestamp: serverTimestampSchema,
  data: homeworkReviewProgressDataSchema
}).strict();
var homeworksQuerySchema = z14.object({
  after: identifierSchema.optional(),
  limit: z14.number().int().min(1).max(MAX_ASSIGNMENT_PAGE_SIZE).default(20)
}).strict();
var homeworksResponseSchema = z14.object({
  requestId: requestIdSchema,
  serverTimestamp: serverTimestampSchema,
  data: z14.object({
    homeworks: z14.array(homeworkSnapshotSchema).max(MAX_ASSIGNMENT_PAGE_SIZE),
    nextAfter: identifierSchema.nullable()
  }).strict()
}).strict();
var studentHomeworkItemSchema = z14.object({
  itemId: identifierSchema,
  sortOrder: z14.number().int().safe().nonnegative(),
  problem: studentProblemSnapshotSchema
}).strict();
var submissionSnapshotSchema = z14.object({
  submissionId: identifierSchema,
  submissionVersion: stateVersionSchema,
  assignmentId: identifierSchema,
  problemId: identifierSchema,
  attempt: z14.number().int().safe().positive(),
  answer: z14.array(problemMoveIntentSchema).max(MAX_SOLUTION_LINE_MOVES),
  elapsedMs: z14.number().int().safe().nonnegative().max(MAX_HOMEWORK_ANSWER_ELAPSED_MS).nullable().optional(),
  status: submissionStatusSchema,
  score: z14.number().min(0).max(100).nullable(),
  feedback: homeworkFeedbackSchema.nullable(),
  reviewedByMembershipId: identifierSchema.nullable(),
  submittedAt: serverTimestampSchema,
  reviewedAt: serverTimestampSchema.nullable()
}).strict();
var homeworkAssignmentSnapshotSchema = z14.object({
  assignmentId: identifierSchema,
  homeworkId: identifierSchema,
  homeworkVersion: stateVersionSchema,
  classId: identifierSchema,
  studentMembershipId: identifierSchema,
  status: assignmentStatusSchema,
  title: homeworkTitleSchema,
  description: homeworkDescriptionSchema.nullable(),
  dueAt: serverTimestampSchema.nullable(),
  maxAttempts: z14.number().int().min(1).max(MAX_HOMEWORK_ATTEMPTS),
  answerMode: homeworkAnswerModeSchema,
  timeLimitSeconds: homeworkTimeLimitSecondsSchema,
  items: z14.array(studentHomeworkItemSchema).min(1).max(MAX_HOMEWORK_ITEMS),
  submissions: z14.array(submissionSnapshotSchema),
  assignedAt: serverTimestampSchema,
  submittedAt: serverTimestampSchema.nullable(),
  reviewedAt: serverTimestampSchema.nullable()
}).strict();
var homeworkAssignmentResponseSchema = z14.object({
  requestId: requestIdSchema,
  serverTimestamp: serverTimestampSchema,
  data: z14.object({ assignment: homeworkAssignmentSnapshotSchema }).strict()
}).strict();
var assignmentsQuerySchema = z14.object({
  after: identifierSchema.optional(),
  limit: z14.number().int().min(1).max(MAX_ASSIGNMENT_PAGE_SIZE).default(20)
}).strict();
var assignmentsResponseSchema = z14.object({
  requestId: requestIdSchema,
  serverTimestamp: serverTimestampSchema,
  data: z14.object({
    assignments: z14.array(homeworkAssignmentSnapshotSchema).max(MAX_ASSIGNMENT_PAGE_SIZE),
    nextAfter: identifierSchema.nullable()
  }).strict()
}).strict();
var submitHomeworkAnswerBodySchema = z14.object({
  problemId: identifierSchema,
  answer: z14.array(problemMoveIntentSchema).max(MAX_SOLUTION_LINE_MOVES),
  answerTrace: homeworkAnswerTraceSchema.optional(),
  elapsedMs: z14.number().int().safe().nonnegative().max(MAX_HOMEWORK_ANSWER_ELAPSED_MS).optional(),
  submissionReason: z14.literal("TIMEOUT").optional()
}).strict().superRefine((body, context) => {
  if (body.answer.length === 0 && body.submissionReason !== "TIMEOUT") {
    context.addIssue({
      code: "custom",
      message: "Only a timeout may submit an empty answer",
      path: ["answer"]
    });
  }
  if (body.submissionReason === "TIMEOUT" && body.elapsedMs === void 0) {
    context.addIssue({
      code: "custom",
      message: "Timeout elapsed time is required",
      path: ["elapsedMs"]
    });
  }
});
var submissionResponseSchema = z14.object({
  requestId: requestIdSchema,
  serverTimestamp: serverTimestampSchema,
  data: z14.object({ submission: submissionSnapshotSchema }).strict()
}).strict();
var homeworkSubmissionReplayFrameSchema = z14.object({
  moveNumber: z14.number().int().safe().nonnegative(),
  move: problemMoveIntentSchema.nullable(),
  board: boardSnapshotSchema
}).strict();
var homeworkSubmissionTraceNodeSchema = homeworkAnswerTraceNodeSchema.extend({
  nodeIndex: z14.number().int().safe().nonnegative(),
  branchIndex: z14.number().int().safe().positive()
});
var homeworkSubmissionTraceStepSchema = z14.object({
  stepIndex: z14.number().int().safe().nonnegative(),
  parentStepIndex: z14.number().int().safe().nonnegative().nullable(),
  branchIndex: z14.number().int().safe().positive(),
  moveNumber: z14.number().int().safe().positive().max(MAX_SOLUTION_LINE_MOVES),
  move: problemMoveIntentSchema,
  board: boardSnapshotSchema
}).strict();
var homeworkSubmissionReplayResponseSchema = z14.object({
  requestId: requestIdSchema,
  serverTimestamp: serverTimestampSchema,
  data: z14.object({
    submissionId: identifierSchema,
    problemId: identifierSchema,
    frames: z14.array(homeworkSubmissionReplayFrameSchema).min(1).max(MAX_SOLUTION_LINE_MOVES + 1),
    trace: z14.object({
      nodes: z14.array(homeworkSubmissionTraceNodeSchema).max(MAX_HOMEWORK_TRACE_NODES),
      submittedNodeIndex: z14.number().int().safe().nonnegative().nullable(),
      steps: z14.array(homeworkSubmissionTraceStepSchema).max(MAX_HOMEWORK_TRACE_STEPS),
      submittedStepIndex: z14.number().int().safe().nonnegative().nullable()
    }).strict()
  }).strict()
}).strict().superRefine((response, context) => {
  response.data.frames.forEach((frame, index) => {
    if (frame.moveNumber !== index || (index === 0 ? frame.move !== null : frame.move === null)) {
      context.addIssue({
        code: "custom",
        message: "Homework Submission replay frames must be contiguous",
        path: ["data", "frames", index]
      });
    }
  });
  const trace = response.data.trace;
  if (trace.submittedNodeIndex !== null && trace.submittedNodeIndex >= trace.nodes.length) {
    context.addIssue({
      code: "custom",
      message: "The submitted trace node must exist",
      path: ["data", "trace", "submittedNodeIndex"]
    });
  }
  if (trace.submittedStepIndex !== null && trace.submittedStepIndex >= trace.steps.length) {
    context.addIssue({
      code: "custom",
      message: "The submitted trace step must exist",
      path: ["data", "trace", "submittedStepIndex"]
    });
  }
  trace.nodes.forEach((node, index) => {
    if (node.nodeIndex !== index || node.parentNodeIndex !== null && node.parentNodeIndex >= index) {
      context.addIssue({
        code: "custom",
        message: "Homework Submission trace nodes must form an append-only tree",
        path: ["data", "trace", "nodes", index]
      });
    }
  });
  trace.steps.forEach((step, index) => {
    const parent = step.parentStepIndex === null ? void 0 : trace.steps[step.parentStepIndex];
    if (step.stepIndex !== index || step.parentStepIndex !== null && step.parentStepIndex >= index || step.moveNumber !== (parent?.moveNumber ?? 0) + 1) {
      context.addIssue({
        code: "custom",
        message: "Homework Submission trace steps must form a contiguous move tree",
        path: ["data", "trace", "steps", index]
      });
    }
  });
});
var reviewHomeworkSubmissionBodySchema = z14.object({
  expectedSubmissionVersion: stateVersionSchema,
  status: z14.enum(["CORRECT", "INCORRECT"]),
  score: z14.number().min(0).max(100),
  feedback: homeworkFeedbackSchema.nullable().optional()
}).strict();
var previewHomeworkAnswerBodySchema = z14.object({
  problemId: identifierSchema,
  answer: z14.array(problemMoveIntentSchema).max(MAX_SOLUTION_LINE_MOVES)
}).strict();
var homeworkAnswerEvaluationSchema = z14.enum(["CONTINUE", "CORRECT", "INCORRECT"]);
var homeworkAnswerPreviewResponseSchema = z14.object({
  requestId: requestIdSchema,
  serverTimestamp: serverTimestampSchema,
  data: z14.object({
    problemId: identifierSchema,
    answer: z14.array(problemMoveIntentSchema).max(MAX_SOLUTION_LINE_MOVES),
    board: boardSnapshotSchema,
    solutionComplete: z14.boolean(),
    evaluation: homeworkAnswerEvaluationSchema
  }).strict()
}).strict();

// src/matchmaking.ts
import { z as z15 } from "zod";
var MATCHMAKING_TICKET_TTL_SECONDS = 300;
var MATCHMAKING_PAGE_LIMIT = 100;
var matchmakingStatusSchema = z15.enum(["QUEUED", "MATCHED", "CANCELLED", "EXPIRED"]);
var matchmakingTicketSnapshotSchema = z15.object({
  ticketId: identifierSchema,
  ticketVersion: stateVersionSchema,
  organizationId: identifierSchema,
  membershipId: identifierSchema,
  status: matchmakingStatusSchema,
  boardSize: boardSizeSchema,
  ruleset: rulesetSchema,
  timing: publicTimingConfigSchema,
  matchedRoomId: identifierSchema.nullable(),
  matchedGameId: identifierSchema.nullable(),
  expiresAt: serverTimestampSchema,
  matchedAt: serverTimestampSchema.nullable(),
  cancelledAt: serverTimestampSchema.nullable(),
  expiredAt: serverTimestampSchema.nullable(),
  createdAt: serverTimestampSchema,
  updatedAt: serverTimestampSchema
}).strict().superRefine((ticket, context) => {
  const matched = ticket.status === "MATCHED";
  if (ticket.ticketVersion < 1 || matched !== (ticket.matchedRoomId !== null && ticket.matchedGameId !== null && ticket.matchedAt !== null) || ticket.status === "CANCELLED" !== (ticket.cancelledAt !== null) || ticket.status === "EXPIRED" !== (ticket.expiredAt !== null) || ticket.status === "QUEUED" && (ticket.matchedRoomId !== null || ticket.matchedGameId !== null || ticket.matchedAt !== null || ticket.cancelledAt !== null || ticket.expiredAt !== null)) {
    context.addIssue({
      code: "custom",
      message: "Matchmaking lifecycle facts are inconsistent",
      path: ["status"]
    });
  }
});
var createMatchmakingTicketBodySchema = z15.object({
  boardSize: boardSizeSchema,
  timing: publicTimingConfigSchema
}).strict();
var cancelMatchmakingTicketBodySchema = z15.object({ expectedTicketVersion: stateVersionSchema }).strict();
var matchmakingTicketResponseSchema = z15.object({
  requestId: requestIdSchema,
  serverTimestamp: serverTimestampSchema,
  data: z15.object({ ticket: matchmakingTicketSnapshotSchema.nullable() }).strict()
}).strict();

// src/question-bank.ts
import { z as z16 } from "zod";
var questionBankSectionSchema = z16.enum([
  "LIFE_AND_DEATH",
  "CAPTURING_RACE",
  "TESUJI",
  "CAPTURE",
  "OPENING",
  "MIDDLE_GAME",
  "ENDGAME",
  "JOSEKI"
]);
var QUESTION_BANK_SECTIONS = Object.freeze([
  "LIFE_AND_DEATH",
  "CAPTURING_RACE",
  "TESUJI",
  "CAPTURE",
  "OPENING",
  "MIDDLE_GAME",
  "ENDGAME",
  "JOSEKI"
]);
var questionBankNameSchema = z16.string().trim().min(1).max(64).refine((value) => !containsC0ControlCharacter(value), {
  message: "Question bank names cannot contain control characters"
});
var createQuestionBankCategoryBodySchema = z16.object({ section: questionBankSectionSchema, name: questionBankNameSchema }).strict();
var updateQuestionBankNameBodySchema = z16.object({ expectedVersion: stateVersionSchema.min(1), name: questionBankNameSchema }).strict();
var updateQuestionBankProblemBodySchema = z16.object({
  expectedVersion: stateVersionSchema.min(1),
  requirements: questionRequirementsSchema,
  requirementsRichText: questionRequirementsRichTextSchema.nullable().optional()
}).strict().refine(
  (body) => body.requirementsRichText == null || body.requirementsRichText.map((run) => run.text).join("") === body.requirements,
  { message: "Formatted requirements must match the plain text", path: ["requirementsRichText"] }
);
var deleteQuestionBankResourceBodySchema = z16.object({ expectedVersion: stateVersionSchema.min(1) }).strict();
var sortQuestionBankProblemsBodySchema = z16.object({ expectedVersion: stateVersionSchema.min(1) }).strict();
var createQuestionBankKnowledgePointBodySchema = z16.object({ name: questionBankNameSchema }).strict();
var questionBankKnowledgePointSchema = z16.object({
  knowledgePointId: identifierSchema,
  categoryId: identifierSchema,
  version: stateVersionSchema.min(1),
  name: questionBankNameSchema,
  sortOrder: z16.number().int().nonnegative(),
  problemCount: z16.number().int().nonnegative()
}).strict();
var questionBankCategorySchema = z16.object({
  categoryId: identifierSchema,
  section: questionBankSectionSchema,
  version: stateVersionSchema.min(1),
  name: questionBankNameSchema,
  sortOrder: z16.number().int().nonnegative(),
  knowledgePoints: z16.array(questionBankKnowledgePointSchema).max(500)
}).strict();
var questionBankCatalogResponseSchema = z16.object({
  requestId: requestIdSchema,
  serverTimestamp: serverTimestampSchema,
  data: z16.object({ categories: z16.array(questionBankCategorySchema).max(500) }).strict()
}).strict();
var questionBankProblemSchema = z16.object({
  problemId: identifierSchema,
  knowledgePointId: identifierSchema,
  version: stateVersionSchema.min(1),
  filename: z16.string().trim().min(1).max(255),
  requirements: questionRequirementsSchema.nullable().optional(),
  requirementsRichText: questionRequirementsRichTextSchema.nullable().optional(),
  boardSize: boardSizeSchema,
  currentPlayer: stoneColorSchema,
  stones: z16.array(stoneColorSchema.nullable()).max(361),
  initialAnnotations: problemBoardAnnotationsSchema.optional(),
  importedAt: serverTimestampSchema
}).strict().superRefine((problem, context) => {
  if (problem.stones.length !== problem.boardSize * problem.boardSize) {
    context.addIssue({
      code: "custom",
      message: "Question preview stones must match the board size",
      path: ["stones"]
    });
  }
});
var questionBankProblemsResponseSchema = z16.object({
  requestId: requestIdSchema,
  serverTimestamp: serverTimestampSchema,
  data: z16.object({
    knowledgePointId: identifierSchema,
    problems: z16.array(questionBankProblemSchema).max(100),
    total: z16.number().int().nonnegative(),
    page: z16.number().int().min(1),
    pageSize: z16.number().int().min(1).max(100),
    pageCount: z16.number().int().min(1)
  }).strict()
}).strict();
var questionBankMutationResponseSchema = z16.object({
  requestId: requestIdSchema,
  serverTimestamp: serverTimestampSchema,
  data: z16.object({
    resourceType: z16.enum(["CATEGORY", "KNOWLEDGE_POINT", "PROBLEM_IMPORT", "PROBLEM"]),
    resourceId: identifierSchema,
    stateVersion: stateVersionSchema,
    importedCount: z16.number().int().nonnegative().optional()
  }).strict()
}).strict();
var questionBankProblemsQuerySchema = z16.object({
  knowledgePointId: identifierSchema,
  page: z16.number().int().min(1).max(1e4).default(1),
  pageSize: z16.number().int().min(1).max(100).default(12)
}).strict();

// src/takeback.ts
import { z as z17 } from "zod";
var takebackStatusSchema = z17.enum(["PENDING", "ACCEPTED", "REJECTED"]);
var gameTakebackSnapshotSchema = z17.object({
  takebackId: identifierSchema,
  takebackVersion: stateVersionSchema,
  gameId: identifierSchema,
  status: takebackStatusSchema,
  requesterMembershipId: identifierSchema,
  responderMembershipId: identifierSchema,
  targetMove: z17.object({
    moveId: identifierSchema,
    sequence: z17.number().int().safe().positive(),
    stateVersion: stateVersionSchema,
    positionRevision: stateVersionSchema,
    kind: moveKindSchema,
    color: stoneColorSchema,
    point: boardPointSchema.nullable()
  }).strict(),
  requestedStateVersion: stateVersionSchema,
  resolvedStateVersion: stateVersionSchema.nullable(),
  acceptedPositionRevision: stateVersionSchema.nullable(),
  requestedAt: serverTimestampSchema,
  resolvedAt: serverTimestampSchema.nullable()
}).strict().superRefine((takeback, context) => {
  const pending = takeback.status === "PENDING";
  if (takeback.takebackVersion < 1 || pending !== (takeback.resolvedStateVersion === null && takeback.acceptedPositionRevision === null && takeback.resolvedAt === null) || takeback.status === "ACCEPTED" !== (takeback.acceptedPositionRevision !== null) || takeback.requesterMembershipId === takeback.responderMembershipId) {
    context.addIssue({
      code: "custom",
      message: "Takeback lifecycle facts are inconsistent",
      path: ["status"]
    });
  }
});
var requestTakebackBodySchema = z17.object({ expectedGameStateVersion: stateVersionSchema }).strict();
var respondTakebackBodySchema = z17.object({
  expectedGameStateVersion: stateVersionSchema,
  expectedTakebackVersion: stateVersionSchema,
  accepted: z17.boolean()
}).strict();
var gameTakebackResponseSchema = z17.object({
  requestId: requestIdSchema,
  gameId: identifierSchema,
  stateVersion: stateVersionSchema,
  serverTimestamp: serverTimestampSchema,
  data: z17.object({
    takeback: gameTakebackSnapshotSchema.nullable(),
    snapshot: timedGameSnapshotSchema
  }).strict()
}).strict().superRefine((response, context) => {
  if (response.gameId !== response.data.snapshot.gameId || response.stateVersion !== response.data.snapshot.stateVersion || response.data.takeback !== null && response.data.takeback.gameId !== response.gameId) {
    context.addIssue({
      code: "custom",
      message: "Takeback response trace must match the authoritative Game snapshot",
      path: ["stateVersion"]
    });
  }
});

// src/review.ts
import { z as z18 } from "zod";
var MAX_GAME_HISTORY_PAGE_SIZE = 50;
var MAX_REVIEW_COMMENTS_PAGE_SIZE = 100;
var safeText2 = (minimum, maximum) => z18.string().trim().min(minimum).max(maximum).refine((value) => !containsC0ControlCharacter(value), {
  message: "Text cannot contain C0 control characters"
});
var historyCursorSchema = z18.string().trim().min(1).max(512);
var reviewTitleSchema = safeText2(1, 160);
var reviewSummarySchema = safeText2(1, 4e3);
var reviewCommentContentSchema = safeText2(1, 2e3);
var reviewStatusSchema = z18.enum(["DRAFT", "PUBLISHED", "ARCHIVED"]);
var playerGameHistoryQuerySchema = z18.object({
  after: historyCursorSchema.optional(),
  limit: z18.number().int().min(1).max(MAX_GAME_HISTORY_PAGE_SIZE).default(20)
}).strict();
var gameHistoryPlayerSchema = z18.object({
  membershipId: identifierSchema,
  displayName: safeText2(1, 64),
  color: stoneColorSchema
}).strict();
var gameHistoryItemSchema = z18.object({
  gameId: identifierSchema,
  stateVersion: stateVersionSchema,
  status: z18.enum(["FINISHED", "ABORTED"]),
  boardSize: boardSizeSchema,
  currentSequence: z18.number().int().safe().nonnegative(),
  players: z18.array(gameHistoryPlayerSchema).length(2),
  winner: stoneColorSchema.nullable(),
  finalResult: safeText2(1, 32).nullable(),
  startedAt: serverTimestampSchema.nullable(),
  finishedAt: serverTimestampSchema
}).strict().superRefine((game, context) => {
  if (new Set(game.players.map((player) => player.membershipId)).size !== 2 || new Set(game.players.map((player) => player.color)).size !== 2) {
    context.addIssue({
      code: "custom",
      message: "A historical game must expose two distinct seats",
      path: ["players"]
    });
  }
  if (game.status === "FINISHED" && game.finalResult === null) {
    context.addIssue({
      code: "custom",
      message: "A finished historical game requires its terminal result",
      path: ["finalResult"]
    });
  }
});
var playerGameHistoryResponseSchema = z18.object({
  requestId: requestIdSchema,
  serverTimestamp: serverTimestampSchema,
  data: z18.object({
    games: z18.array(gameHistoryItemSchema).max(MAX_GAME_HISTORY_PAGE_SIZE),
    nextCursor: historyCursorSchema.nullable()
  }).strict()
}).strict();
var gameReviewPlayerClockSchema = z18.object({
  remainingMs: durationMsSchema,
  byoyomiPeriodsRemaining: z18.number().int().min(0).max(10).nullable()
}).strict();
var gameReviewClockSchema = z18.object({ black: gameReviewPlayerClockSchema, white: gameReviewPlayerClockSchema }).strict();
var gameReviewFrameSchema = z18.object({
  moveNumber: z18.number().int().safe().nonnegative().max(1e4),
  kind: z18.enum(["OPENING", "PLAY", "PASS"]),
  color: stoneColorSchema.nullable(),
  point: boardPointSchema.nullable(),
  stones: z18.array(stoneColorSchema.nullable()).max(361),
  elapsedMs: durationMsSchema.nullable(),
  // Server projection from all timing events, including retracted moves and takebacks.
  // Missing or null means the historical remaining time cannot be established.
  clock: gameReviewClockSchema.nullable().optional()
}).strict().superRefine((frame, context) => {
  const opening = frame.kind === "OPENING";
  const play = frame.kind === "PLAY";
  if (opening !== (frame.moveNumber === 0 && frame.color === null && frame.point === null) || !opening && frame.color === null || play !== (frame.point !== null) || opening && frame.elapsedMs !== null) {
    context.addIssue({
      code: "custom",
      message: "Game review frame fields must match the frame kind",
      path: ["kind"]
    });
  }
});
var gameReviewRecordSchema = z18.object({
  gameId: identifierSchema,
  status: z18.enum(["FINISHED", "ABORTED"]),
  boardSize: boardSizeSchema,
  // Older replay responses may omit these, but callers must never infer default AI rules.
  ruleset: rulesetSchema.optional(),
  komi: roomSnapshotSchema.shape.komi.optional(),
  handicap: roomSnapshotSchema.shape.handicap.optional(),
  currentPlayer: stoneColorSchema.optional(),
  blackName: safeText2(1, 64),
  whiteName: safeText2(1, 64),
  resultText: safeText2(1, 32).nullable(),
  startedAt: serverTimestampSchema.nullable(),
  finishedAt: serverTimestampSchema,
  timingRuleCode: z18.enum(["NONE_V1", "ABSOLUTE_V1"]),
  mainTimeMs: mainTimeMsSchema.nullable().optional(),
  byoyomi: byoyomiConfigSchema.nullable().optional()
}).strict();
var gameReviewResponseSchema = z18.object({
  requestId: requestIdSchema,
  gameId: identifierSchema,
  stateVersion: stateVersionSchema,
  serverTimestamp: serverTimestampSchema,
  data: z18.object({
    record: gameReviewRecordSchema,
    frames: z18.array(gameReviewFrameSchema).min(1).max(10001)
  }).strict()
}).strict().superRefine((response, context) => {
  const { record, frames } = response.data;
  const expectedStones = record.boardSize ** 2;
  if (response.gameId !== record.gameId) {
    context.addIssue({
      code: "custom",
      message: "Game review identifiers must match",
      path: ["data", "record", "gameId"]
    });
  }
  frames.forEach((frame, index) => {
    if (frame.moveNumber !== index || frame.stones.length !== expectedStones || (index === 0 || record.timingRuleCode === "NONE_V1" ? frame.elapsedMs !== null : frame.elapsedMs === null)) {
      context.addIssue({
        code: "custom",
        message: "Game review frames must be contiguous and match the authoritative game",
        path: ["data", "frames", index]
      });
    }
  });
});
var recoverableGameStatusSchema = z18.enum([
  "CREATED",
  "WAITING",
  "READY",
  "PLAYING",
  "COUNTING_REQUESTED",
  "SCORING"
]);
var playerRecoverableGameResponseSchema = z18.object({
  requestId: requestIdSchema,
  serverTimestamp: serverTimestampSchema,
  data: z18.object({
    game: z18.object({
      gameId: identifierSchema,
      stateVersion: stateVersionSchema,
      status: recoverableGameStatusSchema,
      updatedAt: serverTimestampSchema
    }).strict().nullable()
  }).strict()
}).strict();
var reviewAuthorSchema = z18.object({
  membershipId: identifierSchema,
  displayName: safeText2(1, 64)
}).strict();
var reviewContentRevisionSchema = z18.object({
  revisionId: identifierSchema,
  reviewVersion: stateVersionSchema,
  title: reviewTitleSchema,
  summary: reviewSummarySchema.nullable(),
  author: reviewAuthorSchema,
  createdAt: serverTimestampSchema
}).strict();
var reviewCommentSchema = z18.object({
  commentId: identifierSchema,
  reviewId: identifierSchema,
  reviewVersion: stateVersionSchema,
  positionRevision: z18.number().int().safe().nonnegative(),
  moveSequence: z18.number().int().safe().nonnegative().nullable(),
  content: reviewCommentContentSchema,
  author: reviewAuthorSchema,
  createdAt: serverTimestampSchema
}).strict();
var reviewSnapshotSchema = z18.object({
  reviewId: identifierSchema,
  organizationId: identifierSchema,
  gameId: identifierSchema,
  sourceGameStateVersion: stateVersionSchema,
  sourcePositionRevision: z18.number().int().safe().nonnegative(),
  status: reviewStatusSchema,
  reviewVersion: stateVersionSchema,
  latestRevision: reviewContentRevisionSchema,
  publishedAt: serverTimestampSchema.nullable(),
  createdAt: serverTimestampSchema
}).strict().superRefine((review, context) => {
  if (review.latestRevision.reviewVersion > review.reviewVersion) {
    context.addIssue({
      code: "custom",
      message: "The latest content revision cannot exceed the review version",
      path: ["latestRevision", "reviewVersion"]
    });
  }
  if (review.status === "DRAFT" !== (review.publishedAt === null)) {
    context.addIssue({
      code: "custom",
      message: "Only a draft review can have no publication timestamp",
      path: ["publishedAt"]
    });
  }
});
var reviewResponseShape = {
  requestId: requestIdSchema,
  gameId: identifierSchema,
  stateVersion: stateVersionSchema,
  serverTimestamp: serverTimestampSchema
};
var reviewResponseSchema = z18.object({
  ...reviewResponseShape,
  data: z18.object({
    review: reviewSnapshotSchema
  }).strict()
}).strict().superRefine((response, context) => {
  if (response.gameId !== response.data.review.gameId || response.stateVersion !== response.data.review.sourceGameStateVersion) {
    context.addIssue({
      code: "custom",
      message: "Review response trace must match its frozen source Game",
      path: ["stateVersion"]
    });
  }
});
var gameReviewsResponseSchema = z18.object({
  ...reviewResponseShape,
  data: z18.object({
    reviews: z18.array(reviewSnapshotSchema).max(50)
  }).strict()
}).strict().superRefine((response, context) => {
  if (response.data.reviews.some(
    (review) => review.gameId !== response.gameId || review.sourceGameStateVersion !== response.stateVersion
  )) {
    context.addIssue({
      code: "custom",
      message: "Every Review must match the response Game source",
      path: ["data", "reviews"]
    });
  }
});
var reviewCommentsQuerySchema = z18.object({
  after: identifierSchema.optional(),
  limit: z18.number().int().min(1).max(MAX_REVIEW_COMMENTS_PAGE_SIZE).default(50)
}).strict();
var reviewCommentsResponseSchema = z18.object({
  ...reviewResponseShape,
  reviewId: identifierSchema,
  reviewVersion: stateVersionSchema,
  data: z18.object({
    comments: z18.array(reviewCommentSchema).max(MAX_REVIEW_COMMENTS_PAGE_SIZE),
    nextAfter: identifierSchema.nullable()
  }).strict()
}).strict();
var createReviewBodySchema = z18.object({
  sourceGameStateVersion: stateVersionSchema,
  title: reviewTitleSchema,
  summary: reviewSummarySchema.nullable().optional()
}).strict();
var reviseReviewBodySchema = z18.object({
  expectedReviewVersion: stateVersionSchema,
  title: reviewTitleSchema,
  summary: reviewSummarySchema.nullable().optional()
}).strict();
var addReviewCommentBodySchema = z18.object({
  expectedReviewVersion: stateVersionSchema,
  positionRevision: z18.number().int().safe().nonnegative(),
  content: reviewCommentContentSchema
}).strict();
var reviewVersionBodySchema = z18.object({
  expectedReviewVersion: stateVersionSchema
}).strict();
var playerCurrentGamesQuerySchema = z18.object({
  after: historyCursorSchema.optional(),
  limit: z18.number().int().min(1).max(50).default(20)
}).strict();
var playerCurrentGameSchema = z18.object({
  gameId: identifierSchema,
  stateVersion: stateVersionSchema,
  status: recoverableGameStatusSchema,
  boardSize: boardSizeSchema,
  roomCode: roomCodeSchema,
  players: z18.array(gameHistoryPlayerSchema).min(1).max(2),
  createdAt: serverTimestampSchema,
  updatedAt: serverTimestampSchema
}).strict();
var playerCurrentGamesResponseSchema = z18.object({
  requestId: requestIdSchema,
  serverTimestamp: serverTimestampSchema,
  data: z18.object({
    games: z18.array(playerCurrentGameSchema).max(50),
    nextCursor: historyCursorSchema.nullable()
  }).strict()
}).strict();
var gamePlayersPresenceResponseSchema = z18.object({
  requestId: requestIdSchema,
  gameId: identifierSchema,
  stateVersion: stateVersionSchema,
  serverTimestamp: serverTimestampSchema,
  data: z18.object({ onlineMembershipIds: z18.array(identifierSchema).max(2) }).strict()
}).strict();

// src/teacher.ts
import { z as z20 } from "zod";

// src/websocket.ts
import { z as z19 } from "zod";
var GAME_WEBSOCKET_PROTOCOL = "wx-weiqi.v1";
var WEBSOCKET_JWT_PROTOCOL_PREFIX = "wx-weiqi.jwt.";
var emptyPayloadSchema = z19.object({}).strict();
var clientEnvelopeShape = {
  requestId: requestIdSchema,
  gameId: identifierSchema,
  stateVersion: stateVersionSchema
};
var roomJoinCommandSchema = z19.object({
  event: z19.literal("room.join"),
  ...clientEnvelopeShape,
  payload: emptyPayloadSchema
}).strict();
var roomReadyCommandSchema = z19.object({
  event: z19.literal("room.ready"),
  ...clientEnvelopeShape,
  payload: emptyPayloadSchema
}).strict();
var gameNigiriSelectCommandSchema = z19.object({
  event: z19.literal("game.nigiri.select"),
  ...clientEnvelopeShape,
  payload: z19.object({ number: z19.number().int().min(1).max(16) }).strict()
}).strict();
var gameNigiriGuessCommandSchema = z19.object({
  event: z19.literal("game.nigiri.guess"),
  ...clientEnvelopeShape,
  payload: z19.object({ parity: nigiriParitySchema }).strict()
}).strict();
var gameMoveCommandSchema = z19.object({
  event: z19.literal("game.move"),
  ...clientEnvelopeShape,
  payload: boardPointSchema
}).strict();
var gamePassCommandSchema = z19.object({
  event: z19.literal("game.pass"),
  ...clientEnvelopeShape,
  payload: emptyPayloadSchema
}).strict();
var gameSyncRequestCommandSchema = z19.object({
  event: z19.literal("game.sync.request"),
  ...clientEnvelopeShape,
  payload: emptyPayloadSchema
}).strict();
var gameResignCommandSchema = z19.object({
  event: z19.literal("game.resign"),
  ...clientEnvelopeShape,
  payload: emptyPayloadSchema
}).strict();
var gameCountingRequestCommandSchema = z19.object({
  event: z19.literal("game.counting.request"),
  ...clientEnvelopeShape,
  payload: emptyPayloadSchema
}).strict();
var gameCountingRespondCommandSchema = z19.object({
  event: z19.literal("game.counting.respond"),
  ...clientEnvelopeShape,
  payload: z19.object({
    countingRequestId: identifierSchema,
    accepted: z19.boolean()
  }).strict()
}).strict();
var gameScoringProposeCommandSchema = z19.object({
  event: z19.literal("game.scoring.propose"),
  ...clientEnvelopeShape,
  payload: z19.object({
    deadStoneAnchors: z19.array(boardPointSchema).max(MAX_DEAD_STONE_ANCHORS)
  }).strict()
}).strict();
var gameScoringConfirmCommandSchema = z19.object({
  event: z19.literal("game.scoring.confirm"),
  ...clientEnvelopeShape,
  payload: z19.object({
    scoringVersion: z19.number().int().safe().positive(),
    scoringHash: scoringHashSchema
  }).strict()
}).strict();
var gameScoringRejectCommandSchema = z19.object({
  event: z19.literal("game.scoring.reject"),
  ...clientEnvelopeShape,
  payload: z19.object({
    scoringVersion: z19.number().int().safe().positive(),
    scoringHash: scoringHashSchema
  }).strict()
}).strict();
var gameScoringResumeCommandSchema = z19.object({
  event: z19.literal("game.scoring.resume"),
  ...clientEnvelopeShape,
  payload: z19.object({
    scoringVersion: z19.number().int().safe().positive(),
    scoringHash: scoringHashSchema,
    disputedGroupAnchor: boardPointSchema
  }).strict()
}).strict();
var clientCommandEnvelopeSchema = z19.discriminatedUnion("event", [
  roomJoinCommandSchema,
  roomReadyCommandSchema,
  gameNigiriSelectCommandSchema,
  gameNigiriGuessCommandSchema,
  gameMoveCommandSchema,
  gamePassCommandSchema,
  gameSyncRequestCommandSchema,
  gameResignCommandSchema,
  gameCountingRequestCommandSchema,
  gameCountingRespondCommandSchema,
  gameScoringProposeCommandSchema,
  gameScoringConfirmCommandSchema,
  gameScoringRejectCommandSchema,
  gameScoringResumeCommandSchema
]);
var serverEnvelopeShape = {
  eventId: eventIdSchema,
  requestId: requestIdSchema,
  gameId: identifierSchema,
  stateVersion: stateVersionSchema,
  serverTimestamp: serverTimestampSchema
};
var snapshotPayloadSchema = z19.object({
  snapshot: timedGameSnapshotSchema
}).strict();
var moveAcceptedPayloadSchema = z19.object({
  move: timedGameMoveSnapshotSchema,
  snapshot: timedGameSnapshotSchema
}).strict().superRefine((payload, context) => {
  const elapsedMatchesRule = payload.snapshot.timing.ruleCode === "NONE_V1" ? payload.move.elapsedMs === null : payload.move.elapsedMs !== null;
  if (!elapsedMatchesRule) {
    context.addIssue({
      code: "custom",
      message: "Move elapsed time must match the authoritative timing rule",
      path: ["move", "elapsedMs"]
    });
  }
});
var roomJoinedEventSchema = z19.object({
  event: z19.literal("room.joined"),
  ...serverEnvelopeShape,
  payload: snapshotPayloadSchema
}).strict();
var roomUpdatedEventSchema = z19.object({
  event: z19.literal("room.updated"),
  ...serverEnvelopeShape,
  payload: snapshotPayloadSchema
}).strict();
var gameStartEventSchema = z19.object({
  event: z19.literal("game.start"),
  ...serverEnvelopeShape,
  payload: snapshotPayloadSchema
}).strict();
var gameNigiriSelectedEventSchema = z19.object({
  event: z19.literal("game.nigiri.selected"),
  ...serverEnvelopeShape,
  payload: snapshotPayloadSchema
}).strict();
var gameNigiriResolvedEventSchema = z19.object({
  event: z19.literal("game.nigiri.resolved"),
  ...serverEnvelopeShape,
  payload: snapshotPayloadSchema
}).strict();
var gameMoveAcceptedEventSchema = z19.object({
  event: z19.literal("game.move.accepted"),
  ...serverEnvelopeShape,
  payload: moveAcceptedPayloadSchema
}).strict();
var phase5GameScoringStartPayloadSchema = z19.object({
  entryReason: scoringEntryReasonSchema,
  move: timedGameMoveSnapshotSchema.optional(),
  countingRequestId: identifierSchema.optional(),
  snapshot: timedGameSnapshotSchema
}).strict().superRefine((payload, context) => {
  if (payload.snapshot.status !== "SCORING") {
    context.addIssue({
      code: "custom",
      message: "A scoring start event requires a SCORING snapshot",
      path: ["snapshot", "status"]
    });
  }
  if (payload.entryReason === "DOUBLE_PASS") {
    if (payload.move?.kind !== "PASS" || payload.countingRequestId !== void 0 || payload.snapshot.scoring !== void 0 && payload.snapshot.scoring.session?.entryReason !== "DOUBLE_PASS") {
      context.addIssue({
        code: "custom",
        message: "A double-pass scoring start requires the final PASS and no counting request",
        path: ["entryReason"]
      });
    }
    if (payload.move !== void 0 && (payload.snapshot.timing.ruleCode === "NONE_V1" ? payload.move.elapsedMs !== null : payload.move.elapsedMs === null)) {
      context.addIssue({
        code: "custom",
        message: "Move elapsed time must match the authoritative timing rule",
        path: ["move", "elapsedMs"]
      });
    }
    return;
  }
  if (payload.move !== void 0 || payload.countingRequestId === void 0 || payload.snapshot.scoring?.session?.entryReason !== "AGREED_REQUEST" || payload.snapshot.scoring.countingRequest?.countingRequestId !== payload.countingRequestId) {
    context.addIssue({
      code: "custom",
      message: "An agreed scoring start requires its accepted request and no fabricated move",
      path: ["entryReason"]
    });
  }
});
var legacyDoublePassScoringStartPayloadSchema = moveAcceptedPayloadSchema.superRefine(
  (payload, context) => {
    if (payload.move.kind !== "PASS" || payload.snapshot.status !== "SCORING") {
      context.addIssue({
        code: "custom",
        message: "A legacy scoring start is only valid for the final PASS",
        path: ["move", "kind"]
      });
    }
  }
);
var gameScoringStartEventSchema = z19.object({
  event: z19.literal("game.scoring.start"),
  ...serverEnvelopeShape,
  payload: z19.union([
    phase5GameScoringStartPayloadSchema,
    legacyDoublePassScoringStartPayloadSchema
  ])
}).strict();
var gameCountingRequestedEventSchema = z19.object({
  event: z19.literal("game.counting.requested"),
  ...serverEnvelopeShape,
  payload: z19.object({
    countingRequestId: identifierSchema,
    snapshot: timedGameSnapshotSchema
  }).strict()
}).strict();
var gameCountingRejectedEventSchema = z19.object({
  event: z19.literal("game.counting.rejected"),
  ...serverEnvelopeShape,
  payload: z19.object({
    countingRequestId: identifierSchema,
    snapshot: timedGameSnapshotSchema
  }).strict()
}).strict();
var gameScoringProposedEventSchema = z19.object({
  event: z19.literal("game.scoring.proposed"),
  ...serverEnvelopeShape,
  payload: z19.object({
    scoringVersion: z19.number().int().safe().positive(),
    scoringHash: scoringHashSchema,
    snapshot: timedGameSnapshotSchema
  }).strict()
}).strict();
var gameScoringConfirmedEventSchema = z19.object({
  event: z19.literal("game.scoring.confirmed"),
  ...serverEnvelopeShape,
  payload: z19.object({
    scoringVersion: z19.number().int().safe().positive(),
    scoringHash: scoringHashSchema,
    confirmedBy: stoneColorSchema,
    snapshot: timedGameSnapshotSchema
  }).strict()
}).strict();
var gameScoringRejectedEventSchema = z19.object({
  event: z19.literal("game.scoring.rejected"),
  ...serverEnvelopeShape,
  payload: z19.object({
    scoringVersion: z19.number().int().safe().positive(),
    scoringHash: scoringHashSchema,
    rejectedBy: stoneColorSchema,
    snapshot: timedGameSnapshotSchema
  }).strict()
}).strict();
var gamePlayResumedEventSchema = z19.object({
  event: z19.literal("game.play.resumed"),
  ...serverEnvelopeShape,
  payload: z19.object({
    resumeColor: stoneColorSchema,
    positionRevision: z19.number().int().safe().positive(),
    disputedGroupAnchor: boardPointSchema,
    snapshot: timedGameSnapshotSchema
  }).strict()
}).strict();
var gameTeacherPausedEventSchema = z19.object({
  event: z19.literal("game.teacher.paused"),
  ...serverEnvelopeShape,
  payload: z19.object({
    interventionId: identifierSchema,
    snapshot: phase6TimedGameSnapshotSchema
  }).strict()
}).strict();
var gameTeacherResumedEventSchema = z19.object({
  event: z19.literal("game.teacher.resumed"),
  ...serverEnvelopeShape,
  payload: z19.object({
    interventionId: identifierSchema,
    pauseInterventionId: identifierSchema,
    snapshot: phase6TimedGameSnapshotSchema
  }).strict()
}).strict();
var gameTeacherCountingResolvedEventSchema = z19.object({
  event: z19.literal("game.teacher.counting.resolved"),
  ...serverEnvelopeShape,
  payload: z19.object({
    interventionId: identifierSchema,
    countingRequestId: identifierSchema,
    resolution: teacherCountingResolutionSchema,
    snapshot: phase6TimedGameSnapshotSchema
  }).strict()
}).strict();
var gameFinishEventSchema = z19.object({
  event: z19.literal("game.finish"),
  ...serverEnvelopeShape,
  payload: snapshotPayloadSchema
}).strict();
var gameTimeoutEventSchema = z19.object({
  event: z19.literal("game.timeout"),
  ...serverEnvelopeShape,
  payload: z19.object({
    loser: z19.enum(["BLACK", "WHITE"]),
    winner: z19.enum(["BLACK", "WHITE"]),
    resultType: z19.literal("TIMEOUT"),
    finalResult: z19.enum(["B+T", "W+T"]),
    snapshot: timedGameSnapshotSchema
  }).strict()
}).strict();
var gameTakebackRequestedEventSchema = z19.object({
  event: z19.literal("game.takeback.requested"),
  ...serverEnvelopeShape,
  payload: z19.object({
    takeback: gameTakebackSnapshotSchema,
    snapshot: timedGameSnapshotSchema
  }).strict()
}).strict().superRefine((event, context) => {
  if (event.payload.takeback.status !== "PENDING" || event.payload.snapshot.status !== "PLAYING" || event.payload.takeback.requestedStateVersion !== event.stateVersion || event.payload.snapshot.stateVersion !== event.stateVersion) {
    context.addIssue({
      code: "custom",
      message: "A takeback request must carry matching pending authoritative state",
      path: ["payload", "takeback"]
    });
  }
});
var gameTakebackResolvedEventSchema = z19.object({
  event: z19.literal("game.takeback.resolved"),
  ...serverEnvelopeShape,
  payload: z19.object({
    takeback: gameTakebackSnapshotSchema,
    snapshot: timedGameSnapshotSchema
  }).strict()
}).strict().superRefine((event, context) => {
  if (event.payload.takeback.status === "PENDING" || event.payload.snapshot.status !== "PLAYING" || event.payload.takeback.resolvedStateVersion !== event.stateVersion || event.payload.snapshot.stateVersion !== event.stateVersion) {
    context.addIssue({
      code: "custom",
      message: "A takeback resolution must carry matching terminal authoritative state",
      path: ["payload", "takeback"]
    });
  }
});
var gameDisconnectionPausedEventSchema = z19.object({
  event: z19.literal("game.disconnection.paused"),
  ...serverEnvelopeShape,
  payload: snapshotPayloadSchema
}).strict();
var gameDisconnectionResumedEventSchema = z19.object({
  event: z19.literal("game.disconnection.resumed"),
  ...serverEnvelopeShape,
  payload: snapshotPayloadSchema
}).strict();
function validateTimeoutEvent(event, context) {
  if (event.payload.snapshot.status !== "FINISHED") {
    context.addIssue({
      code: "custom",
      message: "A timeout event requires a terminal game snapshot",
      path: ["payload", "snapshot", "status"]
    });
  }
  if (event.payload.loser === event.payload.winner || event.payload.snapshot.board.currentPlayer !== event.payload.loser) {
    context.addIssue({
      code: "custom",
      message: "Timeout loser and winner must be opposing colors",
      path: ["payload", "winner"]
    });
  }
  const expectedResult = event.payload.winner === "BLACK" ? "B+T" : "W+T";
  if (event.payload.finalResult !== expectedResult) {
    context.addIssue({
      code: "custom",
      message: "The final result must identify the timeout winner",
      path: ["payload", "finalResult"]
    });
  }
  const loserTiming = event.payload.loser === "BLACK" ? event.payload.snapshot.timing.black : event.payload.snapshot.timing.white;
  const byoyomi = event.payload.snapshot.timing.byoyomi;
  const exhaustedByoyomi = byoyomi !== null && byoyomi !== void 0 && loserTiming.byoyomiPeriodsRemaining === 0 && loserTiming.byoyomiPeriodRemainingMs === 0;
  const exhaustedAbsoluteTime = (byoyomi === null || byoyomi === void 0) && loserTiming.elapsedMs === event.payload.snapshot.timing.mainTimeMs;
  if (event.payload.snapshot.timing.ruleCode !== "ABSOLUTE_V1" || loserTiming.remainingMs !== 0 || !exhaustedByoyomi && !exhaustedAbsoluteTime) {
    context.addIssue({
      code: "custom",
      message: "A timeout event requires the loser\u2019s authoritative clock to be exhausted",
      path: ["payload", "snapshot", "timing"]
    });
  }
  const terminal = event.payload.snapshot.terminal;
  if (terminal !== void 0 && (terminal.resultType !== "TIMEOUT" || terminal.loser !== event.payload.loser || terminal.winner !== event.payload.winner || terminal.finalResult !== event.payload.finalResult)) {
    context.addIssue({
      code: "custom",
      message: "Timeout payload and terminal snapshot must agree",
      path: ["payload", "snapshot", "terminal"]
    });
  }
}
var replayableGameEventSchema = z19.discriminatedUnion("event", [
  roomJoinedEventSchema,
  roomUpdatedEventSchema,
  gameNigiriSelectedEventSchema,
  gameNigiriResolvedEventSchema,
  gameStartEventSchema,
  gameMoveAcceptedEventSchema,
  gameScoringStartEventSchema,
  gameCountingRequestedEventSchema,
  gameCountingRejectedEventSchema,
  gameScoringProposedEventSchema,
  gameScoringConfirmedEventSchema,
  gameScoringRejectedEventSchema,
  gamePlayResumedEventSchema,
  gameTeacherPausedEventSchema,
  gameTeacherResumedEventSchema,
  gameTeacherCountingResolvedEventSchema,
  gameFinishEventSchema,
  gameTimeoutEventSchema,
  gameTakebackRequestedEventSchema,
  gameTakebackResolvedEventSchema,
  gameDisconnectionPausedEventSchema,
  gameDisconnectionResumedEventSchema
]).superRefine((event, context) => {
  if (event.gameId !== event.payload.snapshot.gameId) {
    context.addIssue({
      code: "custom",
      message: "Envelope and snapshot game ids must match",
      path: ["payload", "snapshot", "gameId"]
    });
  }
  if (event.stateVersion !== event.payload.snapshot.stateVersion) {
    context.addIssue({
      code: "custom",
      message: "Envelope and snapshot state versions must match",
      path: ["payload", "snapshot", "stateVersion"]
    });
  }
  if (event.event === "game.timeout") {
    validateTimeoutEvent(event, context);
  }
  if (event.event === "game.disconnection.paused" && (event.payload.snapshot.disconnection === null || event.payload.snapshot.disconnection === void 0)) {
    context.addIssue({
      code: "custom",
      message: "A disconnection pause event requires an active grace window",
      path: ["payload", "snapshot", "disconnection"]
    });
  }
  if (event.event === "game.disconnection.resumed" && event.payload.snapshot.disconnection !== null) {
    context.addIssue({
      code: "custom",
      message: "A disconnection resume event must clear the grace window",
      path: ["payload", "snapshot", "disconnection"]
    });
  }
  if (event.event === "game.nigiri.selected" && (event.payload.snapshot.status !== "READY" || event.payload.snapshot.nigiri?.stage !== "GUESSING")) {
    context.addIssue({
      code: "custom",
      message: "Nigiri selection must advance to the guessing stage",
      path: ["payload", "snapshot", "nigiri"]
    });
  }
  if (event.event === "game.nigiri.resolved" && (event.payload.snapshot.status !== "READY" || event.payload.snapshot.nigiri?.stage !== "RESOLVED")) {
    context.addIssue({
      code: "custom",
      message: "Nigiri resolution must expose final seats before game start",
      path: ["payload", "snapshot", "nigiri"]
    });
  }
  if (event.event === "game.counting.requested") {
    const request = event.payload.snapshot.scoring?.countingRequest;
    if (event.payload.snapshot.status !== "COUNTING_REQUESTED" || event.payload.snapshot.scoring?.status !== "REQUESTED" || request?.status !== "PENDING" || request.countingRequestId !== event.payload.countingRequestId) {
      context.addIssue({
        code: "custom",
        message: "Counting requested event must match its pending authoritative request",
        path: ["payload", "snapshot", "scoring"]
      });
    }
  }
  if (event.event === "game.counting.rejected" && (event.payload.snapshot.status !== "PLAYING" || event.payload.snapshot.scoring !== void 0)) {
    context.addIssue({
      code: "custom",
      message: "Counting rejection must restore a clean PLAYING snapshot",
      path: ["payload", "snapshot"]
    });
  }
  if (event.event === "game.scoring.proposed") {
    const proposal = event.payload.snapshot.scoring?.proposal;
    if (event.payload.snapshot.status !== "SCORING" || event.payload.snapshot.scoring?.status !== "SCORING" || proposal === null || proposal === void 0 || proposal.scoringVersion !== event.payload.scoringVersion || proposal.scoringHash !== event.payload.scoringHash) {
      context.addIssue({
        code: "custom",
        message: "Scoring proposal event must match its authoritative proposal revision",
        path: ["payload", "snapshot", "scoring", "proposal"]
      });
    }
  }
  if (event.event === "game.scoring.confirmed") {
    const proposal = event.payload.snapshot.scoring?.proposal;
    const confirmation = event.payload.confirmedBy === "BLACK" ? proposal?.confirmedBy.blackAt : proposal?.confirmedBy.whiteAt;
    if (event.payload.snapshot.status !== "SCORING" || event.payload.snapshot.scoring?.status !== "SCORING" || proposal === null || proposal === void 0 || proposal.scoringVersion !== event.payload.scoringVersion || proposal.scoringHash !== event.payload.scoringHash || confirmation === null || confirmation === void 0) {
      context.addIssue({
        code: "custom",
        message: "Scoring confirmation event must match the confirmed proposal revision",
        path: ["payload", "snapshot", "scoring", "proposal"]
      });
    }
  }
  if (event.event === "game.scoring.rejected" && (event.payload.snapshot.status !== "PLAYING" || event.payload.snapshot.scoring !== void 0)) {
    context.addIssue({
      code: "custom",
      message: "Scoring rejection must restore the unchanged board to PLAYING",
      path: ["payload", "snapshot"]
    });
  }
  if (event.event === "game.play.resumed" && (event.payload.snapshot.status !== "PLAYING" || event.payload.snapshot.board.currentPlayer !== event.payload.resumeColor || event.payload.snapshot.scoring !== void 0 || event.payload.disputedGroupAnchor.x >= event.payload.snapshot.board.size || event.payload.disputedGroupAnchor.y >= event.payload.snapshot.board.size)) {
    context.addIssue({
      code: "custom",
      message: "Play resumed event must expose the server-derived board revision and player",
      path: ["payload"]
    });
  }
  if (event.event === "game.teacher.paused" && event.payload.snapshot.supervision?.pause?.interventionId !== event.payload.interventionId) {
    context.addIssue({
      code: "custom",
      message: "Teacher pause event must expose its active supervision overlay",
      path: ["payload", "snapshot", "supervision"]
    });
  }
  if (event.event === "game.teacher.resumed" && event.payload.snapshot.supervision?.pause !== null) {
    context.addIssue({
      code: "custom",
      message: "Teacher resume event must clear its active supervision overlay",
      path: ["payload", "snapshot", "supervision"]
    });
  }
  if (event.event === "game.teacher.resumed" && event.payload.interventionId === event.payload.pauseInterventionId) {
    context.addIssue({
      code: "custom",
      message: "Resume and pause interventions must be distinct",
      path: ["payload", "interventionId"]
    });
  }
  if (event.event === "game.teacher.counting.resolved") {
    const snapshot = event.payload.snapshot;
    const request = snapshot.scoring?.countingRequest;
    const session = snapshot.scoring?.session;
    if (event.payload.resolution === "CANCEL_AND_RESUME" ? snapshot.status !== "PLAYING" || snapshot.scoring !== void 0 : snapshot.status !== "SCORING" || snapshot.scoring?.status !== "SCORING" || request?.countingRequestId !== event.payload.countingRequestId || request.status !== "ACCEPTED" || request.resolutionSource !== "TEACHER" || request.resolutionInterventionId !== event.payload.interventionId || session?.entryReason !== "TEACHER_DECISION") {
      context.addIssue({
        code: "custom",
        message: "Teacher counting resolution must match its authoritative lifecycle result",
        path: ["payload", "snapshot", "scoring"]
      });
    }
  }
  if (event.event === "game.finish" && (event.payload.snapshot.status !== "FINISHED" || event.payload.snapshot.terminal === void 0 || event.payload.snapshot.terminal.resultType === "TIMEOUT")) {
    context.addIssue({
      code: "custom",
      message: "game.finish is reserved for SCORE, RESIGNATION, FORFEIT, or DISCONNECTION terminal snapshots",
      path: ["payload", "snapshot", "terminal"]
    });
  }
  if (event.event === "game.finish") {
    const terminal = event.payload.snapshot.terminal;
    const isTeacherScore = terminal?.resultType === "SCORE" && terminal.resolutionSource === "TEACHER_ADJUDICATED";
    const isForfeit = terminal?.resultType === "FORFEIT";
    if ((isTeacherScore || isForfeit) && (event.payload.snapshot.supervision === void 0 || event.payload.snapshot.supervision.pause !== null)) {
      context.addIssue({
        code: "custom",
        message: "A teacher terminal event must expose a cleared supervision projection",
        path: ["payload", "snapshot", "supervision"]
      });
    }
  }
});
var gameStateDeltaPayloadSchema = z19.object({
  fromStateVersion: stateVersionSchema,
  toStateVersion: stateVersionSchema,
  events: z19.array(replayableGameEventSchema),
  snapshot: timedGameSnapshotSchema
}).strict().superRefine((delta, context) => {
  if (delta.toStateVersion < delta.fromStateVersion) {
    context.addIssue({
      code: "custom",
      message: "toStateVersion cannot be lower than fromStateVersion",
      path: ["toStateVersion"]
    });
    return;
  }
  if (delta.snapshot.stateVersion !== delta.toStateVersion) {
    context.addIssue({
      code: "custom",
      message: "Delta snapshot must expose toStateVersion",
      path: ["snapshot", "stateVersion"]
    });
  }
  const expectedEventCount = delta.toStateVersion - delta.fromStateVersion;
  if (delta.events.length !== expectedEventCount) {
    context.addIssue({
      code: "custom",
      message: "Delta events must cover every state version exactly once",
      path: ["events"]
    });
    return;
  }
  delta.events.forEach((event, index) => {
    const expectedVersion = delta.fromStateVersion + index + 1;
    if (event.stateVersion !== expectedVersion) {
      context.addIssue({
        code: "custom",
        message: `Expected state version ${expectedVersion}`,
        path: ["events", index, "stateVersion"]
      });
    }
    if (index > 0 && event.gameId !== delta.events[0]?.gameId) {
      context.addIssue({
        code: "custom",
        message: "All delta events must belong to the same game",
        path: ["events", index, "gameId"]
      });
    }
  });
  if (delta.events[0] !== void 0 && delta.snapshot.gameId !== delta.events[0].gameId) {
    context.addIssue({
      code: "custom",
      message: "Delta snapshot and events must belong to the same game",
      path: ["snapshot", "gameId"]
    });
  }
  const finalEventSupervision = delta.events.at(-1)?.payload.snapshot.supervision;
  if (finalEventSupervision !== void 0 && JSON.stringify(delta.snapshot.supervision) !== JSON.stringify(finalEventSupervision)) {
    context.addIssue({
      code: "custom",
      message: "Delta snapshot must preserve the final replayed supervision projection",
      path: ["snapshot", "supervision"]
    });
  }
});
var gameMoveRejectedEventSchema = z19.object({
  event: z19.literal("game.move.rejected"),
  ...serverEnvelopeShape,
  payload: gameCommandErrorSchema
}).strict();
var gameStateDeltaEventSchema = z19.object({
  event: z19.literal("game.state.delta"),
  ...serverEnvelopeShape,
  payload: gameStateDeltaPayloadSchema
}).strict();
var gameStateSyncEventSchema = z19.object({
  event: z19.literal("game.state.sync"),
  ...serverEnvelopeShape,
  payload: snapshotPayloadSchema
}).strict();
var commandRejectedEventSchema = z19.object({
  event: z19.literal("command.rejected"),
  ...serverEnvelopeShape,
  payload: gameCommandErrorSchema
}).strict();
var serverEventEnvelopeSchema = z19.discriminatedUnion("event", [
  roomJoinedEventSchema,
  roomUpdatedEventSchema,
  gameNigiriSelectedEventSchema,
  gameNigiriResolvedEventSchema,
  gameStartEventSchema,
  gameMoveAcceptedEventSchema,
  gameMoveRejectedEventSchema,
  gameScoringStartEventSchema,
  gameCountingRequestedEventSchema,
  gameCountingRejectedEventSchema,
  gameScoringProposedEventSchema,
  gameScoringConfirmedEventSchema,
  gameScoringRejectedEventSchema,
  gamePlayResumedEventSchema,
  gameTeacherPausedEventSchema,
  gameTeacherResumedEventSchema,
  gameTeacherCountingResolvedEventSchema,
  gameFinishEventSchema,
  gameTimeoutEventSchema,
  gameTakebackRequestedEventSchema,
  gameTakebackResolvedEventSchema,
  gameDisconnectionPausedEventSchema,
  gameDisconnectionResumedEventSchema,
  gameStateDeltaEventSchema,
  gameStateSyncEventSchema,
  commandRejectedEventSchema
]).superRefine((event, context) => {
  if (event.event === "game.state.sync") {
    if (event.gameId !== event.payload.snapshot.gameId) {
      context.addIssue({
        code: "custom",
        message: "Envelope and snapshot game ids must match",
        path: ["payload", "snapshot", "gameId"]
      });
    }
    if (event.stateVersion !== event.payload.snapshot.stateVersion) {
      context.addIssue({
        code: "custom",
        message: "Envelope and snapshot state versions must match",
        path: ["payload", "snapshot", "stateVersion"]
      });
    }
  }
  if (event.event === "game.state.delta") {
    if (event.stateVersion !== event.payload.toStateVersion) {
      context.addIssue({
        code: "custom",
        message: "Delta envelope must expose the final delta state version",
        path: ["stateVersion"]
      });
    }
    event.payload.events.forEach((replayedEvent, index) => {
      if (event.gameId !== replayedEvent.gameId) {
        context.addIssue({
          code: "custom",
          message: "Delta events must belong to the envelope game",
          path: ["payload", "events", index, "gameId"]
        });
      }
    });
    if (event.gameId !== event.payload.snapshot.gameId) {
      context.addIssue({
        code: "custom",
        message: "Delta snapshot must belong to the envelope game",
        path: ["payload", "snapshot", "gameId"]
      });
    }
  }
  if (event.event !== "game.move.rejected" && event.event !== "game.state.delta" && event.event !== "game.state.sync" && event.event !== "command.rejected") {
    if (event.gameId !== event.payload.snapshot.gameId) {
      context.addIssue({
        code: "custom",
        message: "Envelope and snapshot game ids must match",
        path: ["payload", "snapshot", "gameId"]
      });
    }
    if (event.stateVersion !== event.payload.snapshot.stateVersion) {
      context.addIssue({
        code: "custom",
        message: "Envelope and snapshot state versions must match",
        path: ["payload", "snapshot", "stateVersion"]
      });
    }
  }
  if (event.event === "game.timeout") {
    validateTimeoutEvent(event, context);
  }
  if (event.event !== "game.move.rejected" && event.event !== "game.state.delta" && event.event !== "game.state.sync" && event.event !== "command.rejected" && !replayableGameEventSchema.safeParse(event).success) {
    context.addIssue({
      code: "custom",
      message: "Replayable server event violates its authoritative event contract",
      path: ["payload"]
    });
  }
});

// src/teacher.ts
function boundedSafeText2(min, max) {
  return z20.string().trim().min(min).max(max).refine((value) => !containsC0ControlCharacter(value), {
    message: "Text cannot contain C0 control characters"
  });
}
var deleteTeacherGameRecordBodySchema = z20.object({ stateVersion: stateVersionSchema }).strict();
var deleteTeacherGameRecordResponseSchema = z20.object({
  requestId: requestIdSchema,
  gameId: identifierSchema,
  stateVersion: stateVersionSchema,
  serverTimestamp: serverTimestampSchema,
  data: z20.object({ deleted: z20.literal(true) }).strict()
}).strict();
function addTeacherIssue(context, message, path) {
  context.addIssue({
    code: "custom",
    message,
    path
  });
}
var teacherInternalReasonSchema = boundedSafeText2(1, 500);
var teacherPublicMessageSchema = boundedSafeText2(1, 160);
var teacherNoteSchema = boundedSafeText2(1, 500);
var teacherCursorSchema = z20.string().trim().min(1).max(512);
var teacherAccessLevelSchema = z20.enum(["OBSERVE", "MANAGE"]);
var teacherAuthorizationRoleSchema = z20.enum(["TEACHER", "ADMIN"]);
var teacherPresenceStatusSchema = z20.enum(["ONLINE", "OFFLINE", "UNKNOWN"]);
var teacherAttentionFlagSchema = z20.enum([
  "PAUSED",
  "COUNTING_WAITING",
  "PLAYER_OFFLINE",
  "CLOCK_LOW"
]);
var teacherInterventionActionSchema = z20.enum([
  "PAUSE",
  "RESUME",
  "COUNTING_CANCELLED",
  "COUNTING_ACCEPTED",
  "SCORE_ADJUDICATED",
  "FORFEIT",
  "RESULT_CORRECTED",
  "NOTE"
]);
var teacherGamePresenceSchema = z20.object({
  membershipId: identifierSchema,
  status: teacherPresenceStatusSchema,
  lastSeenAt: serverTimestampSchema.nullable()
}).strict().superRefine((presence, context) => {
  if (presence.status === "ONLINE" && presence.lastSeenAt === null || presence.status === "UNKNOWN" && presence.lastSeenAt !== null) {
    addTeacherIssue(context, "Presence time must match its confidence state", ["lastSeenAt"]);
  }
});
var teacherGamePlayerSummarySchema = z20.object({
  membershipId: identifierSchema,
  displayName: z20.string().trim().min(1).max(64),
  seat: roomSeatSchema,
  matchedClassIds: z20.array(identifierSchema).max(20)
}).strict().superRefine((player, context) => {
  if (new Set(player.matchedClassIds).size !== player.matchedClassIds.length) {
    addTeacherIssue(context, "Matched class ids must be unique", ["matchedClassIds"]);
  }
});
var teacherTimingSummarySchema = z20.object({
  ruleCode: z20.enum(["NONE_V1", "ABSOLUTE_V1"]),
  status: clockStatusSchema,
  activeColor: stoneColorSchema.nullable(),
  deadlineAt: serverTimestampSchema.nullable(),
  blackRemainingMs: durationMsSchema.nullable(),
  whiteRemainingMs: durationMsSchema.nullable()
}).strict().superRefine((timing, context) => {
  const isDisabled = timing.ruleCode === "NONE_V1";
  if (isDisabled !== (timing.status === "DISABLED" && timing.activeColor === null && timing.deadlineAt === null && timing.blackRemainingMs === null && timing.whiteRemainingMs === null)) {
    addTeacherIssue(context, "Timing summary values must match the timing rule", ["ruleCode"]);
  }
  if (timing.status === "RUNNING" && (timing.activeColor === null || timing.deadlineAt === null)) {
    addTeacherIssue(context, "A running timing summary requires an active deadline", ["status"]);
  }
  if (timing.status !== "RUNNING" && (timing.activeColor !== null || timing.deadlineAt !== null)) {
    addTeacherIssue(context, "A non-running timing summary cannot expose an active deadline", [
      "status"
    ]);
  }
  if (!isDisabled && (timing.blackRemainingMs === null || timing.whiteRemainingMs === null)) {
    addTeacherIssue(context, "An absolute timing summary requires both remaining times", [
      "blackRemainingMs"
    ]);
  }
});
var teacherGameSummarySchema = z20.object({
  gameId: identifierSchema,
  stateVersion: stateVersionSchema,
  status: gameStatusSchema,
  boardSize: boardSizeSchema,
  currentSequence: z20.number().int().safe().nonnegative(),
  startedAt: serverTimestampSchema.nullable(),
  updatedAt: serverTimestampSchema,
  accessLevel: teacherAccessLevelSchema,
  players: z20.array(teacherGamePlayerSummarySchema).min(1).max(2),
  presence: z20.array(teacherGamePresenceSchema).min(1).max(2),
  timing: teacherTimingSummarySchema,
  scoringStatus: z20.enum(["REQUESTED", "SCORING", "FINISHED"]).nullable(),
  supervision: supervisionSnapshotSchema,
  attentionFlags: z20.array(teacherAttentionFlagSchema).max(4)
}).strict().superRefine((game, context) => {
  const playerMemberships = /* @__PURE__ */ new Set();
  const presenceMemberships = /* @__PURE__ */ new Set();
  const seats = /* @__PURE__ */ new Set();
  game.players.forEach((player, index) => {
    if (playerMemberships.has(player.membershipId)) {
      addTeacherIssue(context, "Teacher game players must be unique", [
        "players",
        index,
        "membershipId"
      ]);
    }
    if (seats.has(player.seat)) {
      addTeacherIssue(context, "Teacher game seats must be unique", ["players", index, "seat"]);
    }
    playerMemberships.add(player.membershipId);
    seats.add(player.seat);
  });
  game.presence.forEach((presence, index) => {
    if (!playerMemberships.has(presence.membershipId)) {
      addTeacherIssue(context, "Presence must belong to a current game player", [
        "presence",
        index,
        "membershipId"
      ]);
    }
    if (presenceMemberships.has(presence.membershipId)) {
      addTeacherIssue(context, "Presence memberships must be unique", [
        "presence",
        index,
        "membershipId"
      ]);
    }
    presenceMemberships.add(presence.membershipId);
  });
  if (new Set(game.attentionFlags).size !== game.attentionFlags.length) {
    addTeacherIssue(context, "Attention flags must be unique", ["attentionFlags"]);
  }
  if (game.attentionFlags.includes("PAUSED") !== (game.supervision.pause !== null)) {
    addTeacherIssue(context, "The PAUSED attention flag must match supervision", [
      "attentionFlags"
    ]);
  }
  if (game.accessLevel === "MANAGE" && game.players.length !== 2) {
    addTeacherIssue(context, "Manage access requires both current player seats", ["accessLevel"]);
  }
});
var teacherGamesQuerySchema = z20.object({
  classId: identifierSchema.optional(),
  studentMembershipId: identifierSchema.optional(),
  status: z20.array(gameStatusSchema).max(gameStatusSchema.options.length).default([]),
  attentionOnly: z20.boolean().default(false),
  after: teacherCursorSchema.optional(),
  limit: z20.number().int().min(1).max(50).default(20)
}).strict().superRefine((query, context) => {
  if (new Set(query.status).size !== query.status.length) {
    addTeacherIssue(context, "Teacher game status filters must be unique", ["status"]);
  }
});
var teacherGamesResponseSchema = z20.object({
  requestId: requestIdSchema,
  serverTimestamp: serverTimestampSchema,
  data: z20.object({
    games: z20.array(teacherGameSummarySchema).max(50),
    nextCursor: teacherCursorSchema.nullable()
  }).strict()
}).strict().superRefine((response, context) => {
  const gameIds = /* @__PURE__ */ new Set();
  response.data.games.forEach((game, index) => {
    if (gameIds.has(game.gameId)) {
      addTeacherIssue(context, "Teacher game pages cannot contain duplicate games", [
        "data",
        "games",
        index,
        "gameId"
      ]);
    }
    gameIds.add(game.gameId);
  });
});
var teacherInterventionActorSchema = z20.object({
  membershipId: identifierSchema,
  displayName: z20.string().trim().min(1).max(64),
  role: teacherAuthorizationRoleSchema
}).strict();
var teacherInterventionSchema = z20.object({
  interventionId: identifierSchema,
  gameId: identifierSchema,
  action: teacherInterventionActionSchema,
  actor: teacherInterventionActorSchema,
  authorizationScopeClassId: identifierSchema.nullable(),
  requestId: requestIdSchema,
  eventId: eventIdSchema.nullable(),
  expectedStateVersion: stateVersionSchema,
  resultingStateVersion: stateVersionSchema,
  positionRevision: z20.number().int().safe().nonnegative(),
  currentSequence: z20.number().int().safe().nonnegative(),
  reasonCode: teacherInterventionReasonCodeSchema.nullable(),
  internalReason: teacherInternalReasonSchema,
  publicMessage: teacherPublicMessageSchema.optional(),
  relatedInterventionId: identifierSchema.optional(),
  countingRequestId: identifierSchema.optional(),
  scoringRecordId: identifierSchema.optional(),
  terminalResultType: z20.enum(["SCORE", "FORFEIT"]).optional(),
  serverTimestamp: serverTimestampSchema
}).strict().superRefine((intervention, context) => {
  if (intervention.actor.role === "TEACHER" && intervention.authorizationScopeClassId === null) {
    addTeacherIssue(context, "A teacher intervention requires its class authorization evidence", [
      "authorizationScopeClassId"
    ]);
  }
  if (intervention.actor.role === "ADMIN" && intervention.authorizationScopeClassId !== null) {
    addTeacherIssue(context, "An administrator uses organization scope, not class scope", [
      "authorizationScopeClassId"
    ]);
  }
  if (intervention.action === "NOTE" ? intervention.resultingStateVersion !== intervention.expectedStateVersion || intervention.eventId !== null : intervention.resultingStateVersion !== intervention.expectedStateVersion + 1 || intervention.eventId === null) {
    addTeacherIssue(context, "Intervention version and event identity must match its action", [
      "resultingStateVersion"
    ]);
  }
  const requiresReasonCode = intervention.action !== "NOTE" && intervention.action !== "RESUME";
  if (requiresReasonCode ? intervention.reasonCode === null : intervention.reasonCode !== null) {
    addTeacherIssue(context, "Intervention reason code must match its action", ["reasonCode"]);
  }
  if (intervention.action === "RESUME" !== (intervention.relatedInterventionId !== void 0)) {
    addTeacherIssue(context, "Only resume must reference its related pause intervention", [
      "relatedInterventionId"
    ]);
  }
  const isCountingAction = intervention.action === "COUNTING_CANCELLED" || intervention.action === "COUNTING_ACCEPTED";
  if (isCountingAction !== (intervention.countingRequestId !== void 0)) {
    addTeacherIssue(context, "Counting intervention identity must match its action", [
      "countingRequestId"
    ]);
  }
  if (intervention.action === "SCORE_ADJUDICATED" !== (intervention.scoringRecordId !== void 0 && intervention.terminalResultType === "SCORE")) {
    addTeacherIssue(context, "Score intervention provenance must match its action", [
      "scoringRecordId"
    ]);
  }
  if ((intervention.action === "FORFEIT" || intervention.action === "RESULT_CORRECTED") !== (intervention.terminalResultType === "FORFEIT")) {
    addTeacherIssue(context, "Forfeit terminal provenance must match its action", [
      "terminalResultType"
    ]);
  }
  if (intervention.action === "NOTE" && intervention.publicMessage !== void 0) {
    addTeacherIssue(context, "Internal notes cannot expose a public message", ["publicMessage"]);
  }
});
var teacherGameDetailResponseSchema = z20.object({
  requestId: requestIdSchema,
  gameId: identifierSchema,
  stateVersion: stateVersionSchema,
  serverTimestamp: serverTimestampSchema,
  data: z20.object({
    snapshot: phase6TimedGameSnapshotSchema,
    // Optional for clients reading responses from an older backend during rollout.
    frames: z20.array(gameReviewFrameSchema).min(1).max(10001).optional(),
    accessLevel: teacherAccessLevelSchema,
    presence: z20.array(teacherGamePresenceSchema).min(1).max(2),
    recentInterventions: z20.array(teacherInterventionSchema).max(20)
  }).strict()
}).strict().superRefine((response, context) => {
  if (response.gameId !== response.data.snapshot.gameId || response.stateVersion !== response.data.snapshot.stateVersion) {
    addTeacherIssue(context, "Teacher detail envelope must match its authoritative snapshot", [
      "data",
      "snapshot"
    ]);
  }
  response.data.recentInterventions.forEach((intervention, index) => {
    if (intervention.gameId !== response.gameId) {
      addTeacherIssue(context, "Intervention must belong to the requested game", [
        "data",
        "recentInterventions",
        index,
        "gameId"
      ]);
    }
    if (intervention.resultingStateVersion > response.stateVersion) {
      addTeacherIssue(context, "Intervention cannot be newer than the detail snapshot", [
        "data",
        "recentInterventions",
        index,
        "resultingStateVersion"
      ]);
    }
  });
  const { frames, snapshot } = response.data;
  if (frames !== void 0) {
    frames.forEach((frame, index) => {
      if (frame.moveNumber !== index || frame.stones.length !== snapshot.board.size ** 2 || frame.point !== null && (frame.point.x >= snapshot.board.size || frame.point.y >= snapshot.board.size)) {
        addTeacherIssue(
          context,
          "Observation frames must be contiguous and match the board size",
          ["data", "frames", index]
        );
      }
    });
    const last = frames.at(-1);
    if (last === void 0 || last.stones.some((stone, index) => stone !== snapshot.board.stones[index])) {
      addTeacherIssue(context, "Observation frames must end at the authoritative snapshot", [
        "data",
        "frames"
      ]);
    }
  }
  const playerMemberships = new Set(
    response.data.snapshot.room.players.map((player) => player.membershipId)
  );
  const presenceMemberships = /* @__PURE__ */ new Set();
  response.data.presence.forEach((presence, index) => {
    if (!playerMemberships.has(presence.membershipId)) {
      addTeacherIssue(context, "Presence must belong to a current game player", [
        "data",
        "presence",
        index,
        "membershipId"
      ]);
    }
    if (presenceMemberships.has(presence.membershipId)) {
      addTeacherIssue(context, "Presence memberships must be unique", [
        "data",
        "presence",
        index,
        "membershipId"
      ]);
    }
    presenceMemberships.add(presence.membershipId);
  });
});
var teacherInterventionsQuerySchema = z20.object({
  after: teacherCursorSchema.optional(),
  limit: z20.number().int().min(1).max(100).default(50)
}).strict();
var teacherInterventionsResponseSchema = z20.object({
  requestId: requestIdSchema,
  gameId: identifierSchema,
  stateVersion: stateVersionSchema,
  serverTimestamp: serverTimestampSchema,
  data: z20.object({
    interventions: z20.array(teacherInterventionSchema).max(100),
    nextCursor: teacherCursorSchema.nullable()
  }).strict()
}).strict().superRefine((response, context) => {
  response.data.interventions.forEach((intervention, index) => {
    if (intervention.gameId !== response.gameId) {
      addTeacherIssue(context, "Intervention must belong to the requested game", [
        "data",
        "interventions",
        index,
        "gameId"
      ]);
    }
    if (intervention.resultingStateVersion > response.stateVersion) {
      addTeacherIssue(context, "Intervention cannot be newer than the response Game version", [
        "data",
        "interventions",
        index,
        "resultingStateVersion"
      ]);
    }
  });
});
var teacherPauseBodySchema = z20.object({
  stateVersion: stateVersionSchema,
  reasonCode: teacherInterventionReasonCodeSchema,
  internalReason: teacherInternalReasonSchema,
  publicMessage: teacherPublicMessageSchema.optional()
}).strict();
var teacherResumeBodySchema = z20.object({
  stateVersion: stateVersionSchema,
  pauseInterventionId: identifierSchema,
  internalReason: teacherInternalReasonSchema
}).strict();
var teacherCountingResolutionBodySchema = z20.object({
  stateVersion: stateVersionSchema,
  countingRequestId: identifierSchema,
  resolution: teacherCountingResolutionSchema,
  reasonCode: teacherInterventionReasonCodeSchema,
  internalReason: teacherInternalReasonSchema
}).strict();
var teacherScoreAdjudicationBodySchema = z20.object({
  stateVersion: stateVersionSchema,
  scoringSessionId: identifierSchema,
  deadStoneAnchors: z20.array(boardPointSchema).max(MAX_DEAD_STONE_ANCHORS),
  reasonCode: teacherInterventionReasonCodeSchema,
  internalReason: teacherInternalReasonSchema
}).strict();
var teacherForfeitBodySchema = z20.object({
  stateVersion: stateVersionSchema,
  loser: stoneColorSchema,
  reasonCode: teacherInterventionReasonCodeSchema,
  internalReason: teacherInternalReasonSchema
}).strict();
var teacherResultCorrectionBodySchema = z20.object({
  stateVersion: stateVersionSchema,
  winner: stoneColorSchema,
  reasonCode: teacherInterventionReasonCodeSchema,
  internalReason: teacherInternalReasonSchema
}).strict();
var teacherNoteBodySchema = z20.object({
  stateVersion: stateVersionSchema,
  note: teacherNoteSchema
}).strict();
var teacherGameMutationResponseSchema = z20.object({
  requestId: requestIdSchema,
  gameId: identifierSchema,
  stateVersion: stateVersionSchema,
  serverTimestamp: serverTimestampSchema,
  data: z20.object({
    interventionId: identifierSchema,
    snapshot: phase6TimedGameSnapshotSchema
  }).strict()
}).strict().superRefine((response, context) => {
  if (response.gameId !== response.data.snapshot.gameId || response.stateVersion !== response.data.snapshot.stateVersion) {
    addTeacherIssue(context, "Teacher mutation envelope must match its authoritative snapshot", [
      "data",
      "snapshot"
    ]);
  }
});
var teacherNoteResponseSchema = z20.object({
  requestId: requestIdSchema,
  gameId: identifierSchema,
  stateVersion: stateVersionSchema,
  serverTimestamp: serverTimestampSchema,
  data: z20.object({
    noteId: identifierSchema,
    interventionId: identifierSchema,
    stateVersion: stateVersionSchema,
    positionRevision: z20.number().int().safe().nonnegative(),
    currentSequence: z20.number().int().safe().nonnegative(),
    createdAt: serverTimestampSchema
  }).strict()
}).strict().superRefine((response, context) => {
  if (response.stateVersion !== response.data.stateVersion) {
    addTeacherIssue(context, "A teacher note must expose its exact Game version anchor", [
      "data",
      "stateVersion"
    ]);
  }
});
var teacherWatchCommandSchema = z20.object({
  event: z20.literal("teacher.game.watch"),
  requestId: requestIdSchema,
  gameId: identifierSchema,
  payload: z20.object({}).strict()
}).strict();
var teacherSyncRequestCommandSchema = z20.object({
  event: z20.literal("teacher.game.sync.request"),
  requestId: requestIdSchema,
  gameId: identifierSchema,
  stateVersion: stateVersionSchema,
  payload: z20.object({}).strict()
}).strict();
var teacherUnwatchCommandSchema = z20.object({
  event: z20.literal("teacher.game.unwatch"),
  requestId: requestIdSchema,
  gameId: identifierSchema,
  payload: z20.object({}).strict()
}).strict();
var teacherClientCommandEnvelopeSchema = z20.discriminatedUnion("event", [
  teacherWatchCommandSchema,
  teacherSyncRequestCommandSchema,
  teacherUnwatchCommandSchema
]);
var teacherControlEnvelopeShape = {
  eventId: eventIdSchema,
  requestId: requestIdSchema,
  gameId: identifierSchema,
  stateVersion: stateVersionSchema,
  serverTimestamp: serverTimestampSchema
};
var teacherGameWatchedEventSchema = z20.object({
  event: z20.literal("teacher.game.watched"),
  ...teacherControlEnvelopeShape,
  payload: z20.object({
    snapshot: phase6TimedGameSnapshotSchema,
    accessLevel: teacherAccessLevelSchema,
    leaseExpiresAt: serverTimestampSchema
  }).strict()
}).strict().superRefine((event, context) => {
  if (event.gameId !== event.payload.snapshot.gameId || event.stateVersion !== event.payload.snapshot.stateVersion) {
    addTeacherIssue(context, "Watched envelope must match its authoritative snapshot", [
      "payload",
      "snapshot"
    ]);
  }
  const leaseDurationMs = Date.parse(event.payload.leaseExpiresAt) - Date.parse(event.serverTimestamp);
  if (leaseDurationMs < 15e3 || leaseDurationMs > 3e5) {
    addTeacherIssue(context, "A watch lease must be bounded between 15 and 300 seconds", [
      "payload",
      "leaseExpiresAt"
    ]);
  }
});
var teacherWatchRevokedEventSchema = z20.object({
  event: z20.literal("teacher.watch.revoked"),
  ...teacherControlEnvelopeShape,
  payload: z20.object({
    reason: z20.enum(["AUTHORIZATION_REVOKED", "MEMBERSHIP_INACTIVE", "LEASE_RENEWAL_FAILED"])
  }).strict()
}).strict();
var teacherServerEventEnvelopeSchema = z20.union([serverEventEnvelopeSchema, teacherGameWatchedEventSchema, teacherWatchRevokedEventSchema]).superRefine((event, context) => {
  if ("snapshot" in event.payload && event.payload.snapshot.supervision === void 0) {
    addTeacherIssue(context, "A teacher snapshot event must expose supervision", [
      "payload",
      "snapshot",
      "supervision"
    ]);
  }
});

// src/student-activity.ts
import { z as z21 } from "zod";
var studentActivityPageQuerySchema = z21.object({
  page: z21.coerce.number().int().min(1).max(1e5).default(1),
  pageSize: z21.coerce.number().int().min(1).max(50).default(10)
}).strict();
var envelope = { requestId: requestIdSchema, serverTimestamp: serverTimestampSchema };
var pageFields = {
  total: z21.number().int().nonnegative(),
  page: z21.number().int().positive(),
  pageSize: z21.number().int().min(1).max(50)
};
var playerSchema = z21.object({ membershipId: identifierSchema, displayName: z21.string(), color: stoneColorSchema }).strict();
var studentWatchGamesResponseSchema = z21.object({
  ...envelope,
  data: z21.object({
    ...pageFields,
    games: z21.array(
      z21.object({
        gameId: identifierSchema,
        stateVersion: stateVersionSchema,
        status: gameStatusSchema,
        boardSize: boardSizeSchema,
        currentSequence: z21.number().int().nonnegative(),
        players: z21.array(playerSchema)
      }).strict()
    )
  }).strict()
}).strict();
var studentRoomMemberSchema = z21.object({
  membershipId: identifierSchema,
  displayName: z21.string(),
  role: z21.enum(["BLACK", "WHITE", "OBSERVER"]),
  online: z21.boolean()
}).strict();
var studentRoomMembersResponseSchema = z21.object({
  ...envelope,
  gameId: identifierSchema,
  stateVersion: stateVersionSchema,
  data: z21.object({ members: z21.array(studentRoomMemberSchema) }).strict()
}).strict();
var studentObservationBodySchema = z21.object({ viewerId: z21.uuid() }).strict();
var studentObservationResponseSchema = z21.object({
  ...envelope,
  gameId: identifierSchema,
  stateVersion: stateVersionSchema,
  data: z21.object({ snapshot: timedGameSnapshotSchema }).strict()
}).strict();
var studentHomeworkProgressResponseSchema = z21.object({
  ...envelope,
  data: z21.object({
    ...pageFields,
    homeworkId: identifierSchema,
    participants: z21.array(
      z21.object({
        membershipId: identifierSchema,
        displayName: z21.string(),
        completedProblems: z21.number().int().nonnegative(),
        totalProblems: z21.number().int().nonnegative(),
        status: z21.enum(["NOT_STARTED", "IN_PROGRESS", "COMPLETED"])
      }).strict()
    )
  }).strict()
}).strict();

// src/pro-research.ts
import { z as z22 } from "zod";
var day = z22.iso.date();
var researchQuerySchema = z22.object({
  mode: z22.enum(["EVENT", "PLAYER"]).default("EVENT"),
  sourceId: z22.enum(["cwi-agon", "public-import"]).default("cwi-agon"),
  startDate: day,
  endDate: day,
  event: z22.string().max(100).default("Agon"),
  edition: z22.string().max(30).default(""),
  stage: z22.enum(["ALL", "MAIN", "PRELIMINARY", "UNKNOWN"]).default("ALL"),
  playerId: z22.string().max(100).default(""),
  color: z22.enum(["ALL", "BLACK", "WHITE"]).default("ALL"),
  window: z22.union([z22.literal(10), z22.literal(20), z22.literal(30), z22.literal(40), z22.literal(50)]).default(50),
  symmetry: z22.boolean().default(true),
  localSize: z22.union([z22.literal(7), z22.literal(9)]).default(7),
  minSupportGames: z22.number().int().min(1).max(1e5).default(10),
  minEligibleGames: z22.number().int().min(1).max(1e5).default(20),
  frequencyThreshold: z22.number().min(0).max(1).default(0.6),
  topK: z22.number().int().min(1).max(100).default(20),
  shortGamePolicy: z22.literal("EXCLUDE").default("EXCLUDE")
}).strict().refine((q) => q.startDate <= q.endDate, "\u5F00\u59CB\u65E5\u671F\u4E0D\u80FD\u665A\u4E8E\u7ED3\u675F\u65E5\u671F").refine((q) => q.mode !== "PLAYER" || q.playerId.length > 0, "\u8BF7\u9009\u62E9\u5DF2\u786E\u8BA4\u68CB\u624B");
var researchCollectionSchema = z22.object({ query: researchQuerySchema, budget: z22.number().int().min(1).max(500).default(100) }).strict();
var researchSourceSettingsSchema = z22.object({ enabled: z22.boolean(), allowSgfExport: z22.boolean() }).strict();
var researchAliasSchema = z22.object({
  playerId: z22.string().min(1).max(100),
  displayName: z22.string().min(1).max(100),
  alias: z22.string().min(1).max(100),
  sourceId: z22.enum(["cwi-agon", "public-import"]),
  sourcePersonId: z22.string().max(100).default("")
}).strict();
var researchImportFieldsSchema = z22.object({
  event: z22.string().min(1).max(100),
  edition: z22.string().max(30).default(""),
  stage: z22.enum(["MAIN", "PRELIMINARY", "UNKNOWN"]).default("UNKNOWN"),
  publicRightsConfirmed: z22.literal("true"),
  mainlineConfirmed: z22.enum(["true", "false"]).default("false")
}).strict();
var researchPointSchema = z22.object({ x: z22.number().int(), y: z22.number().int() });
var researchMoveSchema = z22.object({
  color: z22.enum(["BLACK", "WHITE"]),
  point: researchPointSchema.nullable()
});
var researchDateSchema = z22.object({
  raw: z22.string(),
  precision: z22.enum(["DAY", "MULTIDAY", "MONTH", "YEAR", "UNKNOWN", "CONFLICT"]),
  days: z22.array(z22.string()),
  displayDate: z22.string().nullable(),
  policy: z22.string()
});
var researchRecordSchema = z22.object({
  gameId: z22.string(),
  versionId: z22.string(),
  sourceId: z22.string(),
  provenanceSourceIds: z22.array(z22.string()).default([]),
  sourceRecordId: z22.string(),
  detailUrl: z22.string(),
  downloadUrl: z22.string(),
  fetchedAt: z22.string(),
  fileHash: z22.string(),
  date: researchDateSchema,
  event: z22.string(),
  edition: z22.string(),
  stage: z22.string(),
  round: z22.string(),
  blackName: z22.string(),
  whiteName: z22.string(),
  blackId: z22.string().nullable(),
  whiteId: z22.string().nullable(),
  rules: z22.string(),
  komi: z22.string(),
  result: z22.string(),
  encoding: z22.string(),
  moves: z22.array(researchMoveSchema),
  issues: z22.array(z22.string()),
  eligible: z22.boolean(),
  rawProperties: z22.record(z22.string(), z22.array(z22.string())),
  fullLineKey: z22.string()
});
var researchHitSchema = z22.object({
  gameId: z22.string(),
  versionId: z22.string(),
  moveNumber: z22.number(),
  transform: z22.number(),
  corner: z22.number().nullable(),
  globalMoves: z22.array(z22.number()),
  outsideInsertion: z22.boolean()
});
var researchPatternSchema = z22.object({
  id: z22.string(),
  kind: z22.enum(["PREFIX", "SHAPE", "LOCAL_SHAPE", "LOCAL_SEQUENCE", "RESPONSE", "LOCAL_RESPONSE"]),
  key: z22.string(),
  parentKey: z22.string().nullable(),
  depth: z22.number(),
  label: z22.string(),
  k: z22.number(),
  n: z22.number(),
  rate: z22.number(),
  highFrequency: z22.boolean(),
  insufficient: z22.boolean(),
  numerator: z22.array(z22.string()),
  denominator: z22.array(z22.string()),
  hits: z22.array(researchHitSchema),
  stones: z22.array(z22.enum(["BLACK", "WHITE"]).nullable()),
  nextMove: researchMoveSchema.nullable(),
  localSize: z22.number().nullable(),
  notes: z22.array(z22.string())
});
var researchGroupSchema = z22.object({
  color: z22.enum(["ALL", "BLACK", "WHITE"]),
  gameIds: z22.array(z22.string()),
  windowEligible: z22.array(z22.string()),
  patterns: z22.array(researchPatternSchema),
  patternTotal: z22.number().default(0),
  highFrequencyCount: z22.number().default(0),
  ownMoves: z22.array(
    z22.object({ point: researchPointSchema, games: z22.array(z22.string()), count: z22.number() })
  ),
  prefixStops: z22.array(
    z22.object({
      parentKey: z22.string(),
      ended: z22.number(),
      missing: z22.number(),
      windowCutoff: z22.number()
    })
  )
});
var researchReportSchema = z22.object({
  id: z22.string(),
  algorithmVersion: z22.string(),
  datasetVersion: z22.string(),
  generatedAt: z22.string(),
  query: researchQuerySchema,
  timezone: z22.literal("Asia/Shanghai"),
  datePolicy: z22.string(),
  groups: z22.array(researchGroupSchema),
  records: z22.array(researchRecordSchema),
  excluded: z22.number(),
  coverage: z22.string(),
  warnings: z22.array(z22.string()),
  denominatorDefinition: z22.string()
});
var researchStatisticsExportSchema = researchReportSchema.extend({
  records: z22.array(
    researchRecordSchema.omit({ moves: true, rawProperties: true, fullLineKey: true })
  )
});
var researchJobSchema = z22.object({
  id: z22.string(),
  kind: z22.enum(["COLLECT", "ANALYZE", "IMPORT"]),
  status: z22.enum(["QUEUED", "RUNNING", "PAUSED", "COMPLETED", "PARTIAL", "FAILED", "CANCELLED"]),
  stateVersion: z22.number(),
  requestId: z22.string(),
  createdAt: z22.string(),
  updatedAt: z22.string(),
  progress: z22.object({
    candidates: z22.number(),
    downloaded: z22.number(),
    cacheHits: z22.number().default(0),
    uniqueGames: z22.number(),
    duplicates: z22.number(),
    parseErrors: z22.number(),
    excluded: z22.number(),
    valid: z22.number(),
    failed: z22.number(),
    pending: z22.number(),
    requests: z22.number(),
    total: z22.number().nullable()
  }),
  failures: z22.array(z22.object({ recordId: z22.string(), reason: z22.string() })),
  message: z22.string(),
  reportId: z22.string().nullable()
});
var researchCatalogSchema = z22.object({
  canManage: z22.boolean(),
  sources: z22.array(
    z22.object({
      id: z22.string(),
      name: z22.string(),
      enabled: z22.boolean(),
      allowSgfExport: z22.boolean(),
      termsUrl: z22.string(),
      status: z22.string(),
      scope: z22.string()
    })
  ),
  players: z22.array(z22.object({ id: z22.string(), name: z22.string(), aliases: z22.array(z22.string()) })),
  jobs: z22.array(researchJobSchema),
  reports: z22.array(z22.object({ id: z22.string(), createdAt: z22.string() }))
});
var researchReplaySchema = z22.object({
  record: researchRecordSchema,
  frames: z22.array(
    z22.object({
      stones: z22.array(z22.enum(["BLACK", "WHITE"]).nullable()),
      lastMove: researchPointSchema.nullable()
    })
  ),
  sources: z22.array(
    z22.object({
      versionId: z22.string(),
      sourceId: z22.string(),
      sourceRecordId: z22.string(),
      downloadUrl: z22.string(),
      fileHash: z22.string(),
      selected: z22.boolean()
    })
  )
});
function researchEnvelope(data) {
  return z22.object({
    data,
    requestId: z22.string(),
    serverTime: z22.string(),
    stateVersion: z22.number(),
    gameId: z22.string().nullable()
  });
}
var researchReviewSchema = z22.object({ reason: z22.string().trim().min(5).max(400) }).strict();

// src/fox-research.ts
import { z as z23 } from "zod";
var foxResearchScopeSchema = z23.object({
  target: z23.enum(["ACCOUNT", "BADGES"]).default("ACCOUNT"),
  accountQuery: z23.string().trim().max(64).default(""),
  badges: z23.array(z23.enum(["6", "A", "P"])).min(1).max(3).default(["6", "A"]),
  startDate: z23.iso.date(),
  endDate: z23.iso.date(),
  color: z23.enum(["ALL", "BLACK", "WHITE"]).default("ALL"),
  window: z23.union([z23.literal(10), z23.literal(20), z23.literal(30), z23.literal(40), z23.literal(50)]).default(50)
}).strict().refine((s) => s.startDate <= s.endDate, "\u5F00\u59CB\u65E5\u671F\u4E0D\u80FD\u665A\u4E8E\u7ED3\u675F\u65E5\u671F").refine(
  (s) => Date.parse(s.endDate) - Date.parse(s.startDate) <= 365 * 864e5,
  "\u65E5\u671F\u8303\u56F4\u6700\u591A 366 \u5929\uFF08\u542B\u9996\u5C3E\uFF09"
).refine((s) => new Set(s.badges).size === s.badges.length, "\u6807\u8BB0\u4E0D\u80FD\u91CD\u590D");
var foxResearchPreviewSchema = z23.object({
  kind: z23.literal("FOX_RESEARCH_SCOPE_PREVIEW"),
  version: z23.literal("fox-scope-v1"),
  generatedAt: z23.iso.datetime(),
  timezone: z23.literal("Asia/Shanghai"),
  scope: foxResearchScopeSchema,
  status: z23.literal("BLOCKED"),
  canCollect: z23.literal(false),
  resolvedAccountId: z23.null(),
  rosterTotal: z23.null(),
  gameTotal: z23.null(),
  colorGroups: z23.array(z23.enum(["BLACK", "WHITE"])),
  blockers: z23.array(z23.object({ code: z23.string(), message: z23.string() })),
  notes: z23.array(z23.string()),
  sources: z23.array(
    z23.object({ title: z23.string(), url: z23.url(), checkedOn: z23.iso.date(), finding: z23.string() })
  )
});

// src/ebooks.ts
import { z as z24 } from "zod";
var EBOOK_CAPACITY = {
  arrangedQuestions: 2e3,
  draftQuestions: 6e3,
  relations: 12e3
};
var text = z24.string().max(8e3);
var ebookPointSchema = z24.object({
  x: z24.number().int().min(0).max(18),
  y: z24.number().int().min(0).max(18)
});
var ebookColorSchema = z24.enum(["BLACK", "WHITE"]);
var ebookMoveSchema = z24.object({
  color: ebookColorSchema,
  point: ebookPointSchema.nullable()
});
var ebookNodeSchema = z24.object({
  id: z24.string().max(80),
  parentId: z24.string().max(80).nullable(),
  move: ebookMoveSchema,
  outcome: z24.enum(["CONTINUE", "CORRECT", "INCORRECT"]),
  comment: text
});
var ebookRegionSchema = z24.object({
  x: z24.number().min(0),
  y: z24.number().min(0),
  width: z24.number().positive(),
  height: z24.number().positive()
});
var ebookPageGeometrySchema = z24.object({
  width: z24.number().positive(),
  height: z24.number().positive(),
  rotation: z24.number()
});
var ebookQuestionSchema = z24.object({
  id: z24.string().min(1).max(80),
  revision: z24.number().int().positive(),
  candidateId: z24.string().max(100).nullable(),
  title: z24.string().max(200),
  number: z24.string().max(40),
  text,
  goal: text,
  difficulty: z24.string().max(80),
  category: z24.string().max(80),
  chapter: z24.string().max(120),
  hint: text,
  explanation: text,
  notes: text,
  source: z24.object({
    pageIndex: z24.number().int().nonnegative(),
    printedPage: z24.string().max(40),
    region: ebookRegionSchema,
    rotation: z24.number(),
    recognitionVersion: z24.string().max(80)
  }).nullable(),
  role: z24.enum(["PROBLEM", "ANSWER", "VARIATION", "EXAMPLE", "UNKNOWN"]),
  size: z24.union([z24.literal(9), z24.literal(13), z24.literal(19)]),
  currentPlayer: ebookColorSchema,
  visible: z24.object({
    x: z24.number().int().min(0).max(18),
    y: z24.number().int().min(0).max(18),
    columns: z24.number().int().min(2).max(19),
    rows: z24.number().int().min(2).max(19)
  }),
  geometryConfirmed: z24.boolean(),
  outside: z24.enum(["UNKNOWN", "CONFIRMED_EMPTY", "COMPLETE"]),
  assumption: text,
  stones: z24.array(ebookColorSchema.nullable()).max(361),
  uncertain: z24.array(ebookPointSchema).max(361),
  marks: z24.array(
    z24.object({
      point: ebookPointSchema,
      kind: z24.enum(["LABEL", "TRIANGLE", "CIRCLE", "SQUARE", "CROSS"]),
      label: z24.string().max(12)
    })
  ).max(361),
  faceConfirmed: z24.boolean(),
  answerConfirmed: z24.boolean(),
  answerSource: text,
  closedWorld: z24.boolean(),
  mode: z24.enum(["MANUAL", "AUTO"]),
  nodes: z24.array(ebookNodeSchema).max(1e3)
});
var ebookLayoutSchema = z24.object({
  title: z24.string().min(1).max(200),
  introduction: text,
  cover: z24.string().max(200),
  coverImage: z24.number().int().positive().nullable().optional(),
  totalQuestionCount: z24.number().int().positive().optional(),
  chapters: z24.array(
    z24.string().trim().min(1, "\u7AE0\u8282\u540D\u79F0\u4E0D\u80FD\u4E3A\u7A7A").max(120, "\u7AE0\u8282\u540D\u79F0\u4E0D\u80FD\u8D85\u8FC7 120 \u4E2A\u5B57\u7B26").refine((name) => !["\u672A\u5206\u7AE0", "\u672A\u5206\u914D\u7AE0\u8282"].includes(name), "\u4E0D\u80FD\u4F7F\u7528\u672A\u5206\u7AE0\u4FDD\u7559\u540D\u79F0")
  ).max(500).refine((names) => new Set(names).size === names.length, "\u7AE0\u8282\u540D\u79F0\u4E0D\u80FD\u91CD\u590D").optional(),
  questionIds: z24.array(z24.string().max(80)).max(EBOOK_CAPACITY.arrangedQuestions),
  density: z24.union([z24.literal(1), z24.literal(2), z24.literal(3)]),
  fontSize: z24.number().int().min(14).max(24),
  numberStyle: z24.enum(["SOURCE", "SEQUENTIAL"])
});
var ebookWorkspaceSchema = z24.object({
  schemaVersion: z24.literal(1),
  relations: z24.array(
    z24.object({
      sourceId: z24.string(),
      targetId: z24.string().nullable(),
      kind: z24.enum(["ANSWER", "VARIATION", "CONTINUATION"]),
      status: z24.enum(["CANDIDATE", "CONFIRMED"]),
      reason: z24.string().max(1e3)
    })
  ).max(EBOOK_CAPACITY.relations).default([]),
  questions: z24.array(ebookQuestionSchema).max(EBOOK_CAPACITY.draftQuestions),
  book: ebookLayoutSchema
});
var ebookCandidateSchema = z24.object({
  id: z24.string(),
  pageIndex: z24.number().int(),
  region: ebookRegionSchema,
  columns: z24.number().int(),
  rows: z24.number().int(),
  stones: z24.array(ebookColorSchema.nullable()),
  uncertain: z24.array(ebookPointSchema),
  marks: z24.array(
    z24.object({
      point: ebookPointSchema,
      kind: z24.enum(["LABEL", "TRIANGLE", "CIRCLE", "SQUARE", "CROSS"]),
      label: z24.string()
    })
  ),
  text: z24.string(),
  role: z24.enum(["PROBLEM", "ANSWER", "VARIATION", "EXAMPLE", "UNKNOWN"]),
  method: z24.string(),
  risks: z24.array(z24.string()),
  score: z24.number(),
  completeness: z24.object({ clippedSides: z24.array(z24.enum(["left", "right", "top", "bottom"])).max(4) }).optional(),
  geometry: z24.object({
    edges: z24.object({
      left: z24.enum(["EDGE", "OPEN", "UNKNOWN"]),
      right: z24.enum(["EDGE", "OPEN", "UNKNOWN"]),
      top: z24.enum(["EDGE", "OPEN", "UNKNOWN"]),
      bottom: z24.enum(["EDGE", "OPEN", "UNKNOWN"])
    }),
    horizontal: z24.enum(["LEFT", "RIGHT"]).nullable(),
    vertical: z24.enum(["TOP", "BOTTOM"]).nullable(),
    evidence: z24.record(
      z24.string(),
      z24.object({
        samples: z24.number(),
        continuing: z24.number(),
        stopping: z24.number()
      })
    ),
    reason: z24.string()
  }).optional(),
  grid: z24.object({
    x: z24.number(),
    y: z24.number(),
    dx: z24.number(),
    dy: z24.number(),
    angle: z24.number()
  }),
  evidence: z24.array(
    z24.object({
      x: z24.number(),
      y: z24.number(),
      black: z24.number(),
      ring: z24.number(),
      risk: z24.boolean()
    })
  ).max(361)
});
var ebookRecognitionModeSchema = z24.enum(["AUTO", "ORIGINAL", "FAINT", "BLEED"]);
var ebookOptimizationSchema = z24.object({
  profile: z24.literal("scan-optimize-v1"),
  requestedMode: ebookRecognitionModeSchema,
  appliedMode: z24.enum(["ORIGINAL", "FAINT", "BLEED"]),
  compared: z24.array(
    z24.object({
      mode: z24.enum(["ORIGINAL", "FAINT", "BLEED"]),
      candidates: z24.number().int().nonnegative(),
      uncertain: z24.number().int().nonnegative(),
      rejection: z24.enum([
        "GRID_MISMATCH",
        "STONE_CONFLICT",
        "SEPARATE_BOARDS",
        "WEAK_CANDIDATE",
        "NO_IMPROVEMENT"
      ]).optional()
    })
  ).min(1).max(3),
  reason: z24.enum(["RETAINED_ORIGINAL", "PROCESSING_BUDGET"]).optional()
});
var ebookPageResultSchema = z24.object({
  candidates: z24.array(ebookCandidateSchema),
  text: z24.string(),
  width: z24.number(),
  height: z24.number(),
  rotation: z24.number(),
  cropBox: z24.array(z24.number()).length(4),
  elapsedMs: z24.number(),
  peakRssKb: z24.number(),
  algorithm: z24.string(),
  enhancement: z24.enum(["scan-clarity-v1", "scan-optimize-v1"]).optional(),
  optimization: ebookOptimizationSchema.optional()
});
var ebookPageSchema = z24.object({
  index: z24.number(),
  status: z24.enum(["QUEUED", "RUNNING", "DONE", "FAILED", "SKIPPED"]),
  attempts: z24.number(),
  enhanceClarity: z24.boolean().optional(),
  recognitionMode: ebookRecognitionModeSchema.optional(),
  error: z24.string().nullable(),
  result: ebookPageResultSchema.nullable()
});
var ebookDocumentSchema = z24.object({
  id: z24.string(),
  name: z24.string(),
  hash: z24.string(),
  pageCount: z24.number(),
  stateVersion: z24.number(),
  cancelled: z24.boolean(),
  data: ebookWorkspaceSchema,
  pages: z24.array(ebookPageSchema),
  updatedAt: z24.string()
});
var ebookDocumentIndexSchema = ebookDocumentSchema.omit({ data: true }).extend({
  book: ebookLayoutSchema,
  draftCount: z24.number().int().nonnegative(),
  chapters: z24.array(z24.object({ name: z24.string(), count: z24.number().int().nonnegative() }))
});
var ebookDirectoryQuerySchema = z24.object({
  page: z24.coerce.number().int().min(1).default(1),
  pageSize: z24.coerce.number().int().min(1).max(100).default(24),
  search: z24.string().trim().max(200).default(""),
  chapter: z24.string().max(120).optional(),
  sourcePage: z24.coerce.number().int().min(0).max(499).optional(),
  scope: z24.enum(["ALL", "ARRANGED"]).default("ALL"),
  expectedVersion: z24.coerce.number().int().positive().optional()
});
var ebookDirectoryItemSchema = ebookQuestionSchema.pick({
  id: true,
  revision: true,
  title: true,
  number: true,
  chapter: true,
  role: true,
  size: true,
  currentPlayer: true,
  visible: true,
  stones: true,
  mode: true,
  faceConfirmed: true,
  answerConfirmed: true
}).extend({ sourcePage: z24.number().int().nonnegative().nullable(), arranged: z24.boolean() });
var ebookDirectoryPageSchema = z24.object({
  stateVersion: z24.number().int().positive(),
  page: z24.number().int().positive(),
  pageSize: z24.number().int().positive(),
  total: z24.number().int().nonnegative(),
  items: z24.array(ebookDirectoryItemSchema)
});
var ebookQuestionDetailSchema = z24.object({
  documentId: z24.string(),
  stateVersion: z24.number().int().positive(),
  question: ebookQuestionSchema
});
var ebookSaveQuestionSchema = z24.object({
  expectedVersion: z24.number().int().positive(),
  question: ebookQuestionSchema,
  newChapter: z24.string().trim().min(1).max(120).optional()
});
var ebookArrangementSchema = z24.object({
  expectedVersion: z24.number().int().positive(),
  questionId: z24.string().min(1).max(80),
  included: z24.boolean()
});
var ebookArrangementResultSchema = z24.object({
  stateVersion: z24.number().int().positive(),
  questionIds: ebookLayoutSchema.shape.questionIds
});
var ebookLimitsSchema = z24.object({
  maxBytes: z24.number(),
  maxPages: z24.number(),
  maxPixels: z24.number(),
  pageTimeoutMs: z24.number(),
  maxPagesPerRun: z24.number(),
  recognitionAvailable: z24.boolean(),
  message: z24.string(),
  storageAvailable: z24.boolean().default(true),
  storageMessage: z24.string().default("")
});
var ebookCoverUploadSchema = z24.object({
  expectedVersion: z24.coerce.number().int().positive()
});
var ebookDeleteDocumentSchema = z24.object({
  expectedVersion: z24.number().int().positive(),
  confirmed: z24.literal(true)
});
var ebookDeleteDocumentResultSchema = z24.object({
  id: z24.string(),
  deleted: z24.literal(true)
});
var ebookCoverUploadResultSchema = z24.object({
  id: z24.string(),
  coverImage: z24.number().int().positive()
});
var ebookShelfItemSchema = z24.object({
  id: z24.string(),
  name: z24.string(),
  pageCount: z24.number(),
  stateVersion: z24.number(),
  updatedAt: z24.string(),
  coverImage: z24.number().int().positive().nullable().optional(),
  coverReleaseId: z24.string().nullable().optional(),
  // Catalog visibility is independent of permission to read a publication.
  accessStatus: z24.enum(["AVAILABLE", "LOCKED"]).optional(),
  coverDocumentId: z24.string().nullable().optional(),
  // Teacher-only draft authoring metadata, independent of student learning progress.
  authoringProgress: z24.object({
    arrangedCount: z24.number().int().nonnegative(),
    totalCount: z24.number().int().positive().nullable()
  }).optional(),
  // Presence advertises the paged authoring endpoints to upgraded web clients.
  draftCount: z24.number().int().nonnegative().optional(),
  // Distinct submitted questions in the latest authorized contents; absent on older servers.
  completedCount: z24.number().int().nonnegative().optional(),
  // Questions currently DUE under the same review projection used inside the book.
  pendingReviewCount: z24.number().int().nonnegative().optional()
});
var ebookReleaseSchema = z24.object({
  id: z24.string(),
  documentId: z24.string(),
  version: z24.number(),
  book: ebookLayoutSchema,
  questions: z24.array(ebookQuestionSchema),
  createdAt: z24.string()
});
var ebookPublicationSchema = z24.object({
  id: z24.string(),
  version: z24.number().int(),
  createdAt: z24.string(),
  questionIds: z24.array(z24.string()),
  classIds: z24.array(z24.string()),
  studentIds: z24.array(z24.string())
});
var ebookReadingSchema = z24.object({
  releaseId: z24.string(),
  studentId: z24.string(),
  questionId: z24.string().nullable(),
  scrollY: z24.number()
});
var ebookAttemptSchema = z24.object({
  id: z24.string(),
  releaseId: z24.string(),
  questionId: z24.string(),
  questionRevision: z24.number(),
  stateVersion: z24.number(),
  board: z24.object({
    stones: z24.array(ebookColorSchema.nullable()).max(361),
    currentPlayer: ebookColorSchema
  }),
  moves: z24.array(ebookMoveSchema).max(1e3),
  status: z24.enum(["CONTINUE", "CORRECT", "INCORRECT", "UNKNOWN", "PENDING_REVIEW"]),
  submitted: z24.boolean(),
  hinted: z24.boolean(),
  demonstrated: z24.boolean(),
  attemptNumber: z24.number(),
  feedback: z24.string(),
  studentId: z24.string(),
  updatedAt: z24.string(),
  // Optional for historical attempts; the server initializes a deadline on resume.
  timing: z24.object({
    startedAt: z24.iso.datetime(),
    deadlineAt: z24.iso.datetime(),
    finishedAt: z24.iso.datetime().nullable(),
    expired: z24.boolean()
  }).optional()
});
var ebookAttemptSummarySchema = z24.object({
  questionId: z24.string(),
  correct: z24.number().int().nonnegative(),
  incorrect: z24.number().int().nonnegative()
});
var ebookLearningAttemptSchema = ebookAttemptSchema.pick({
  id: true,
  releaseId: true,
  questionId: true,
  stateVersion: true,
  status: true,
  submitted: true,
  hinted: true,
  demonstrated: true
}).extend({
  startedAt: z24.iso.datetime(),
  finishedAt: z24.iso.datetime().nullable(),
  updatedAt: z24.iso.datetime()
});
var ebookQuestionProgressSchema = ebookAttemptSummarySchema.extend({
  completedCount: z24.number().int().nonnegative(),
  reviewCount: z24.number().int().nonnegative(),
  first: ebookLearningAttemptSchema.nullable(),
  latest: ebookLearningAttemptSchema,
  pending: ebookLearningAttemptSchema.nullable(),
  reviewState: z24.enum(["NONE", "DUE", "SCHEDULED", "MASTERED"]),
  nextReviewAt: z24.iso.datetime().nullable()
});
var ebookLearningProgressSchema = z24.object({
  asOf: z24.iso.datetime(),
  questions: z24.array(ebookQuestionProgressSchema),
  resume: ebookLearningAttemptSchema.nullable()
});
var EBOOK_TIME_LIMIT_SECONDS = 180;
var ebookAttemptPositionSchema = ebookAttemptSchema.pick({ board: true }).extend({
  ply: z24.number().int().nonnegative(),
  total: z24.number().int().nonnegative()
});
var ebookStudyQuerySchema = z24.object({
  page: z24.coerce.number().int().min(1).max(1e5).default(1),
  studentId: z24.string().max(80).optional(),
  questionId: z24.string().max(80).optional(),
  status: ebookAttemptSchema.shape.status.optional(),
  from: z24.iso.datetime().optional(),
  to: z24.iso.datetime().optional()
}).refine((q) => !q.from || !q.to || q.from <= q.to, "\u7ED3\u675F\u65F6\u95F4\u4E0D\u80FD\u65E9\u4E8E\u5F00\u59CB\u65F6\u95F4");
var ebookStudyRecordSchema = ebookAttemptSchema.pick({
  id: true,
  releaseId: true,
  questionId: true,
  studentId: true,
  stateVersion: true,
  status: true,
  submitted: true,
  attemptNumber: true,
  hinted: true,
  demonstrated: true
}).extend({
  studentName: z24.string(),
  questionTitle: z24.string(),
  questionNumber: z24.number().int().positive().nullable(),
  questionLabel: z24.string(),
  moveCount: z24.number().int().nonnegative(),
  startedAt: z24.iso.datetime(),
  finishedAt: z24.iso.datetime().nullable(),
  durationMs: z24.number().nonnegative().nullable()
});
var ebookStudyPageSchema = z24.object({
  items: z24.array(ebookStudyRecordSchema),
  page: z24.number().int().positive(),
  pageSize: z24.number().int().positive(),
  total: z24.number().int().nonnegative(),
  students: z24.array(z24.object({ id: z24.string(), name: z24.string() })),
  questions: z24.array(z24.object({ id: z24.string(), label: z24.string() }))
});
var ebookStudyReplaySchema = z24.object({
  record: ebookStudyRecordSchema,
  question: ebookQuestionSchema.pick({
    id: true,
    title: true,
    size: true,
    currentPlayer: true,
    marks: true,
    text: true,
    goal: true,
    mode: true
  }),
  moves: z24.array(ebookMoveSchema),
  position: ebookAttemptPositionSchema,
  nextMoves: z24.array(
    z24.object({
      point: ebookMoveSchema.shape.point.unwrap(),
      outcome: z24.enum(["CORRECT", "INCORRECT"])
    })
  ),
  playedNext: z24.object({
    move: ebookMoveSchema,
    outcome: z24.enum(["CORRECT", "INCORRECT", "UNKNOWN"]),
    byStudent: z24.boolean()
  }).nullable()
});
var ebookEnvelope = (schema) => z24.object({
  requestId: z24.string(),
  gameId: z24.null(),
  stateVersion: z24.number(),
  serverTimestamp: z24.string(),
  data: schema
});
var ebookUploadFieldsSchema = z24.object({
  filename: z24.string().max(250).optional(),
  password: z24.string().max(200).optional(),
  pages: z24.string().max(200).optional()
});
var ebookTrialSchema = z24.object({
  question: ebookQuestionSchema,
  moves: z24.array(ebookMoveSchema).max(1e3)
});
var ebookPracticePreviewSchema = ebookTrialSchema.extend({
  move: ebookMoveSchema.optional()
});
var ebookPracticePreviewResultSchema = ebookAttemptSchema.pick({
  board: true,
  moves: true,
  status: true
});
var ebookImportSgfSchema = z24.object({
  question: ebookQuestionSchema,
  sgf: z24.string().max(1e6)
});
var ebookReadingCommandSchema = z24.object({
  questionId: z24.string().max(80).nullable(),
  scrollY: z24.number().int().min(0).max(1e6)
});
var ebookCommandSchema = z24.discriminatedUnion("action", [
  z24.object({
    action: z24.literal("RENAME_CHAPTER"),
    expectedVersion: z24.number().int().positive(),
    currentName: z24.string().trim().min(1).max(120),
    nextName: z24.string().trim().min(1).max(120)
  }),
  z24.object({
    action: z24.literal("SET_QUESTION_TOTAL"),
    expectedVersion: z24.number().int().positive(),
    totalQuestionCount: z24.number().int().positive()
  }),
  z24.object({
    action: z24.literal("OPTIMIZE_PAGE"),
    expectedVersion: z24.number().int(),
    pageIndex: z24.number().int().min(0).max(499),
    mode: ebookRecognitionModeSchema
  }),
  z24.object({
    action: z24.literal("ENHANCE_PAGE"),
    expectedVersion: z24.number().int(),
    pageIndex: z24.number().int().min(0).max(499)
  }),
  z24.object({
    action: z24.literal("PROCESS_PAGES"),
    expectedVersion: z24.number().int(),
    pageIndexes: z24.array(z24.number().int().nonnegative()).min(1).max(500).refine((indexes) => new Set(indexes).size === indexes.length, "\u9875\u7D22\u5F15\u4E0D\u80FD\u91CD\u590D")
  }),
  z24.object({
    action: z24.literal("SAVE"),
    expectedVersion: z24.number().int(),
    data: ebookWorkspaceSchema
  }),
  z24.object({
    action: z24.enum(["CANCEL", "RESUME", "RETRY_FAILED"]),
    expectedVersion: z24.number().int()
  }),
  z24.object({
    action: z24.literal("RECOGNIZE"),
    expectedVersion: z24.number().int(),
    pageIndex: z24.number().int().nonnegative(),
    region: ebookRegionSchema.nullable()
  }),
  z24.object({
    action: z24.literal("PUBLISH"),
    expectedVersion: z24.number().int(),
    classIds: z24.array(z24.string().max(80)).min(1).max(30),
    studentIds: z24.array(z24.string().max(80)).max(1e3),
    rightsConfirmed: z24.literal(true)
  })
]);
var ebookQuestionTotalResultSchema = z24.object({
  id: z24.string(),
  stateVersion: z24.number().int().positive(),
  updatedAt: z24.iso.datetime(),
  authoringProgress: z24.object({
    arrangedCount: z24.number().int().nonnegative(),
    totalCount: z24.number().int().positive()
  })
});
var ebookPreviewQuerySchema = z24.object({
  source: z24.enum(["original", "enhanced"]).default("original")
});
var ebookAttemptCommandSchema = z24.discriminatedUnion("action", [
  z24.object({ action: z24.literal("START"), questionId: z24.string().max(80) }),
  z24.object({
    action: z24.literal("MOVE"),
    attemptId: z24.string(),
    expectedVersion: z24.number().int(),
    move: ebookMoveSchema
  }),
  z24.object({
    action: z24.enum(["SUBMIT", "HINT", "DEMONSTRATE", "SYNC"]),
    attemptId: z24.string(),
    expectedVersion: z24.number().int()
  }),
  z24.object({
    action: z24.literal("GRADE"),
    attemptId: z24.string(),
    expectedVersion: z24.number().int(),
    status: z24.enum(["CORRECT", "INCORRECT", "PENDING_REVIEW"]),
    feedback: text
  })
]);

// src/ebook-rankings.ts
import { z as z25 } from "zod";
var date = z25.string().regex(/^\d{4}-\d{2}-\d{2}$/).refine((value) => {
  const parsed = /* @__PURE__ */ new Date(`${value}T00:00:00.000Z`);
  return Number.isFinite(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value;
});
var ebookRankingQuerySchema = z25.object({
  period: z25.enum(["WEEK", "MONTH", "BOOK"]).default("WEEK"),
  classId: z25.string().min(1).max(80).optional(),
  releaseId: z25.string().min(1).max(80).optional(),
  date: date.optional(),
  page: z25.coerce.number().int().min(1).max(1e5).default(1),
  pageSize: z25.coerce.number().int().min(1).max(20).optional()
}).strict().refine((q) => q.period !== "BOOK" || !!q.releaseId, {
  message: "\u5355\u4E66\u6392\u540D\u9700\u8981\u9009\u62E9\u7535\u5B50\u4E66"
}).refine((q) => q.period !== "BOOK" || q.date === void 0, {
  message: "\u5355\u4E66\u6392\u540D\u4E3A\u7D2F\u8BA1\u7EDF\u8BA1"
});
var ebookRankingOptionsSchema = z25.object({
  classes: z25.array(z25.object({ id: z25.string(), name: z25.string() })),
  books: z25.array(
    z25.object({
      releaseId: z25.string(),
      title: z25.string(),
      questionCount: z25.number().int().nonnegative()
    })
  )
});
var ebookRankingStatsSchema = z25.object({
  firstCorrect: z25.number().int().nonnegative(),
  completed: z25.number().int().nonnegative(),
  accuracy: z25.number().min(0).max(100).nullable(),
  practiceDays: z25.number().int().nonnegative(),
  reviewed: z25.number().int().nonnegative(),
  pending: z25.number().int().nonnegative()
});
var ebookRankingRowSchema = ebookRankingStatsSchema.extend({
  rank: z25.number().int().positive().nullable(),
  name: z25.string(),
  isMe: z25.boolean()
});
var ebookRankingPageSchema = z25.object({
  asOf: z25.string().datetime(),
  period: z25.enum(["WEEK", "MONTH", "BOOK"]),
  start: z25.string().datetime().nullable(),
  end: z25.string().datetime().nullable(),
  classId: z25.string().nullable(),
  className: z25.string().nullable(),
  book: z25.object({
    releaseId: z25.string(),
    title: z25.string(),
    questionCount: z25.number().int().nonnegative()
  }).nullable(),
  participants: z25.number().int().nonnegative(),
  eligibleStudents: z25.number().int().nonnegative(),
  page: z25.number().int().positive(),
  pageSize: z25.number().int().positive(),
  items: z25.array(ebookRankingRowSchema),
  mine: ebookRankingRowSchema,
  legacyTimingExcluded: z25.boolean()
});

// src/lecture-records.ts
import { z as z26 } from "zod";
var lectureRecordIdSchema = z26.uuid();
var bounded = z26.number().finite().min(-1e4).max(11e3);
var lectureAnnotationSchema = z26.discriminatedUnion("kind", [
  z26.object({
    kind: z26.literal("BRUSH"),
    color: z26.enum(["RED", "BLUE"]),
    width: z26.number().min(1).max(30),
    points: z26.array(z26.object({ x: bounded, y: bounded }).strict()).min(1).max(2e3)
  }).strict(),
  z26.object({
    kind: z26.enum(["LETTER", "TRIANGLE", "CIRCLE", "CROSS"]),
    color: z26.enum(["RED", "BLUE"]),
    point: boardPointSchema,
    label: z26.string().max(8).nullable()
  }).strict()
]);
var lectureContentSchema = z26.array(
  z26.object({
    kind: z26.enum(["PARAGRAPH", "BULLET"]),
    runs: z26.array(z26.object({ text: z26.string().max(8e3), bold: z26.boolean() }).strict()).min(1).max(500)
  }).strict()
).max(500).refine(
  (blocks) => blocks.reduce((sum, b) => sum + b.runs.reduce((n, r) => n + r.text.length, 0), 0) <= 8e3,
  "\u5206\u6790\u6700\u591A8000\u5B57"
);
var lectureSnapshotSchema = z26.object({
  size: boardSizeSchema,
  stones: z26.array(stoneColorSchema.nullable()).min(81).max(361),
  nextColor: stoneColorSchema,
  source: z26.object({
    kind: z26.enum(["SGF", "FOX", "TEXTBOOK", "DIAGRAM"]),
    id: z26.string().min(1).max(160),
    version: z26.string().min(1).max(128),
    name: z26.string().min(1).max(240),
    gameId: z26.string().max(64).nullable()
  }).strict(),
  baseMove: z26.number().int().min(0).max(1e5),
  moveNumber: z26.number().int().min(0).max(1e5),
  variation: z26.array(boardPointSchema).max(2e3),
  labels: z26.array(
    z26.object({
      point: boardPointSchema,
      label: z26.string().regex(/^\d{1,6}$/),
      color: stoneColorSchema
    }).strict()
  ).max(361),
  initialAnnotations: problemBoardAnnotationsSchema,
  annotations: z26.array(lectureAnnotationSchema).max(200),
  capturedAt: serverTimestampSchema
}).strict().superRefine((s, ctx) => {
  const fail = (message) => ctx.addIssue({ code: "custom", message });
  const inside = (p) => p.x < s.size && p.y < s.size;
  if (s.stones.length !== s.size * s.size) fail("\u68CB\u76D8\u5C3A\u5BF8\u4E0E\u68CB\u5B50\u4E0D\u4E00\u81F4");
  if (new Set(s.labels.map((l) => `${l.point.x}:${l.point.y}`)).size !== s.labels.length)
    fail("\u7F16\u53F7\u4F4D\u7F6E\u91CD\u590D");
  for (const label of s.labels)
    if (!inside(label.point) || s.stones[label.point.y * s.size + label.point.x] !== label.color)
      fail("\u7F16\u53F7\u5FC5\u987B\u5BF9\u5E94\u5F53\u524D\u68CB\u5B50");
  for (const point of s.variation) if (!inside(point)) fail("\u53D8\u5316\u5750\u6807\u8D8A\u754C");
  for (const a of s.initialAnnotations)
    if (!inside(a.point) || "to" in a && !inside(a.to)) fail("\u6807\u8BB0\u5750\u6807\u8D8A\u754C");
  for (const a of s.annotations) if (a.kind !== "BRUSH" && !inside(a.point)) fail("\u6807\u8BB0\u5750\u6807\u8D8A\u754C");
});
function utf8Length(value) {
  let length = 0;
  for (const character of value) {
    const point = character.codePointAt(0);
    length += point <= 127 ? 1 : point <= 2047 ? 2 : point <= 65535 ? 3 : 4;
  }
  return length;
}
var lectureSaveBodySchema = z26.object({
  expectedVersion: z26.number().int().nonnegative(),
  title: z26.string().trim().max(120),
  content: lectureContentSchema,
  snapshot: lectureSnapshotSchema
}).strict().superRefine((v, ctx) => {
  if (!v.content.some((b) => b.runs.some((r) => r.text.trim())))
    ctx.addIssue({ code: "custom", message: "\u8BF7\u8F93\u5165\u5206\u6790\u6B63\u6587" });
  if (utf8Length(JSON.stringify(v)) > 48 * 1024)
    ctx.addIssue({ code: "custom", message: "\u8BB0\u5F55\u8FC7\u5927\uFF0C\u8BF7\u51CF\u5C11\u6807\u8BB0\u6216\u6B63\u6587" });
});
var lectureRecordSchema = z26.object({
  recordId: lectureRecordIdSchema,
  version: z26.number().int().positive(),
  title: z26.string(),
  content: lectureContentSchema,
  snapshot: lectureSnapshotSchema,
  createdAt: serverTimestampSchema,
  updatedAt: serverTimestampSchema
}).strict();
var lectureRecordSummarySchema = lectureRecordSchema.omit({ content: true, snapshot: true }).extend({
  sourceName: z26.string(),
  moveNumber: z26.number().int(),
  baseMove: z26.number().int(),
  variationCount: z26.number().int()
});
var lectureEnvelope = (data) => z26.object({
  requestId: requestIdSchema,
  gameId: z26.string().nullable(),
  stateVersion: z26.number().int().nonnegative(),
  serverTimestamp: serverTimestampSchema,
  data
}).strict();
var lectureRecordResponseSchema = lectureEnvelope(lectureRecordSchema);
var lectureListResponseSchema = lectureEnvelope(
  z26.object({
    records: z26.array(lectureRecordSummarySchema),
    nextCursor: lectureRecordIdSchema.nullable()
  }).strict()
);
var lectureExportBodySchema = z26.object({
  records: z26.array(
    z26.object({ recordId: lectureRecordIdSchema, version: z26.number().int().positive() }).strict()
  ).min(1).max(50)
}).strict().refine(
  (v) => new Set(v.records.map((r) => r.recordId)).size === v.records.length,
  "\u4E0D\u80FD\u91CD\u590D\u9009\u62E9\u8BB0\u5F55"
);
var lectureExportResponseSchema = lectureEnvelope(
  z26.object({ records: z26.array(lectureRecordSchema).max(50) }).strict()
);
var lectureOperationResponseSchema = lectureEnvelope(lectureRecordSchema.nullable());
export {
  AI_POSITION_SCHEMA_VERSION,
  AI_RESULT_SCHEMA_VERSION,
  CHINESE_AREA_KOMI,
  CHINESE_AREA_KOMI_HALF_POINTS,
  DEFAULT_AI_RETENTION_DAYS,
  DEFAULT_MAIN_TIME_MS,
  EBOOK_CAPACITY,
  EBOOK_TIME_LIMIT_SECONDS,
  ERROR_MESSAGES_ZH,
  GAME_DISCONNECTION_GRACE_MS,
  GAME_SGF_MEDIA_TYPE,
  GAME_WEBSOCKET_PROTOCOL,
  MATCHMAKING_PAGE_LIMIT,
  MATCHMAKING_TICKET_TTL_SECONDS,
  MAX_AI_ANALYSES_PAGE_SIZE,
  MAX_AI_BEST_MOVES,
  MAX_AI_CONCURRENT_ANALYSES,
  MAX_AI_CURSOR_LENGTH,
  MAX_AI_DAILY_ANALYSIS_LIMIT,
  MAX_AI_POSITION_OPTIONS_PAGE_SIZE,
  MAX_AI_PV_MOVES,
  MAX_AI_QUEUED_ANALYSES,
  MAX_AI_RETENTION_DAYS,
  MAX_AI_VISITS,
  MAX_ASSIGNMENT_PAGE_SIZE,
  MAX_AUTH_MEMBERSHIPS,
  MAX_CLASSES_PAGE_SIZE,
  MAX_CLASSROOM_SGF_BYTES,
  MAX_CLASS_MEMBERS_PAGE_SIZE,
  MAX_DEAD_STONE_ANCHORS,
  MAX_GAME_HISTORY_PAGE_SIZE,
  MAX_HOMEWORK_ANSWER_ELAPSED_MS,
  MAX_HOMEWORK_ATTEMPTS,
  MAX_HOMEWORK_ITEMS,
  MAX_HOMEWORK_TRACE_NODES,
  MAX_HOMEWORK_TRACE_NODE_MOVES,
  MAX_HOMEWORK_TRACE_STEPS,
  MAX_MAIN_TIME_MS,
  MAX_ORGANIZATION_MEMBERS_PAGE_SIZE,
  MAX_REVIEW_COMMENTS_PAGE_SIZE,
  MAX_SCORING_HALF_POINTS,
  MAX_SOLUTION_LINES,
  MAX_SOLUTION_LINE_MOVES,
  MAX_TIMING_SEGMENT_MOVES,
  MIN_AI_RETENTION_DAYS,
  MIN_MAIN_TIME_MS,
  PROBLEM_SCHEMA_VERSION,
  QUESTION_BANK_SECTIONS,
  QUESTION_REQUIREMENTS_MAX_LENGTH,
  QUESTION_REQUIREMENTS_MAX_RUNS,
  SOLUTION_TREE_SCHEMA_VERSION,
  WEBSOCKET_JWT_PROTOCOL_PREFIX,
  absoluteTimingConfigSchema,
  accessTokenSchema,
  accountLoginRequestSchema,
  accountPasswordSchema,
  accountUsernameSchema,
  addClassMemberBodySchema,
  addReviewCommentBodySchema,
  aiAnalysesQuerySchema,
  aiAnalysesResponseSchema,
  aiAnalysisFailureCodeSchema,
  aiAnalysisOutboxEventSchema,
  aiAnalysisOutboxEventTypeSchema,
  aiAnalysisProviderMetadataSchema,
  aiAnalysisResponseSchema,
  aiAnalysisResultSchema,
  aiAnalysisSchema,
  aiAnalysisStatusSchema,
  aiAnalysisSummarySchema,
  aiApiErrorSchema,
  aiBestMoveSchema,
  aiCancelReasonSchema,
  aiCursorSchema,
  aiEnabledExecutionModeSchema,
  aiErrorCodeSchema,
  aiExecutionModeSchema,
  aiGameAnalysisConfigurationQuerySchema,
  aiGameAnalysisConfigurationResponseSchema,
  aiHashSchema,
  aiMoveSchema,
  aiMoveSequenceSchema,
  aiOutboxEventSchema,
  aiPolicyResponseSchema,
  aiPolicySchema,
  aiPolicyUpdateBodySchema,
  aiPolicyUpdatedOutboxEventSchema,
  aiPolicyVersionSchema,
  aiPositionRevisionSchema,
  aiProcessingNoticeVersionSchema,
  aiProviderComponentCodeSchema,
  aiProviderProfileCodeSchema,
  aiProviderProfileMetadataSchema,
  aiProviderVersionSchema,
  aiScoreLeadHalfPointsSchema,
  aiStateVersionSchema,
  aiVisitsSchema,
  aiWinRateBpsSchema,
  apiErrorSchema,
  assignedClassGameSchema,
  assignedClassGamesResponseSchema,
  assignmentStatusSchema,
  assignmentsQuerySchema,
  assignmentsResponseSchema,
  authMutationResponseSchema,
  authSessionResponseSchema,
  avatarUrlSchema,
  boardPointSchema,
  boardSizeSchema,
  boardSnapshotSchema,
  byoyomiConfigSchema,
  byoyomiPeriodTimeMsSchema,
  byoyomiPeriodsSchema,
  cancelAiAnalysisBodySchema,
  cancelAiAnalysisResponseSchema,
  cancelMatchmakingTicketBodySchema,
  canonicalBoardPointListSchema,
  chineseAreaKomiHalfPointsSchema,
  classMemberResponseSchema,
  classMemberSchema,
  classMemberStatusSchema,
  classMembersQuerySchema,
  classMembersResponseSchema,
  classNameSchema,
  classResponseSchema,
  classStatusSchema,
  classSummarySchema,
  classesQuerySchema,
  classesResponseSchema,
  classroomSgfDataSchema,
  classroomSgfFileSchema,
  classroomSgfImportResponseSchema,
  classroomSgfUploadFieldsSchema,
  clientCommandEnvelopeSchema,
  clientGameEventSchema,
  clockStatusSchema,
  containsC0ControlCharacter,
  countingRequestSnapshotSchema,
  countingRequestStatusSchema,
  countingResolutionSourceSchema,
  createAiAnalysisBodySchema,
  createAiAnalysisResponseSchema,
  createClassBodySchema,
  createClassGameRequestSchema,
  createClassGameResponseSchema,
  createGuidedGameRequestSchema,
  createHomeworkBodySchema,
  createInvitationBodySchema,
  createInvitationResponseSchema,
  createManagedAccountBodySchema,
  createManagedAccountResponseSchema,
  createMatchmakingTicketBodySchema,
  createOrganizationBodySchema,
  createOrganizationResponseSchema,
  createProblemBodySchema,
  createQuestionBankCategoryBodySchema,
  createQuestionBankKnowledgePointBodySchema,
  createReviewBodySchema,
  createRoomRequestSchema,
  createRoomResponseSchema,
  creatorColorPreferenceSchema,
  currentUserResponseSchema,
  deleteQuestionBankResourceBodySchema,
  deleteTeacherGameRecordBodySchema,
  deleteTeacherGameRecordResponseSchema,
  displayNameSchema,
  durationMsSchema,
  ebookArrangementResultSchema,
  ebookArrangementSchema,
  ebookAttemptCommandSchema,
  ebookAttemptPositionSchema,
  ebookAttemptSchema,
  ebookAttemptSummarySchema,
  ebookCandidateSchema,
  ebookColorSchema,
  ebookCommandSchema,
  ebookCoverUploadResultSchema,
  ebookCoverUploadSchema,
  ebookDeleteDocumentResultSchema,
  ebookDeleteDocumentSchema,
  ebookDirectoryItemSchema,
  ebookDirectoryPageSchema,
  ebookDirectoryQuerySchema,
  ebookDocumentIndexSchema,
  ebookDocumentSchema,
  ebookEnvelope,
  ebookImportSgfSchema,
  ebookLayoutSchema,
  ebookLearningAttemptSchema,
  ebookLearningProgressSchema,
  ebookLimitsSchema,
  ebookMoveSchema,
  ebookNodeSchema,
  ebookOptimizationSchema,
  ebookPageGeometrySchema,
  ebookPageResultSchema,
  ebookPageSchema,
  ebookPointSchema,
  ebookPracticePreviewResultSchema,
  ebookPracticePreviewSchema,
  ebookPreviewQuerySchema,
  ebookPublicationSchema,
  ebookQuestionDetailSchema,
  ebookQuestionProgressSchema,
  ebookQuestionSchema,
  ebookQuestionTotalResultSchema,
  ebookRankingOptionsSchema,
  ebookRankingPageSchema,
  ebookRankingQuerySchema,
  ebookRankingRowSchema,
  ebookRankingStatsSchema,
  ebookReadingCommandSchema,
  ebookReadingSchema,
  ebookRecognitionModeSchema,
  ebookRegionSchema,
  ebookReleaseSchema,
  ebookSaveQuestionSchema,
  ebookShelfItemSchema,
  ebookStudyPageSchema,
  ebookStudyQuerySchema,
  ebookStudyRecordSchema,
  ebookStudyReplaySchema,
  ebookTrialSchema,
  ebookUploadFieldsSchema,
  ebookWorkspaceSchema,
  eventIdSchema,
  foxKifuAiAccountAccessListResponseSchema,
  foxKifuAiAccountAccessResponseSchema,
  foxKifuAiAccountChoiceSchema,
  foxKifuAiAccountCreateBodySchema,
  foxKifuAiAccountIdentitySchema,
  foxKifuAiAccountListResponseSchema,
  foxKifuAiAccountProfileBodySchema,
  foxKifuAiAccountResponseSchema,
  foxKifuAiAccountSchema,
  foxKifuAiAnalysisBodySchema,
  foxKifuAiAnalysisResponseSchema,
  foxKifuAiAnalysisSchema,
  foxKifuAiAnalysisStatusSchema,
  foxKifuAiCandidateRankSchema,
  foxKifuAiComputeModeSchema,
  foxKifuAiComputeSelectionSchema,
  foxKifuAiComputeTierSchema,
  foxKifuAiDefaultBodySchema,
  foxKifuAiDefaultResponseSchema,
  foxKifuAiEngineCatalogResponseSchema,
  foxKifuAiEngineCatalogSchema,
  foxKifuAiEngineModelSchema,
  foxKifuAiEngineWeightSchema,
  foxKifuAiExclusiveTierSchema,
  foxKifuAiFailureCodeSchema,
  foxKifuAiMaxVisitsSchema,
  foxKifuAiProviderConfigurationBodySchema,
  foxKifuAiProviderConfigurationDataSchema,
  foxKifuAiProviderConfigurationResponseSchema,
  foxKifuAiPublicProfileSchema,
  foxKifuAiStatusResponseSchema,
  foxKifuAiUserAccessBodySchema,
  foxKifuAiUserAccessListResponseSchema,
  foxKifuAiUserAccessResponseSchema,
  foxKifuAiUserAccessSchema,
  foxKifuAiVariationDataSchema,
  foxKifuAiVariationFrameSchema,
  foxKifuAiVariationResponseSchema,
  foxKifuClassroomAiAnalysisBodySchema,
  foxKifuClassroomResearchBodySchema,
  foxKifuDateSchema,
  foxKifuGameFamilySchema,
  foxKifuGamePlayerSchema,
  foxKifuObservationAiAnalysisBodySchema,
  foxKifuObservationAiAnalysisResponseSchema,
  foxKifuObservationResearchBodySchema,
  foxKifuObservationResearchResponseSchema,
  foxKifuObservationTimelineBodySchema,
  foxKifuObservationTimelineQuerySchema,
  foxKifuObservationTimelineResponseSchema,
  foxKifuPlayerSchema,
  foxKifuQueryTextSchema,
  foxKifuRecordDataSchema,
  foxKifuRecordIdSchema,
  foxKifuRecordResponseSchema,
  foxKifuRecordSummarySchema,
  foxKifuReplayFrameSchema,
  foxKifuReplayRecordSchema,
  foxKifuResearchBodySchema,
  foxKifuResearchDataSchema,
  foxKifuResearchResponseSchema,
  foxKifuSearchDataSchema,
  foxKifuSearchQuerySchema,
  foxKifuSearchResponseSchema,
  foxKifuSourceBranchSchema,
  foxKifuStatisticsSchema,
  foxKifuUidSchema,
  foxResearchPreviewSchema,
  foxResearchScopeSchema,
  gameCommandErrorSchema,
  gameCountingRejectedEventSchema,
  gameCountingRequestBodySchema,
  gameCountingRequestCommandSchema,
  gameCountingRequestedEventSchema,
  gameCountingRespondCommandSchema,
  gameCountingResponseBodySchema,
  gameDisconnectionSnapshotSchema,
  gameErrorCodeSchema,
  gameFinishEventSchema,
  gameHistoryItemSchema,
  gameHistoryPlayerSchema,
  gameMoveCommandSchema,
  gameMoveSnapshotSchema,
  gameMutationResponseSchema,
  gameNigiriGuessCommandSchema,
  gameNigiriSelectCommandSchema,
  gamePassCommandSchema,
  gamePlayResumedEventSchema,
  gamePlayersPresenceResponseSchema,
  gameResignCommandSchema,
  gameResignationBodySchema,
  gameReviewClockSchema,
  gameReviewFrameSchema,
  gameReviewRecordSchema,
  gameReviewResponseSchema,
  gameReviewsResponseSchema,
  gameScoringConfirmCommandSchema,
  gameScoringConfirmationBodySchema,
  gameScoringConfirmedEventSchema,
  gameScoringProposalBodySchema,
  gameScoringProposeCommandSchema,
  gameScoringProposedEventSchema,
  gameScoringRejectCommandSchema,
  gameScoringRejectedEventSchema,
  gameScoringResumeBodySchema,
  gameScoringResumeCommandSchema,
  gameScoringStartEventSchema,
  gameSgfChecksumSchema,
  gameSnapshotSchema,
  gameStateDeltaPayloadSchema,
  gameStatusSchema,
  gameSyncRequestCommandSchema,
  gameTakebackResponseSchema,
  gameTakebackSnapshotSchema,
  gameTeacherCountingResolvedEventSchema,
  gameTeacherPausedEventSchema,
  gameTeacherResumedEventSchema,
  gameTerminalSnapshotSchema,
  gameTimeoutEventSchema,
  gameTimingMoveSchema,
  gameTimingMovesQuerySchema,
  gameTimingMovesResponseSchema,
  gameTimingReportMoveMarkerSchema,
  gameTimingReportResponseSchema,
  gameTimingReportSegmentSchema,
  gameTimingReportSideSchema,
  gameTimingSegmentsQuerySchema,
  gameTimingSegmentsResponseSchema,
  gameTimingSideStatisticsSchema,
  gameTimingSnapshotSchema,
  gameTypeSchema,
  getAiAnalysisResponseSchema,
  getAiPolicyResponseSchema,
  getGameAiAnalysesResponseSchema,
  getGameResponseSchema,
  healthResponseSchema,
  historyCursorSchema,
  homeworkAnswerEvaluationSchema,
  homeworkAnswerModeSchema,
  homeworkAnswerPreviewResponseSchema,
  homeworkAnswerSessionBodySchema,
  homeworkAnswerSessionResponseSchema,
  homeworkAnswerTraceNodeSchema,
  homeworkAnswerTraceSchema,
  homeworkAssignmentResponseSchema,
  homeworkAssignmentSnapshotSchema,
  homeworkDescriptionSchema,
  homeworkFeedbackSchema,
  homeworkItemSchema,
  homeworkResponseSchema,
  homeworkReviewProgressResponseSchema,
  homeworkSnapshotSchema,
  homeworkStatusSchema,
  homeworkSubmissionReplayFrameSchema,
  homeworkSubmissionReplayResponseSchema,
  homeworkSubmissionTraceNodeSchema,
  homeworkSubmissionTraceStepSchema,
  homeworkTimeLimitSecondsSchema,
  homeworkTitleSchema,
  homeworkVersionBodySchema,
  homeworksQuerySchema,
  homeworksResponseSchema,
  idempotencyKeySchema,
  identifierSchema,
  invitationResponseSchema,
  invitationTokenSchema,
  joinRoomByCodeRequestSchema,
  joinRoomRequestSchema,
  joinRoomResponseSchema,
  lectureAnnotationSchema,
  lectureContentSchema,
  lectureEnvelope,
  lectureExportBodySchema,
  lectureExportResponseSchema,
  lectureListResponseSchema,
  lectureOperationResponseSchema,
  lectureRecordIdSchema,
  lectureRecordResponseSchema,
  lectureRecordSchema,
  lectureRecordSummarySchema,
  lectureSaveBodySchema,
  lectureSnapshotSchema,
  localizeError,
  logoutAllRequestSchema,
  mainTimeMsSchema,
  managedOrganizationMemberSchema,
  matchmakingStatusSchema,
  matchmakingTicketResponseSchema,
  matchmakingTicketSnapshotSchema,
  maxAiScoreLeadHalfPoints,
  membershipRoleSchema,
  membershipStatusSchema,
  membershipSummarySchema,
  membershipsResponseSchema,
  moveKindSchema,
  nigiriParitySchema,
  nigiriResolutionSchema,
  nigiriSnapshotSchema,
  noTimingConfigSchema,
  normalizeQuestionRequirements,
  organizationInvitationSchema,
  organizationMemberResponseSchema,
  organizationMemberSchema,
  organizationMembersQuerySchema,
  organizationMembersResponseSchema,
  organizationNameSchema,
  organizationResponseSchema,
  organizationSlugSchema,
  organizationSummarySchema,
  phase6TimedGameSnapshotSchema,
  platformOrganizationSchema,
  platformOrganizationsQuerySchema,
  platformOrganizationsResponseSchema,
  playerCurrentGameSchema,
  playerCurrentGamesQuerySchema,
  playerCurrentGamesResponseSchema,
  playerGameHistoryQuerySchema,
  playerGameHistoryResponseSchema,
  playerRecoverableGameResponseSchema,
  playerTimingSnapshotSchema,
  previewHomeworkAnswerBodySchema,
  problemBoardAnnotationSchema,
  problemBoardAnnotationsSchema,
  problemDescriptionSchema,
  problemMoveIntentSchema,
  problemResponseSchema,
  problemSnapshotSchema,
  problemSolutionTreeSchema,
  problemTitleSchema,
  publicTimingConfigSchema,
  publicUserSchema,
  questionBankCatalogResponseSchema,
  questionBankCategorySchema,
  questionBankKnowledgePointSchema,
  questionBankMutationResponseSchema,
  questionBankNameSchema,
  questionBankProblemSchema,
  questionBankProblemsQuerySchema,
  questionBankProblemsResponseSchema,
  questionBankSectionSchema,
  questionRequirementsRichTextSchema,
  questionRequirementsSchema,
  recoverableGameStatusSchema,
  refreshSessionRequestSchema,
  refreshTokenSchema,
  removeClassMemberBodySchema,
  replayableGameEventSchema,
  requestIdSchema,
  requestTakebackBodySchema,
  researchAliasSchema,
  researchCatalogSchema,
  researchCollectionSchema,
  researchDateSchema,
  researchEnvelope,
  researchGroupSchema,
  researchHitSchema,
  researchImportFieldsSchema,
  researchJobSchema,
  researchMoveSchema,
  researchPatternSchema,
  researchPointSchema,
  researchQuerySchema,
  researchRecordSchema,
  researchReplaySchema,
  researchReportSchema,
  researchReviewSchema,
  researchSourceSettingsSchema,
  researchStatisticsExportSchema,
  resetManagedAccountPasswordBodySchema,
  respondTakebackBodySchema,
  reviewAuthorSchema,
  reviewCommentContentSchema,
  reviewCommentSchema,
  reviewCommentsQuerySchema,
  reviewCommentsResponseSchema,
  reviewContentRevisionSchema,
  reviewHomeworkSubmissionBodySchema,
  reviewResponseSchema,
  reviewSnapshotSchema,
  reviewStatusSchema,
  reviewSummarySchema,
  reviewTitleSchema,
  reviewVersionBodySchema,
  reviseReviewBodySchema,
  revokeInvitationBodySchema,
  roomCodeSchema,
  roomJoinCommandSchema,
  roomPlayerSnapshotSchema,
  roomReadyCommandSchema,
  roomSeatSchema,
  roomSnapshotSchema,
  roomStatusSchema,
  rulesetSchema,
  scoreResolutionSourceSchema,
  scoringAreaBreakdownSchema,
  scoringEntryReasonSchema,
  scoringHashSchema,
  scoringProposalSnapshotSchema,
  scoringSessionSnapshotSchema,
  scoringSnapshotSchema,
  serverEventEnvelopeSchema,
  serverGameEventSchema,
  serverTimestampSchema,
  sessionTokenPairSchema,
  solutionLineOutcomeSchema,
  solutionLineSchema,
  sortQuestionBankProblemsBodySchema,
  stateVersionSchema,
  stoneColorSchema,
  studentActivityPageQuerySchema,
  studentHomeworkItemSchema,
  studentHomeworkProgressResponseSchema,
  studentObservationBodySchema,
  studentObservationResponseSchema,
  studentProblemSnapshotSchema,
  studentRoomMemberSchema,
  studentRoomMembersResponseSchema,
  studentWatchGamesResponseSchema,
  submissionResponseSchema,
  submissionSnapshotSchema,
  submissionStatusSchema,
  submitHomeworkAnswerBodySchema,
  supervisionPauseSnapshotSchema,
  supervisionSnapshotSchema,
  supplementAssignmentBodySchema,
  switchMembershipRequestSchema,
  takebackStatusSchema,
  teacherAccessLevelSchema,
  teacherAttentionFlagSchema,
  teacherAuthorizationRoleSchema,
  teacherClientCommandEnvelopeSchema,
  teacherCountingResolutionBodySchema,
  teacherCountingResolutionSchema,
  teacherCursorSchema,
  teacherForfeitBodySchema,
  teacherGameDetailResponseSchema,
  teacherGameMutationResponseSchema,
  teacherGamePlayerSummarySchema,
  teacherGamePresenceSchema,
  teacherGameSummarySchema,
  teacherGameWatchedEventSchema,
  teacherGamesQuerySchema,
  teacherGamesResponseSchema,
  teacherInternalReasonSchema,
  teacherInterventionActionSchema,
  teacherInterventionActorSchema,
  teacherInterventionReasonCodeSchema,
  teacherInterventionSchema,
  teacherInterventionsQuerySchema,
  teacherInterventionsResponseSchema,
  teacherNoteBodySchema,
  teacherNoteResponseSchema,
  teacherNoteSchema,
  teacherPauseBodySchema,
  teacherPresenceStatusSchema,
  teacherPublicMessageSchema,
  teacherResultCorrectionBodySchema,
  teacherResumeBodySchema,
  teacherScoreAdjudicationBodySchema,
  teacherServerEventEnvelopeSchema,
  teacherTimingSummarySchema,
  teacherWatchRevokedEventSchema,
  termLabelSchema,
  timedGameMoveSnapshotSchema,
  timedGameSnapshotSchema,
  timingConfigSchema,
  unlockManagedAccountBodySchema,
  updateAiPolicyResponseSchema,
  updateClassBodySchema,
  updateHomeworkBodySchema,
  updateManagedAccountProfileBodySchema,
  updateOrganizationBodySchema,
  updateOrganizationMemberBodySchema,
  updateProfileBodySchema,
  updateQuestionBankNameBodySchema,
  updateQuestionBankProblemBodySchema,
  userErrorMessage,
  userStatusSchema,
  validateFoxKifuSourceBranches,
  wechatLoginRequestSchema
};
